import React, { useState, useEffect, useContext, createContext, useCallback, useRef, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom';
import "./App.css";
import axios from "axios";
import { AuthProvider, useAuth } from "./AuthContext";
import { BackgroundProvider, useBackground } from "./BackgroundContext";
import { NotificationProvider, useNotifications } from "./NotificationContext";
import { GpsProvider, useGps, GPS_MODES } from "./GpsContext";
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { LoginModal, RegisterModal } from "./AuthModals";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap, Circle } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import appSettings, { getSetting } from './appsetting';
import { resolvePhotoSrc } from './photoSrc';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';
import AnnouncementPopup from './AnnouncementPopup';
import V2Shell from './v2/V2Shell';
import Onboarding from './v2/Onboarding';
import { HomeFeedRoute, MeProfileRoute, MapV2Route, CommunityHubRoute, ChallengeEventRoute, V2AnimalProfileRoute, V2UserProfileRoute, QuickSpotRoute, PokdengLobbyRoute, CommunityCareRoute, WildlifeRoute, DebugRoute, AdminZoneRoute, StampHuntRoute, V2PlaceholderRoute } from './v2/routes';

// Heavy V1-only modules — code-split so V2 users (default landing page) skip them.
const MapView = lazy(() => import('./MapView'));
const EventsPage = lazy(() => import('./EventsPage'));
const LostPetsPage = lazy(() => import('./LostPetsPage'));
const BloodBankPage = lazy(() => import('./BloodBankPage'));
const BloodBuddyPage = lazy(() => import('./BloodBuddyPage'));
const PokdengPage = lazy(() => import('./PokdengPage'));
const LanguageSettingsPage = lazy(() => import('./LanguageSettingsPage'));

// Small inline fallback used wherever a lazy V1 view is mounted.
const V1Fallback = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh', color: '#999' }}>
    กำลังโหลด…
  </div>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
if (!BACKEND_URL) throw new Error('REACT_APP_BACKEND_URL is required');
const API = `${BACKEND_URL}/api`;

// Privacy Map Component - Shows approximate location with circle overlay
const PrivacyMapView = ({ actualLat, actualLng, animalName }) => {
  const [offsetPosition, setOffsetPosition] = useState(null);
  
  useEffect(() => {
    // Random offset 100m from actual position
    const randomOffset = () => {
      const earthRadius = 6371000; // meters
      const offsetDistance = 100; // 100 meters
      
      // Random angle
      const angle = Math.random() * 2 * Math.PI;
      
      // Calculate offset in degrees
      const deltaLat = (offsetDistance * Math.cos(angle)) / earthRadius * (180 / Math.PI);
      const deltaLng = (offsetDistance * Math.sin(angle)) / (earthRadius * Math.cos(actualLat * Math.PI / 180)) * (180 / Math.PI);
      
      return {
        lat: actualLat + deltaLat,
        lng: actualLng + deltaLng
      };
    };
    
    setOffsetPosition(randomOffset());
  }, [actualLat, actualLng]);
  
  if (!offsetPosition) return null;
  
  return (
    <MapContainer 
      center={[offsetPosition.lat, offsetPosition.lng]} 
      zoom={15} 
      style={{ width: '100%', height: '100%' }}
      zoomControl={true}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      {/* Circle overlay with 200m radius */}
      <Circle
        center={[offsetPosition.lat, offsetPosition.lng]}
        radius={200}
        pathOptions={{
          fillColor: '#3b82f6',
          fillOpacity: 0.2,
          color: '#2563eb',
          weight: 2
        }}
      />
      <Circle
        center={[offsetPosition.lat, offsetPosition.lng]}
        radius={20}
        pathOptions={{
          fillColor: '#2563eb',
          fillOpacity: 0.8,
          color: '#1e40af',
          weight: 2
        }}
      />
    </MapContainer>
  );
};

// Locale mapping for date formatting
const getLocaleCode = (langCode) => {
  const localeMap = {
    'en': 'en-US',
    'th': 'th-TH',
    'es': 'es-ES',
    'fr': 'fr-FR',
    'de': 'de-DE',
    'zh': 'zh-CN',
    'ja': 'ja-JP',
    'ko': 'ko-KR',
    'hi': 'hi-IN',
    'el': 'el-GR',
    'tr': 'tr-TR'
  };
  return localeMap[langCode] || 'en-US';
};

// SVG Icon Components
const ArrowLeft = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
  </svg>
);

const User = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const Heart = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 000-6.364 4.5 4.5 0 00-6.364 0L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
  </svg>
);

const MessageCircle = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);

const Share2 = ({ className = "w-5 h-5" }) => (
  <img src="/share-icon.png" alt="Share" className={className} />
);

// DB Test Redirect Component
const DBTestRedirect = () => {
  useEffect(() => {
    // Redirect to backend db-test endpoint
    window.location.href = `${BACKEND_URL}/db-test`;
  }, []);
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-500 to-orange-700">
      <div className="text-center text-white">
        <div className="mb-4">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
        </div>
        <p className="text-xl">Redirecting to Database Test...</p>
      </div>
    </div>
  );
};

// Helper function to update meta tags
const updateMetaTag = (name, content) => {
  let element = document.querySelector(`meta[name="${name}"], meta[property="${name}"]`);
  if (!element) {
    element = document.createElement('meta');
    if (name.startsWith('og:') || name.startsWith('twitter:')) {
      element.setAttribute('property', name);
    } else {
      element.setAttribute('name', name);
    }
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

// 🎨 Furbook Design Utilities
const furPatterns = {
  cat: 'fur-pattern-cat',
  dog: 'fur-pattern-dog', 
  rabbit: 'fur-pattern-rabbit',
  bird: 'fur-pattern-bird',
  other: 'fur-pattern-other'
};

const animalEmojis = {
  pet: '🐕',
  stray: '🐾', 
  social: '🦮'
};

// ฟังก์ชันCreate fur badge จากชื่อผู้ใช้
const generateFurBadge = (name, type = 'other', size = 'md') => {
  if (!name) return { pattern: furPatterns.other, emoji: '👤', size };
  
  const firstChar = name.charAt(0).toUpperCase();
  const charCode = name.charCodeAt(0);
  
  // เลือก pattern ตาม character code
  const patterns = Object.keys(furPatterns);
  const patternIndex = charCode % patterns.length;
  const pattern = furPatterns[patterns[patternIndex]];
  
  // เลือก emoji ตามt('admin.hero_type')
  const emoji = animalEmojis[type] || firstChar;
  
  return { pattern, emoji, size };
};

// 🎨 Furbook Badge Component
const FurBadge = ({ name, type = 'other', size = 'md', className = '', onClick }) => {
  const badge = generateFurBadge(name, type, size);
  const sizeClass = size === 'sm' ? 'fur-badge-sm' : 
                   size === 'lg' ? 'fur-badge-lg' : 
                   size === 'xl' ? 'fur-badge-xl' : 'fur-badge';
  
  return (
    <div 
      className={`fur-badge ${sizeClass} ${badge.pattern} ${className} ${onClick ? 'cursor-pointer hover:scale-105 transition-transform' : ''}`}
      onClick={onClick}
      title={name || 'User'}
    >
      <span className="relative z-10 text-white drop-shadow-sm">
        {badge.emoji}
      </span>
    </div>
  );
};

// 🎨 Furbook Button Component
const FurButton = ({ variant = 'primary', size = 'md', children, className = '', ...props }) => {
  const baseClass = 'fur-btn';
  const variantClass = `fur-btn-${variant}`;
  const sizeClass = size === 'sm' ? 'text-sm px-4 py-2' :
                   size === 'lg' ? 'text-lg px-8 py-4' : 'px-6 py-3';
  
  return (
    <button 
      className={`${baseClass} ${variantClass} ${sizeClass} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

// 🎨 Furbook Card Component  
const FurCard = ({ children, className = '', hover = true, ...props }) => {
  return (
    <div 
      className={`fur-card ${hover ? 'hover:fur-card-hover' : ''} ${className}`}
      {...props}
    >
      {children}
    </div>
  );
};

// 🌄 Background Image Component
const BackgroundImage = ({ page = 'home', children, className = '' }) => {
  const { getBackgroundStyle } = useBackground();
  
  const backgroundStyle = getBackgroundStyle();

  return (
    <div className={`relative min-h-screen ${className}`} style={backgroundStyle}>
      {/* Content */}
      <div className="relative z-20">
        {children}
      </div>
    </div>
  );
};

// 🎨 Welcome Banner Component
const WelcomeBanner = ({ show = true }) => {
  const welcomeMessage = getSetting('content.welcomeMessage', '🐾 ยินดีต้อนรับสู่ TailLink');
  const subtitle = getSetting('content.subtitle', 'ชุมชนคนรักสัตว์ที่อบอุ่น');
  
  if (!show) return null;
  
  return (
    <div className="text-center py-8 px-4">
      <h1 className="fur-heading text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-fur-primary">
        {welcomeMessage}
      </h1>
      <p className="text-lg md:text-xl text-fur-neutral-1 max-w-3xl mx-auto leading-relaxed">
        {subtitle}
      </p>
    </div>
  );
};

// Fix Leaflet default markers issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Map Components with Search
const LocationPicker = ({ center, onLocationSelect, selectedLocation }) => {
  const { t, i18n } = useTranslation();
  const [position, setPosition] = useState(selectedLocation || center);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);

  const MapClickHandler = () => {
    useMapEvents({
      click(e) {
        const newPos = [e.latlng.lat, e.latlng.lng];
        setPosition(newPos);
        if (onLocationSelect) {
          onLocationSelect({
            lat: e.latlng.lat,
            lng: e.latlng.lng,
            address: `${e.latlng.lat.toFixed(6)}, ${e.latlng.lng.toFixed(6)}`
          });
        }
      },
    });
    return null;
  };

  const MapController = ({ center }) => {
    const map = useMap();
    useEffect(() => {
      if (center) {
        map.setView(center, 15);
      }
    }, [center, map]);
    return null;
  };

  const searchLocation = async () => {
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    setSearchResults([]);
    
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=5`
      );
      const data = await response.json();
      setSearchResults(data);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setSearching(false);
    }
  };

  const selectSearchResult = (result) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    const newPos = [lat, lng];
    
    setPosition(newPos);
    if (onLocationSelect) {
      onLocationSelect({
        lat,
        lng,
        address: result.display_name
      });
    }
    setSearchResults([]);
    setSearchQuery('');
  };

  try {
    return (
      <div>
        {/* Search Box */}
        <div className="mb-3">
          <div className="flex space-x-2">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && searchLocation()}
              placeholder={t('common.search') + ' ' + t('common.location').toLowerCase() + '...'}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <button
              onClick={searchLocation}
              disabled={searching || !searchQuery.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {searching ? '...' : '🔍'}
            </button>
          </div>
          
          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="mt-2 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
              {searchResults.map((result, index) => (
                <button
                  key={index}
                  onClick={() => selectSearchResult(result)}
                  className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b last:border-b-0"
                >
                  <p className="text-sm font-medium text-gray-800">{result.display_name}</p>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Map */}
        <MapContainer
          center={center}
          zoom={15}
          className="map-picker"
          style={{ height: '300px', width: '100%' }}
          scrollWheelZoom={true}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          <MapClickHandler />
          <MapController center={position} />
          {position && <Marker position={position} />}
        </MapContainer>
      </div>
    );
  } catch (error) {
    console.error('Map error:', error);
    return (
      <div className="map-picker bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-2">🗺️</div>
          <p className="text-gray-600">{t('alerts.cannot_load_map')}</p>
          <p className="text-sm text-gray-500">{t('alerts.please_try_again')}</p>
        </div>
      </div>
    );
  }
};

const LocationSelector = ({ onLocationSelect, selectedLocation, showExactLocation = true, className = "" }) => {
  const { t, i18n } = useTranslation();
  const [showMap, setShowMap] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mapCenter, setMapCenter] = useState([13.7563, 100.5018]); // Default to Bangkok
  const [useMapFallback, setUseMapFallback] = useState(false);

  useEffect(() => {
    // Try to get user's current location for map center
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setMapCenter([position.coords.latitude, position.coords.longitude]);
        },
        (error) => {
          console.log('Location access denied:', error);
        }
      );
    }
  }, []);

  const getCurrentLocation = () => {
    setLoading(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`
            );
            const data = await response.json();
            const address = data.display_name || `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
            
            onLocationSelect({ lat, lng, address });
          } catch (error) {
            console.error('Error getting address:', error);
            onLocationSelect({ 
              lat, 
              lng, 
              address: `${lat.toFixed(6)}, ${lng.toFixed(6)}` 
            });
          }
          setLoading(false);
        },
        (error) => {
          console.error('Error getting location:', error);
          alert(t('alerts.cannot_get_location'));
          setLoading(false);
        }
      );
    }
  };

  const handleMapLocationSelect = async (location) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${location.lat}&lon=${location.lng}&addressdetails=1`
      );
      const data = await response.json();
      const address = data.display_name || `${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}`;
      
      onLocationSelect({ ...location, address });
      setShowMap(false);
    } catch (error) {
      console.error('Error getting address:', error);
      onLocationSelect(location);
      setShowMap(false);
    }
  };

  const handleFallbackLocationSelect = (location) => {
    onLocationSelect(location);
    setShowMap(false);
  };

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 mb-2">{t('forms.location_required')}</label>
      
      {!selectedLocation ? (
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <button
              type="button"
              onClick={getCurrentLocation}
              disabled={loading}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-xl hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center font-medium transition-all"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {t('common.locating')}
                </>
              ) : (
                <span className="flex items-center space-x-2">
                  <img src="/location-pin.png" alt="GPS" className="w-5 h-5 brightness-0 invert" />
                  <span>{t('animals.use_gps')}</span>
                </span>
              )}
            </button>
            
            <button
              type="button"
              onClick={() => setShowMap(true)}
              className="w-full bg-green-600 text-white px-4 py-2 rounded-xl hover:bg-green-700 flex items-center justify-center space-x-2 font-medium transition-all"
            >
              <img src="/maps-icon.png" alt="Map" className="w-6 h-6" />
              <span>{t('animals.select_on_map')}</span>
            </button>
          </div>
          
          <p className="text-sm text-gray-500 text-center">
            {t('forms.select_location_to_place')}
          </p>
        </div>
      ) : (
        <div className="bg-green-50 border border-green-200 rounded-md p-3">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <p className="text-sm font-medium text-green-800">{t('forms.selected_location')}</p>
              <p className="text-sm text-green-700 mb-1">
                {showExactLocation === false 
                  ? t('forms.not_specified') + ' (' + t('profile.approximate_location_notice') + ')'
                  : selectedLocation.address
                }
              </p>
              {showExactLocation !== false && (
                <p className="text-xs text-green-600">
                  📍 {selectedLocation.lat.toFixed(6)}, {selectedLocation.lng.toFixed(6)}
                </p>
              )}
            </div>
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={() => setShowMap(true)}
                className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
              >
                <span className="inline">✏️</span>
                <span className="hidden xs:inline ml-1">{t('common.edit')}</span>
              </button>
              <button
                type="button"
                onClick={() => onLocationSelect(null)}
                className="text-red-600 hover:text-red-800 text-sm flex items-center"
              >
                <span className="inline">🗑️</span>
                <span className="hidden xs:inline ml-1">{t('common.delete')}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Map Modal */}
      {showMap && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full max-h-screen overflow-hidden">
            <div className="p-4 border-b">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">🗺️ {t('common.select_location')}</h3>
                <button
                  onClick={() => setShowMap(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ×
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                {t('common.click_to_select')}
              </p>
            </div>
            
            <div className="h-96 relative">
              {useMapFallback ? (
                <MapFallback
                  onLocationSelect={handleFallbackLocationSelect}
                  selectedLocation={selectedLocation}
                />
              ) : (
                <LocationPicker
                  center={selectedLocation ? [selectedLocation.lat, selectedLocation.lng] : mapCenter}
                  onLocationSelect={handleMapLocationSelect}
                  selectedLocation={selectedLocation ? [selectedLocation.lat, selectedLocation.lng] : null}
                />
              )}
            </div>
            
            <div className="p-4 border-t">
              <div className="flex justify-between items-center">
                <button
                  type="button"
                  onClick={() => setUseMapFallback(!useMapFallback)}
                  className="text-sm text-blue-600 hover:underline"
                >
                  {useMapFallback ? `🗺️ ${t('forms.use_map')}` : `📍 ${t('forms.enter_coordinates')}`}
                </button>
                
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowMap(false)}
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Radius Selector Component
const RadiusSelector = ({ radius, onRadiusChange, className = "" }) => {
  const { t, i18n } = useTranslation();
  
  const radiusOptions = [
    { value: 1000, label: `1 ${t('common.km')}` },
    { value: 2000, label: `2 ${t('common.km')}` },
    { value: 5000, label: `5 ${t('common.km')}` },
  ];

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {t('animals.search_radius')} <span className="text-red-600 font-semibold">
          {radiusOptions.find(r => r.value === radius)?.label || `${(radius/1000).toFixed(1)} ${t('common.km')}`}
        </span>
      </label>
      
      <div className="space-y-3">
        {/* Range Slider */}
        <input
          type="range"
          min="1000"
          max="5000"
          step="1000"
          value={radius}
          onChange={(e) => onRadiusChange(parseInt(e.target.value))}
          className="w-full range-slider"
        />
        
        {/* Quick Select Buttons */}
        <div className="flex flex-wrap gap-2">
          {radiusOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => onRadiusChange(option.value)}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                radius === option.value
                  ? 'bg-fur-primary text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// Simple Map Fallback Component
const MapFallback = ({ onLocationSelect, selectedLocation }) => {
  const { t } = useTranslation();
  const [manualLat, setManualLat] = useState(selectedLocation?.lat || '');
  const [manualLng, setManualLng] = useState(selectedLocation?.lng || '');

  const handleManualLocation = () => {
    const lat = parseFloat(manualLat);
    const lng = parseFloat(manualLng);
    
    if (isNaN(lat) || isNaN(lng)) {
      alert(t('alerts.enter_valid_coordinates'));
      return;
    }
    
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      alert(t('alerts.coordinates_out_of_bounds'));
      return;
    }

    onLocationSelect({
      lat,
      lng,
      address: `${lat.toFixed(6)}, ${lng.toFixed(6)}`
    });
  };

  return (
    <div className="map-picker bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-6">
      <div className="text-center mb-4">
        <div className="text-4xl mb-2">📍</div>
        <h3 className="text-lg font-semibold text-gray-700">{t('forms.enter_coordinates_manually')}</h3>
        <p className="text-sm text-gray-500">{t('forms.enter_coordinates_desc')}</p>
      </div>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms.latitude')}</label>
          <input
            type="number"
            step="any"
            value={manualLat}
            onChange={(e) => setManualLat(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder={t('forms.latitude_placeholder')}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms.longitude')}</label>
          <input
            type="number"
            step="any"
            value={manualLng}
            onChange={(e) => setManualLng(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder={t('forms.longitude_placeholder')}
          />
        </div>
      </div>
      
      <button
        onClick={handleManualLocation}
        className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
      >
        {t('forms.use_these_coordinates')}
      </button>
      
      <div className="mt-4 text-xs text-gray-500">
        <p><strong>{t('common.tip')}:</strong> {t('forms.coordinate_tip')}</p>
      </div>
    </div>
  );
};

// Animal Map View Component
const AnimalMapView = ({ animals, userLocation, onAnimalClick, filters }) => {
  const { t, i18n } = useTranslation();
  // ถ้ามี userLocation ให้ใช้เป็น default center เลย ไม่ใช่ Bangkok
  const [mapCenter, setMapCenter] = useState(
    userLocation ? [userLocation.lat, userLocation.lng] : [13.7563, 100.5018]
  );
  const [mapZoom, setMapZoom] = useState(19); // ซูมใกล้ที่สุด
  const [map, setMap] = useState(null);

  // Force center map to user location ทันทีที่มี userLocation
  useEffect(() => {
    if (userLocation) {
      const newCenter = [userLocation.lat, userLocation.lng];
      setMapCenter(newCenter);
      setMapZoom(19);
      
      // Force map to pan to user location if map instance exists
      if (map) {
        map.setView(newCenter, 19);
      }
    }
  }, [userLocation, map]);

  // Center map ทันทีที่ component mount ถ้ามี userLocation
  useEffect(() => {
    if (userLocation) {
      setMapCenter([userLocation.lat, userLocation.lng]);
      setMapZoom(19);
    }
  }, []); // empty dependency เพื่อรันแค่ครั้งแรก

  // Re-center map when filters change (especially radius) 
  useEffect(() => {
    if (userLocation && map) {
      map.setView([userLocation.lat, userLocation.lng], 19);
    }
  }, [filters, userLocation, map]);

  // Custom marker colors for different animal types
  const getMarkerColor = (animalType) => {
    switch (animalType) {
      case 'owned': return '#3b82f6'; // Blue
      case 'stray': return '#f97316'; // Orange  
      case 'social_pet': return '#10b981'; // Green
      case 'wild': return '#8b4513'; // Brown
      default: return '#6b7280'; // Gray
    }
  };

  // Create custom marker icon
  const createCustomIcon = (animal) => {
    const color = getMarkerColor(animal.animal_type);
    const emoji = getAnimalIcon(animal.species);
    
    return L.divIcon({
      html: `
        <div style="
          background-color: ${color};
          width: 40px;
          height: 40px;
          border-radius: 50%;
          border: 3px solid white;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          cursor: pointer;
        ">
          ${emoji}
        </div>
      `,
      className: 'custom-marker',
      iconSize: [40, 40],
      iconAnchor: [20, 20]
    });
  };

  // Helper function: Calculate distance for display (backend already sorted by distance)
  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const R = 6371; // Earth radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Add distance to animals for display (backend returns top 50 already sorted)
  const animalsWithDistance = animals.map(animal => {
    if (lastLocation && animal.lat && animal.lng) {
      const distance = calculateDistance(
        lastLocation.lat,
        lastLocation.lng,
        animal.lat,
        animal.lng
      );
      return { ...animal, distance };
    }
    return animal;
  });

  // Map Event Handler for automatic centering
  const MapEventHandler = ({ userLocation, filters }) => {
    const map = useMap();
    
    useEffect(() => {
      if (userLocation) {
        map.setView([userLocation.lat, userLocation.lng], 19); // ซูมใกล้ที่สุดระดับ 19
      }
    }, [userLocation, filters, map]);
    
    return null;
  };

  // Map Click Handler for adding new animals
  const MapClickHandler = () => {
    useMapEvents({
      click(e) {
        console.log('Map clicked at:', e.latlng);
        // Could be used for adding animals at clicked location
      },
    });
    return null;
  };

  try {
    return (
      <div className="h-full w-full">
        <MapContainer
          center={mapCenter}
          zoom={mapZoom}
          className="h-full w-full"
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          
          {/* Auto-center handler */}
          <MapEventHandler userLocation={userLocation} filters={filters} />
          
          <MapClickHandler />
          
          {/* User Location Marker */}
          {userLocation && (
            <Marker 
              position={[userLocation.lat, userLocation.lng]}
              icon={L.divIcon({
                html: `
                  <div style="
                    background-color: #ef4444;
                    width: 20px;
                    height: 20px;
                    border-radius: 50%;
                    border: 3px solid white;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                    animation: pulse 2s infinite;
                  "></div>
                `,
                className: 'user-location-marker',
                iconSize: [20, 20],
                iconAnchor: [10, 10]
              })}
            />
          )}
          
          {/* Animal Markers */}
          {animalsWithDistance.map((animal) => {
            if (!animal.location || !animal.location.lat || !animal.location.lng) return null;
            
            return (
              <Marker
                key={animal.id}
                position={[animal.location.lat, animal.location.lng]}
                icon={createCustomIcon(animal)}
                eventHandlers={{
                  click: () => onAnimalClick(animal)
                }}
              >
                {/* Popup for animal info */}
                <div className="animal-popup">
                  <div className="p-3 max-w-xs">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="text-xl">{getAnimalIcon(animal.species)}</span>
                      <h3 className="font-bold text-gray-800">{animal.name}</h3>
                    </div>
                    
                    <div className="space-y-1 text-sm">
                      <p><strong>t('admin.hero_type'):</strong> {
                        animal.animal_type === 'owned' ? t('animals.pet') :
                             animal.animal_type === 'stray' ? t('animals.stray') :
                             animal.animal_type === 'wild' ? t('profile.pet_type_wild') :
                             t('animals.social_pet')
                      }</p>
                      <p><strong>ชนิด:</strong> {animal.species}</p>
                      {animal.breed && <p><strong>สายพันธุ์:</strong> {animal.breed}</p>}
                      <p className="text-gray-600 line-clamp-2">{animal.description}</p>
                      {animal.needs_help && (
                        <p className="text-red-600 font-medium">🆘 ต้องการความช่วยเหลือ</p>
                      )}
                    </div>
                    
                    <button 
                      onClick={() => onAnimalClick(animal)}
                      className="w-full mt-2 bg-blue-600 text-white py-1 px-3 rounded text-sm hover:bg-blue-700 flex items-center justify-center"
                    >
                      <span className="inline">👁️</span>
                      <span className="hidden sm:inline ml-1">ดูรายละเอียด</span>
                    </button>
                  </div>
                </div>
              </Marker>
            );
          })}
        </MapContainer>
      </div>
    );
  } catch (error) {
    console.error('Map error:', error);
    return (
      <div className="h-full w-full bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🗺️</div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">{t('alerts.cannot_load_map')}</h3>
          <p className="text-gray-600 mb-4">{t('alerts.please_refresh')}</p>
          <div className="space-y-2">
            <p className="text-sm text-gray-500"><strong>สัตว์ทั้งหมด:</strong> {animals.length} ตัว</p>
            <p className="text-sm text-gray-500"><strong>สัตว์ที่แสดง:</strong> {animalsWithDistance.length} ตัว (Top 50 ใกล้ที่สุด)</p>
          </div>
        </div>
      </div>
    );
  }
};

// Animal Icon Helper Function
const getAnimalIcon = (species) => {
  const icons = {
    'dog': '🐕',
    'cat': '🐱',
    'bird': '🐦',
    'rabbit': '🐰',
    'hamster': '🐹',
    'fish': '🐠',
    'turtle': '🐢',
    'other': '🐾'
  };
  return icons[species] || icons.other;
};

// Mobile Menu Component
const MobileMenu = ({ isOpen, onClose, currentView, onNavigate, isAuthenticated, onLogin, onRegister, onLogout, user, unreadNotifications }) => {
  const { t, i18n } = useTranslation();
  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
      <div className="fixed top-0 right-0 h-full w-64 bg-white shadow-lg transform transition-transform">
        <div className="p-4 border-b">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <img src="/taillink-logo.png" alt="TailLink" className="h-10 w-auto object-contain" />
              <h2 className="fur-heading font-bold text-2xl text-fur-primary">{t('app_name')}</h2>
            </div>
                       <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        <div className="p-4 space-y-4">
          {/* Add Animal - moved to first, changed to orange CTA */}
          <button
            onClick={() => { onNavigate('add'); onClose(); }}
            className="w-full bg-orange-600 text-white px-4 py-3 rounded-md hover:bg-orange-700 flex items-center font-semibold"
          >
            <span className="mr-3 text-2xl font-bold text-white">+</span>
            <span>{t('nav.add_animal')}</span>
          </button>
          
          {isAuthenticated && user?.is_admin && (
            <button
              onClick={() => { window.open('/admin', '_blank'); onClose(); }}
              className="w-full text-left px-4 py-3 rounded-md text-orange-600 hover:bg-orange-50 border border-orange-200 flex items-center"
            >
              <span className="mr-3">🛠️</span>
              <span>{t('nav.admin')}</span>
            </button>
          )}

          <button
            onClick={() => { onNavigate('events'); onClose(); }}
            className="w-full text-left px-4 py-3 rounded-md text-orange-600 hover:bg-orange-50 border border-orange-200 flex items-center"
            data-testid="mobile-menu-events"
          >
            <span className="mr-3">🎉</span>
            <span>Events</span>
          </button>

          <button
            onClick={() => { onNavigate('lost-pets'); onClose(); }}
            className="w-full text-left px-4 py-3 rounded-md text-red-600 hover:bg-red-50 border border-red-200 flex items-center"
            data-testid="mobile-menu-lost-pets"
          >
            <span className="mr-3">🔍</span>
            <span>ตามหาสัตว์เลี้ยง</span>
          </button>

          <button
            onClick={() => { onNavigate('blood-bank'); onClose(); }}
            className="w-full text-left px-4 py-3 rounded-md text-red-700 hover:bg-red-50 border border-red-200 flex items-center"
            data-testid="mobile-menu-blood-bank"
          >
            <span className="mr-3">🩸</span>
            <span>Blood Bank เลือดสำรอง</span>
          </button>

          <button
            onClick={() => { onNavigate('blood-buddy'); onClose(); }}
            className="w-full text-left px-4 py-3 rounded-md text-orange-700 hover:bg-orange-50 border border-orange-200 flex items-center"
            data-testid="mobile-menu-blood-buddy"
          >
            <span className="mr-3">🤝</span>
            <span>Blood Buddy เพื่อนซี้สายเลือด</span>
          </button>

          {/* Settings button - visible to everyone */}
          <button
            onClick={() => { 
              window.location.href = '/notifications';
              onClose(); 
            }}
            className="w-full bg-gray-600 text-white px-4 py-3 rounded-md hover:bg-gray-700 flex items-center relative"
          >
            <span className="mr-3">⚙️</span>
            <span>{t('nav.settings')}</span>
            {/* Notification Badge */}
            {unreadNotifications > 0 && (
              <span className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center shadow-lg animate-pulse">
                {unreadNotifications > 9 ? '9+' : unreadNotifications}
              </span>
            )}
          </button>

          <div className="border-t pt-4 mt-4">
            {isAuthenticated ? (
              <div className="space-y-3">
                <button
                  onClick={() => { 
                    window.location.href = `/user/${user?.id}`;
                    onClose(); 
                  }}
                  className="flex items-center space-x-3 px-4 py-2 w-full text-left hover:bg-gray-50 rounded"
                >
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm">
                    🐾
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">{user?.full_name || user?.username}</p>
                    <p className="text-sm text-gray-600">@{user?.username}</p>
                  </div>
                </button>
                <button
                  onClick={() => { onLogout(); onClose(); }}
                  className="w-full text-left px-4 py-2 text-red-600 hover:bg-red-50"
                >
                  🚪 {t('nav.logout')}
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <button
                  onClick={() => { onLogin(); onClose(); }}
                  className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                >
                  {t('nav.login')}
                </button>
                <button
                  onClick={() => { onRegister(); onClose(); }}
                  className="w-full border border-blue-600 text-blue-600 px-4 py-2 rounded-md hover:bg-blue-50"
                >
                  {t('nav.register')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// User Location Manager Component
const UserLocationManager = ({ user, onLocationUpdate, onClose }) => {
  const { t, i18n } = useTranslation();
  const { lastLocation } = useGps();
  const [loading, setLoading] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [mapCenter, setMapCenter] = useState([13.7563, 100.5018]); // Default to Bangkok

  useEffect(() => {
    // Priority 1: โหลดจาก localStorage (user selection)
    const savedLocation = localStorage.getItem('taillink_location');
    if (savedLocation) {
      try {
        const location = JSON.parse(savedLocation);
        setSelectedLocation(location);
        setMapCenter([location.lat, location.lng]);
        console.log('📍 UserLocationManager loaded location from localStorage:', location);
        return; // Found saved location, skip rest
      } catch (e) {
        console.error('Error parsing saved location:', e);
      }
    }
    
    // Priority 2: Use last map center location (always)
    if (lastLocation) {
      const location = {
        lat: lastLocation.lat,
        lng: lastLocation.lng,
        address: 'Last Map Location'
      };
      setSelectedLocation(location);
      setMapCenter([location.lat, location.lng]);
      console.log('📍 UserLocationManager using last map location:', location);
      return; // Used last location, skip GPS check
    }
    
    // Priority 3: Try to get current GPS location as fallback
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setMapCenter([position.coords.latitude, position.coords.longitude]);
        },
        (error) => {
          console.log('Location access denied:', error);
        }
      );
    }
  }, [user, lastLocation]);

  const getCurrentLocation = async () => {
    setLoading(true);
    
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`
            );
            const data = await response.json();
            const address = data.display_name || `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
            
            const locationData = { lat, lng, address };
            setSelectedLocation(locationData);
            await saveLocation(locationData);
          } catch (error) {
            console.error('Error getting address:', error);
            const locationData = { 
              lat, 
              lng, 
              address: `${lat.toFixed(6)}, ${lng.toFixed(6)}` 
            };
            setSelectedLocation(locationData);
            await saveLocation(locationData);
          }
          setLoading(false);
        },
        (error) => {
          console.error('Error getting location:', error);
          alert(t('alerts.cannot_get_location'));
          setLoading(false);
        }
      );
    }
  };

  const saveLocation = async (locationData) => {
    try {
      console.log('💾 Saving location:', locationData);
      
      // บันทึกใน localStorage สำหรับทุกคน (ทั้ง login และไม่ login)
      localStorage.setItem('taillink_location', JSON.stringify(locationData));
      if (onLocationUpdate) {
        console.log('📡 Calling onLocationUpdate callback');
        onLocationUpdate(locationData);
      }
      alert(`📍 ${t('alerts.location_saved')}`);
      
      // TODO: Uncomment this later to save to backend for logged-in users
      // if (user) {
      //   await axios.put(`${API}/auth/location`, locationData);
      //   console.log('✅ Location also saved to backend');
      // }
    } catch (error) {
      console.error('Error saving location:', error);
      alert(t('alerts.location_error'));
    }
  };

  const clearLocation = async () => {
    if (confirm(t('alerts.delete_location_confirm'))) {
      try {
        // ลบจาก localStorage สำหรับทุกคน
        localStorage.removeItem('taillink_location');
        setSelectedLocation(null);
        if (onLocationUpdate) {
          onLocationUpdate(null);
        }
        alert(t('alerts.location_deleted'));
        
        // TODO: Uncomment this later to also delete from backend for logged-in users
        // if (user) {
        //   await axios.delete(`${API}/auth/location`);
        //   console.log('✅ Location also deleted from backend');
        // }
      } catch (error) {
        console.error('Error clearing location:', error);
        alert(t('alerts.location_error'));
      }
    }
  };

  const handleMapLocationSelect = async (location) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${location.lat}&lon=${location.lng}&addressdetails=1`
      );
      const data = await response.json();
      const address = data.display_name || `${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}`;
      
      const locationData = { ...location, address };
      setSelectedLocation(locationData);
      await saveLocation(locationData);
      setShowMap(false);
    } catch (error) {
      console.error('Error getting address:', error);
      setSelectedLocation(location);
      await saveLocation(location);
      setShowMap(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full max-h-screen overflow-y-auto">
        <div className="p-6 border-b">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <img src="/location-pin.png" alt="Location" className="w-6 h-6" />
              <h3 className="text-xl font-bold text-gray-800">{t('settings.set_my_location')}</h3>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl"
            >
              ×
            </button>
          </div>
          <p className="text-sm text-gray-600 mt-2">
            {t('settings.set_location_description')}
          </p>
        </div>

        <div className="p-6 space-y-6">
          {/* Current Location Status */}
          {selectedLocation ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-medium text-green-800 mb-1">✅ {t('settings.current_location')}</h4>
                  <p className="text-sm text-green-700 mb-1">{selectedLocation.address}</p>
                  <p className="text-xs text-green-600">
                    📍 {selectedLocation.lat.toFixed(6)}, {selectedLocation.lng.toFixed(6)}
                  </p>
                  {user?.location_set_at && (
                    <p className="text-xs text-green-500 mt-1">
                      Updateล่าสุด: {new Date(user.location_set_at).toLocaleString('th-TH')}
                    </p>
                  )}
                </div>
                <button
                  onClick={clearLocation}
                  className="text-red-600 hover:text-red-800 text-sm ml-4"
                >{t('common.delete')}</button>
              </div>
            </div>
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="font-medium text-yellow-800 mb-1">{t('alerts.location_not_set_warning')}</h4>
              <p className="text-sm text-yellow-700">{t('alerts.please_select_location')}</p>
            </div>
          )}

          {/* Location Setting Options */}
          <div className="space-y-4">
            <h4 className="font-medium text-gray-800">{t('common.select_location_method')}</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={getCurrentLocation}
                disabled={loading}
                className="w-full bg-blue-600 text-white px-4 py-3 rounded-xl hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center font-medium transition-all"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    {t('common.locating')}
                  </>
                ) : (
                  <span className="flex items-center space-x-2">
                    <img src="/location-pin.png" alt="GPS" className="w-5 h-5 brightness-0 invert" />
                    <span>{t('animals.use_gps')}</span>
                  </span>
                )}
              </button>
              
              <button
                onClick={() => setShowMap(true)}
                className="w-full bg-green-600 text-white px-4 py-3 rounded-xl hover:bg-green-700 flex items-center justify-center space-x-2 font-medium transition-all"
              >
                <img src="/maps-icon.png" alt="Map" className="w-6 h-6" />
                <span>{t('animals.select_on_map')}</span>
              </button>
            </div>
          </div>

          {/* Privacy Settings */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="font-medium text-gray-800 mb-2">{t('settings.location_sharing')}</h4>
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                defaultChecked={user?.location_sharing_enabled !== false}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">{t('common.show_location_to_others')}</span>
            </label>
            <p className="text-xs text-gray-500 mt-1">{t('common.location_privacy_note')}</p>
          </div>

          {/* Benefits Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-800 mb-2">{t('location.benefits_title')}</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>{t('location.benefit_1')}</li>
              <li>{t('location.benefit_2')}</li>
              <li>{t('location.benefit_3')}</li>
              <li>{t('location.benefit_4')}</li>
            </ul>
          </div>
        </div>

        {/* Map Modal */}
        {showMap && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-60 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-lg max-w-3xl w-full max-h-screen overflow-hidden">
              <div className="p-4 border-b">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold">🗺️ {t('common.select_location')}</h3>
                  <button
                    onClick={() => setShowMap(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ×
                  </button>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  {t('common.click_to_select')}
                </p>
              </div>
              
              <div className="h-96 relative">
                <LocationPicker
                  center={selectedLocation ? [selectedLocation.lat, selectedLocation.lng] : mapCenter}
                  onLocationSelect={handleMapLocationSelect}
                  selectedLocation={selectedLocation ? [selectedLocation.lat, selectedLocation.lng] : null}
                />
              </div>
              
              <div className="p-4 border-t">
                <button
                  onClick={() => setShowMap(false)}
                  className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                >{t('common.cancel')}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
// Animal Profile Page Component
const AnimalProfilePage = () => {
  const { t, i18n } = useTranslation();
  const { animalId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser, logout } = useAuth();
  const { getBackgroundStyle } = useBackground();
  const [animal, setAnimal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  useEffect(() => {
    const loadAnimal = async () => {
      if (!animalId) return;
      
      setLoading(true);
      try {
        const response = await axios.get(`${API}/animals/${animalId}`);
        const animalData = response.data;
        setAnimal(animalData);
        
        // อัปเดต meta tags สำหรับ social sharing
        const shareTitle = `${animalData.name} - ${animalData.species}`;
        const shareDescription = animalData.description || `พบ ${animalData.name} บน TailLink - ชุมชนคนรักสัตว์`;
        // ใช้ endpoint รูปสัตว์จริง Or fallback เป็นรูป default
        const shareImage = animalData.photos && animalData.photos.length > 0 
          ? `${API}/animals/${animalId}/social-image`
          : `${window.location.origin}/taillink-social.jpg`;
          
        const shareUrl = window.location.href;
        
        // อัปเดต title
        document.title = `${shareTitle} | TailLink`;
        
      } catch (error) {
        console.error('Error loading animal:', error);
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    loadAnimal();
  }, [animalId, navigate]);

  // NEW: Reload animal data function
  const reloadAnimal = async () => {
    if (!animalId) return;
    
    try {
      const response = await axios.get(`${API}/animals/${animalId}`);
      setAnimal(response.data);
      console.log('✅ Animal data reloaded');
    } catch (error) {
      console.error('Error reloading animal:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-fur-light to-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4 animate-bounce">🐾</div>
          <p className="text-gray-600">{t('common.loading_animal_data')}</p>
        </div>
      </div>
    );
  }

  if (!animal) {
    return (
      <>
        <Helmet>
          <title>{t('meta.animal_not_found_title')}</title>
          <meta name="description" content={t('meta.animal_not_found_desc')} />
        </Helmet>
        <div className="min-h-screen bg-gradient-to-b from-fur-light to-white flex items-center justify-center">
          <div className="text-center">
            <div className="text-6xl mb-4">😿</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('alerts.animal_not_found')}</h2>
            <p className="text-gray-600 mb-4">{t('alerts.animal_not_found_desc')}</p>
            <button
              onClick={() => navigate('/')}
              className="bg-blue-600 text-white px-4 sm:px-6 py-2 rounded-md hover:bg-blue-700 flex items-center"
            >
              <span className="inline">🏠</span>
              <span className="hidden xs:inline ml-1">{t('nav.back_to_home')}</span>
            </button>
          </div>
        </div>
      </>
    );
  }

  const animalImage = animal.photos && animal.photos.length > 0 
    ? resolvePhotoSrc(animal.photos[0], 'medium') 
    : null;

  const shareTitle = `${animal.name} - ${animal.species}`;
  const shareDescription = animal.description || `พบ ${animal.name} บน TailLink - ชุมชนคนรักสัตว์`;
  const shareUrl = window.location.href;
  
  // ใช้ social-image endpoint สำหรับ sharing แทน base64
  const socialImage = animal.photos && animal.photos.length > 0 
    ? `${API}/animals/${animalId}/social-image`
    : `${window.location.origin}/taillink-social.jpg`;

  return (
    <>
      <Helmet>
        <title>{shareTitle} | TailLink</title>
        <meta name="description" content={shareDescription} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:title" content={shareTitle} />
        <meta property="og:description" content={shareDescription} />
        <meta property="og:site_name" content="TailLink" />
        <meta property="og:image" content={socialImage} />
        <meta property="og:image:width" content="800" />
        <meta property="og:image:height" content="600" />
        <meta property="og:image:type" content="image/jpeg" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={shareUrl} />
        <meta property="twitter:title" content={shareTitle} />
        <meta property="twitter:description" content={shareDescription} />
        <meta property="twitter:image" content={socialImage} />
        
        {/* Additional meta for other platforms */}
        <meta name="author" content="TailLink" />
        <meta name="keywords" content={`${animal.name}, ${animal.species}, สัตว์เลี้ยง, TailLink, ชุมชนคนรักสัตว์`} />
      </Helmet>
      
      <div className="min-h-screen" style={getBackgroundStyle()}>
        {/* Navigation */}
        <nav className="bg-white/95 shadow-sm border-b backdrop-blur-md">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => navigate('/')}
                  className="text-blue-600 hover:text-blue-800 flex items-center space-x-2"
                >
                  <img src="/taillink-logo.png" alt="TailLink" className="h-12 w-auto object-contain" />
                  <span className="fur-heading font-bold text-2xl">TailLink</span>
                </button>
                <span className="text-gray-400">|</span>
                <span className="hidden xs:inline text-gray-600">{t('common.animal_details')}</span>
              </div>
              
              <div className="flex items-center space-x-3">
                {/* Desktop Menu - Hidden on Mobile */}
                <div className="hidden md:flex items-center space-x-4">
                  {currentUser ? (
                    <>
                      <button
                        onClick={() => navigate(`/user/${currentUser.id}`)}
                        className="flex items-center space-x-2 text-gray-700 hover:text-blue-600"
                      >
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          🐾
                        </div>
                        <span>{currentUser.username}</span>
                      </button>
                      <button
                        onClick={() => {
                          logout();
                          navigate('/');
                        }}
                        className="text-gray-500 hover:text-gray-700 px-3 py-2 rounded-md"
                      >
                        🚪 {t('nav.logout')}
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => setShowLoginModal(true)}
                        className="text-blue-600 hover:text-blue-800 px-3 py-2 rounded-md"
                      >
                        เข้าสู่ระบบ
                      </button>
                      <button
                        onClick={() => setShowRegisterModal(true)}
                        className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
                      >
                        สมัครสมาชิก
                      </button>
                    </>
                  )}
                  <span className="text-gray-300">|</span>
                </div>

                {/* Share Button - Always Visible */}
                <button
                  onClick={() => {
                    // Use share endpoint for social media preview with dynamic meta tags
                    // This will show proper animal image in social media previews
                    // and redirect users to the actual animal page
                    const shareUrl = window.location.origin + `/api/share/animal/${animalId}`;
                    
                    navigator.share ? navigator.share({
                      title: `${animal.name} - ${animal.species}`,
                      text: animal.description,
                      url: shareUrl
                    }) : 
                    navigator.clipboard.writeText(shareUrl).then(() => alert(t('alerts.share_link_copied')))
                  }}
                  className="bg-green-600 text-white px-3 py-2 sm:px-4 sm:py-2 rounded-xl hover:bg-green-700 flex items-center space-x-1 sm:space-x-2 font-medium transition-all"
                >
                  <img src="/share-icon.png" alt="Share" className="w-5 h-5 brightness-0 invert" />
                  <span className="hidden sm:inline">{t('common.share')}</span>
                </button>
                
                {/* Hamburger Menu Button - Only on Mobile */}
                <button
                  onClick={() => setShowMobileMenu(true)}
                  className="md:hidden text-gray-700 hover:text-gray-900 p-2"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </nav>

        {/* Animal Profile Content */}
        <div className="max-w-4xl mx-auto py-8 px-4 pb-20">
          <AnimalDetailView
            animal={animal}
            onBack={() => navigate('/')}
            onEdit={(editedAnimal) => setAnimal(editedAnimal)}
            onAnimalUpdate={reloadAnimal}
            currentUser={currentUser}
            onViewUser={(userId) => navigate(`/user/${userId}`)}
          />
        </div>
      </div>

      {/* Bottom Navigation Bar - All Screen Sizes */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-gray-200 shadow-lg z-[2000]">
        <div className="flex justify-around items-center h-16">
          <button
            onClick={() => navigate('/?view=map')}
            className="flex flex-col items-center justify-center flex-1 h-full transition-colors text-gray-600 hover:text-blue-600"
          >
            <span className="text-2xl mb-1">🗺️</span>
            <span className="text-xs font-medium">Map</span>
          </button>
          
          <button
            onClick={() => navigate('/?view=feed')}
            className="flex flex-col items-center justify-center flex-1 h-full transition-colors text-gray-600 hover:text-blue-600"
          >
            <span className="text-2xl mb-1">📋</span>
            <span className="text-xs font-medium">Feed</span>
          </button>
          
          <button
            onClick={() => {
              if (currentUser) {
                navigate(`/user/${currentUser.id}`);
              } else {
                setShowLoginModal(true);
              }
            }}
            className="flex flex-col items-center justify-center flex-1 h-full transition-colors text-gray-600 hover:text-blue-600"
          >
            <span className="text-2xl mb-1">👤</span>
            <span className="text-xs font-medium">Profile</span>
          </button>
        </div>
      </div>

      {/* Login Modal */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSwitchToRegister={() => {
          setShowLoginModal(false);
          setShowRegisterModal(true);
        }}
      />

      {/* Register Modal */}
      <RegisterModal
        isOpen={showRegisterModal}
        onClose={() => setShowRegisterModal(false)}
        onSwitchToLogin={() => {
          setShowRegisterModal(false);
          setShowLoginModal(true);
        }}
      />

      {/* Mobile Menu */}
      <MobileMenu
        isOpen={showMobileMenu}
        onClose={() => setShowMobileMenu(false)}
        currentView="animal"
        onNavigate={(view) => {
          if (view === 'feed') {
            navigate('/?view=feed');
          } else if (view === 'map') {
            navigate('/?view=map');
          } else if (view === 'add') {
            navigate('/');
            // Will trigger add animal on main page
          } else if (view === 'set_location') {
            navigate('/');
            // Will trigger location manager on main page
          } else if (view.startsWith('profile_')) {
            const userId = view.replace('profile_', '');
            navigate(`/user/${userId}`);
          }
        }}
        isAuthenticated={!!currentUser}
        onLogin={() => {
          setShowMobileMenu(false);
          setShowLoginModal(true);
        }}
        onRegister={() => {
          setShowMobileMenu(false);
          setShowRegisterModal(true);
        }}
        onLogout={() => {
          logout();
          navigate('/');
        }}
        user={currentUser}
      />
    </>
  );
};

// User Profile Component
const UserProfile = ({ userId, currentUser, onBack }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { getContentOpacity } = useBackground();
  const [user, setUser] = useState(null);
  const [userAnimals, setUserAnimals] = useState([]);
  const [userStats, setUserStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    username: '',
    full_name: '',
    bio: ''
  });

  const isOwnProfile = currentUser && currentUser.id === userId;
  
  // Get dynamic opacity
  const opacity = getContentOpacity();
  const cardClass = `backdrop-blur-md rounded-lg shadow-md`;
  const cardStyle = { backgroundColor: `rgba(255, 255, 255, ${opacity})` };

  useEffect(() => {
    if (userId) {
      loadUserProfile();
    }
  }, [userId]);

  const loadUserProfile = async () => {
    setLoading(true);
    try {
      // Load user data and stats in parallel
      const [userResponse, animalsResponse, statsResponse] = await Promise.all([
        axios.get(`${API}/users/${userId}`),
        axios.get(`${API}/users/${userId}/animals`),
        axios.get(`${API}/users/${userId}/stats`)
      ]);
      
      setUser(userResponse.data);
      setUserAnimals(animalsResponse.data);
      setUserStats(statsResponse.data);
      
      // Initialize edit form with current data
      setEditForm({
        username: userResponse.data.username || '',
        full_name: userResponse.data.full_name || '',
        bio: userResponse.data.bio || ''
      });
    } catch (error) {
      console.error('Error loading user profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/auth/profile`, editForm);
      setIsEditing(false);
      loadUserProfile(); // Reload to show updated data
      alert(t('alerts.profile_updated'));
    } catch (error) {
      console.error('Error updating profile:', error);
      if (error.response?.data?.detail) {
        alert(`เกิดข้อผิดพลาด: ${error.response.data.detail}`);
      } else {
        alert(t('alerts.profile_update_error'));
      }
    }
  };

  const getUserAnimalIcon = () => {
    if (!userAnimals.length) return '🐾';
    // Get icon from user's most recent animal
    const latestAnimal = userAnimals[0];
    return getAnimalIcon(latestAnimal.species);
  };

  if (loading) {
    return (
      <div className={`${cardClass} p-6`} style={cardStyle}>
        <div className="animate-pulse">
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-20 h-20 bg-gray-200 rounded-full"></div>
            <div className="flex-1">
              <div className="h-6 bg-gray-200 rounded w-32 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-48"></div>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className={`${cardClass} p-6 text-center`} style={cardStyle}>
        <p className="text-gray-600">{t('alerts.user_not_found')}</p>
        <button
          onClick={onBack}
          className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
        >
          {t('common.back')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20">
      {/* Header */}
      <div className={`${cardClass} p-6`} style={cardStyle}>
              
        {isEditing ? (
          /* Edit Form */
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <h2 className="text-xl font-bold text-gray-800 mb-4">{`✏️ ${t('common.edit_profile')}`}</h2>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms.username_label')}</label>
              <input
                type="text"
                required
                value={editForm.username}
                onChange={(e) => setEditForm(prev => ({ ...prev, username: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder={t('forms.unique_username_placeholder')}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms.full_name_optional')}</label>
              <input
                type="text"
                value={editForm.full_name}
                onChange={(e) => setEditForm(prev => ({ ...prev, full_name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder={t('forms.full_name_placeholder')}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms.about_you_optional')}</label>
              <textarea
                value={editForm.bio}
                onChange={(e) => setEditForm(prev => ({ ...prev, bio: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                rows="3"
                placeholder={t('forms.about_you_placeholder')}
              />
            </div>
            
            <div className="flex space-x-3">
              <button
                type="submit"
                className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700"
              >{`💾 ${t('common.save')}`}</button>
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                className="bg-gray-300 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-400"
              >{t('common.cancel')}</button>
            </div>
          </form>
        ) : (
          /* Display Mode */
          <>
            <div className="flex items-center space-x-6 mb-6">
              {/* User Avatar - Animal Icon */}
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-green-500 rounded-full flex items-center justify-center text-4xl">
                {getUserAnimalIcon()}
              </div>
              
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h1 className="text-2xl font-bold text-gray-800">{user.full_name || user.username}</h1>
                  {isOwnProfile && (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="bg-blue-600 text-white px-2 sm:px-3 py-1 rounded-md text-sm hover:bg-blue-700 flex items-center"
                    >
                      <span className="inline">✏️</span>
                      <span className="hidden xs:inline ml-1">{t('common.edit')}</span>
                    </button>
                  )}
                </div>
                <p className="text-gray-600">@{user.username}</p>
                {user.bio && (
                  <p className="text-gray-700 mt-2">{user.bio}</p>
                )}
                <p className="text-sm text-gray-500 mt-1">
                  {t('profile.member_since')} {new Date(user.created_at).toLocaleDateString(getLocaleCode(i18n.language))}
                </p>
                
                {/* Account Info - Only visible to profile owner */}
                {isOwnProfile && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">{t('profile.account_info')}</h3>
                    <div className="space-y-2 text-sm">
                      {user.email && (
                        <div className="flex items-center space-x-2 text-gray-600">
                          <span className="text-lg">📧</span>
                          <span className="font-medium">{t('profile.email')}:</span>
                          <span className="text-gray-800">{user.email}</span>
                        </div>
                      )}
                      {user.provider && (
                        <div className="flex items-center space-x-2 text-gray-600">
                          <span className="text-lg">
                            {user.provider === 'google' ? '🔐' : '🔑'}
                          </span>
                          <span className="font-medium">{t('profile.login_method')}:</span>
                          <span className="text-gray-800">
                            {user.provider === 'google' ? t('profile.google_account') : t('profile.email_password')}
                          </span>
                        </div>
                      )}
                      {user.provider === 'google' && user.provider_id && (
                        <div className="flex items-center space-x-2 text-gray-600">
                          <span className="text-lg">🆔</span>
                          <span className="font-medium">{t('profile.google_id')}:</span>
                          <span className="text-gray-800 text-xs font-mono">{user.provider_id}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Stats */}
            {userStats && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg text-center">
                    <div className="text-2xl font-bold text-blue-600">{userStats.total_animals}</div>
                    <div className="text-sm text-gray-600">{t('profile.total_animals')}</div>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg text-center">
                    <div className="text-2xl font-bold text-green-600">{userStats.owned_pets}</div>
                    <div className="text-sm text-gray-600">{t('animals.owned')}</div>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg text-center">
                    <div className="text-2xl font-bold text-orange-600">{userStats.stray_animals}</div>
                    <div className="text-sm text-gray-600">{t('animals.stray')}</div>
                  </div>
                  <div className="bg-teal-50 p-4 rounded-lg text-center">
                    <div className="text-2xl font-bold text-teal-600">{userStats.social_pets || 0}</div>
                    <div className="text-sm text-gray-600">{t('animals.social_pet')}</div>
                  </div>
                </div>
                
                {/* Secondary Stats Row */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
                  <div className="bg-orange-50 p-4 rounded-lg text-center">
                    <div className="text-2xl font-bold text-orange-600">{userStats.total_followers}</div>
                    <div className="text-sm text-gray-600">{t('profile.total_followers')}</div>
                  </div>
                  <div className="bg-pink-50 p-4 rounded-lg text-center">
                    <div className="text-2xl font-bold text-pink-600">{userStats.total_posts || 0}</div>
                    <div className="text-sm text-gray-600">{t('profile.total_posts')}</div>
                  </div>
                  <div className="bg-indigo-50 p-4 rounded-lg text-center">
                    <div className="text-2xl font-bold text-indigo-600">
                      {userStats.member_since && new Date(userStats.member_since).toLocaleDateString(getLocaleCode(i18n.language), { 
                        year: 'numeric', 
                        month: 'short' 
                      })}
                    </div>
                    <div className="text-sm text-gray-600">{t('profile.member_since')}</div>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* User's Animals */}
      <div className={`${cardClass} p-6`} style={cardStyle}>
        <h2 className="text-xl font-bold text-gray-800 mb-4">{t('profile.animals_of')} {user.username}</h2>
        
        {userAnimals.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-6xl mb-4">🐾</div>
            <p className="text-gray-600">{t('alerts.no_registered_animals')}</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Group animals by type and show each group */}
            {[
              { type: 'owned', title: t('profile.owned_pets'), color: 'green' },
              { type: 'stray', title: t('profile.stray_animals'), color: 'orange' }, 
              { type: 'social_pet', title: t('profile.community_pets'), color: 'teal' }
            ].map(group => {
              const animalsInGroup = userAnimals.filter(animal => animal.animal_type === group.type);
              if (animalsInGroup.length === 0) return null;
              
              return (
                <div key={group.type} className="space-y-4">
                  <div className="flex items-center space-x-2">
                   
                                 
                    <h3 className="text-lg font-semibold text-gray-800">{group.title}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium bg-${group.color}-100 text-${group.color}-800`}>
                      {animalsInGroup.length} {t('profile.animals_count')}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {animalsInGroup.map((animal) => (
                      <div key={animal.id} className="border rounded-lg p-4 hover:shadow-lg transition-shadow">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-2">
                            <span className="text-2xl">{getAnimalIcon(animal.species)}</span>
                            <h4 className="font-semibold text-gray-800">{animal.name}</h4>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            animal.animal_type === 'owned' ? 'bg-green-100 text-green-800' :
                            animal.animal_type === 'stray' ? 'bg-orange-100 text-orange-800' :
                            animal.animal_type === 'wild' ? 'bg-amber-100 text-amber-800' :
                            'bg-teal-100 text-teal-800'
                          }`}>
                            {animal.animal_type === 'owned' ? t('profile.pet_type_owned') :
                             animal.animal_type === 'stray' ? t('profile.pet_type_stray') :
                             animal.animal_type === 'wild' ? t('profile.pet_type_wild') : 
                             t('profile.pet_type_community')}
                          </span>
                        </div>
                        
                        <p className="text-gray-600 text-sm mb-2">
                          {animal.species} {animal.breed && `• ${animal.breed}`}
                        </p>
                        
                        <p className="text-gray-700 text-sm mb-3 line-clamp-2">
                          {animal.description}
                        </p>
                        
                        <div className="flex justify-between items-center text-xs text-gray-500 mb-3">
                          <span>👥 {animal.followers?.length || 0} {t('common.followers')}</span>
                          <span>{new Date(animal.created_at).toLocaleDateString(getLocaleCode(i18n.language))}</span>
                        </div>
                        
                        {animal.location && (
                          <p className="text-xs text-gray-500 mb-3">
                            📍 {animal.location.address || `${animal.location.lat?.toFixed(4)}, ${animal.location.lng?.toFixed(4)}`}
                          </p>
                        )}
                        
                        <button
                          onClick={() => navigate(`/animal/${animal.id}`)}
                          className="w-full bg-blue-600 text-white py-2 rounded-md text-sm hover:bg-blue-700"
                        >
                          {t('profile.view_details')}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// Admin Panel Component
const AdminPanel = ({ user }) => {
  const [heroContents, setHeroContents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingHero, setEditingHero] = useState(null);
  const [formData, setFormData] = useState({
    type: 'custom',
    title: '',
    subtitle: '',
    description: '',
    bullet_points: ['', '', ''],
    image_url: '',
    image_base64: ''
  });

  useEffect(() => {
    loadHeroContents();
  }, []);

  const loadHeroContents = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/hero-content`);
      setHeroContents(response.data);
    } catch (error) {
      console.error('Error loading hero contents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result.split(',')[1];
        setFormData(prev => ({ ...prev, image_base64: base64, image_url: '' }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const cleanBulletPoints = formData.bullet_points.filter(point => point.trim());
      const submitData = {
        ...formData,
        bullet_points: cleanBulletPoints
      };

      if (editingHero) {
        await axios.put(`${API}/admin/hero-content/${editingHero.id}`, submitData);
      } else {
        await axios.post(`${API}/admin/hero-content`, submitData);
      }

      setShowCreateForm(false);
      setEditingHero(null);
      setFormData({
        type: 'custom',
        title: '',
        subtitle: '',
        description: '',
        bullet_points: ['', '', ''],
        image_url: '',
        image_base64: ''
      });
      loadHeroContents();
    } catch (error) {
      console.error('Error saving hero content:', error);
      alert(t('alerts.save_error'));
    }
  };

  const handleToggleActive = async (heroId) => {
    try {
      await axios.post(`${API}/admin/hero-content/${heroId}/toggle`);
      loadHeroContents();
    } catch (error) {
      console.error('Error toggling hero content:', error);
    }
  };

  const handleDelete = async (heroId) => {
    if (window.confirm(t('alerts.delete_hero_confirm'))) {
      try {
        await axios.delete(`${API}/admin/hero-content/${heroId}`);
        loadHeroContents();
      } catch (error) {
        console.error('Error deleting hero content:', error);
      }
    }
  };

  const handleRegenerateAuto = async () => {
    try {
      await axios.post(`${API}/admin/hero-content/regenerate-auto`);
      loadHeroContents();
    } catch (error) {
      console.error('Error regenerating auto content:', error);
    }
  };

  const startEdit = (hero) => {
    setEditingHero(hero);
    setFormData({
      type: hero.type,
      title: hero.title,
      subtitle: hero.subtitle,
      description: hero.description,
      bullet_points: [...hero.bullet_points, '', '', ''].slice(0, 3),
      image_url: hero.image_url || '',
      image_base64: hero.image_base64 || ''
    });
    setShowCreateForm(true);
  };

  return (
    <div className="bg-white rounded-lg shadow-md">
      <div className="p-6 border-b">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">🛠️ Admin: Hero Content Management</h2>
            <p className="text-gray-600">จัดการเนื้อหาส่วน Hero Section ของหน้าแรก</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={handleRegenerateAuto}
              className="bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700"
            >
              🔄 Createเนื้อหาอัตโนมัติใหม่
            </button>
            <button
              onClick={() => {
                setShowCreateForm(true);
                setEditingHero(null);
                setFormData({
                  type: 'custom',
                  title: '',
                  subtitle: '',
                  description: '',
                  bullet_points: ['', '', ''],
                  image_url: '',
                  image_base64: ''
                });
              }}
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
            >
              ➕ Createเนื้อหาใหม่
            </button>
          </div>
        </div>
      </div>

      <div className="p-6">
        {loading ? (
          <div className="text-center py-8">
            <div className="spinner mb-4"></div>
            <p className="text-gray-600">{t('common.loading')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {heroContents.map((hero) => (
              <div
                key={hero.id}
                className={`border rounded-lg p-4 ${hero.is_active ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        hero.type === 'auto' ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800'
                      }`}>
                        {hero.type === 'auto' ? '🤖 Auto' : `✏️ ${t('admin.hero_custom')}`}
                      </span>
                      {hero.is_active && (
                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">{`🟢 ${t('common.active')}`}</span>
                      )}
                    </div>
                    <h3 className="font-bold text-lg">{hero.title}</h3>
                    <p className="text-gray-600 mb-2">{hero.subtitle}</p>
                    <p className="text-sm text-gray-500">{hero.description}</p>
                    {hero.bullet_points && hero.bullet_points.length > 0 && (
                      <ul className="mt-2 text-sm text-gray-600">
                        {hero.bullet_points.map((point, index) => (
                          <li key={index}>• {point}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                  
                  {(hero.image_base64 || hero.image_url) && (
                    <div className="ml-4">
                      <img
                        src={hero.image_base64 ? `data:image/jpeg;base64,${hero.image_base64}` : hero.image_url}
                        alt="Hero image"
                        className="w-20 h-20 object-cover rounded-md"
                      />
                    </div>
                  )}
                </div>

                <div className="flex space-x-2">
                  <button
                    onClick={() => handleToggleActive(hero.id)}
                    className={`px-3 py-1 rounded text-sm font-medium ${
                      hero.is_active
                        ? 'bg-red-100 text-red-700 hover:bg-red-200'
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
                  >
                    {hero.is_active ? `⏸️ ${t('common.disable')}` : `▶️ ${t('common.enable')}`}
                  </button>
                  
                  {hero.type === 'custom' && (
                    <button
                      onClick={() => startEdit(hero)}
                      className="bg-blue-100 text-blue-700 px-3 py-1 rounded text-sm font-medium hover:bg-blue-200"
                    >{`✏️ ${t('common.edit')}`}</button>
                  )}
                  
                  <button
                    onClick={() => handleDelete(hero.id)}
                    className="bg-red-100 text-red-700 px-3 py-1 rounded text-sm font-medium hover:bg-red-200"
                  >{`🗑️ ${t('common.delete')}`}</button>
                  
                  <span className="text-xs text-gray-400 self-center">
                    {t('admin.created_at')} {new Date(hero.created_at).toLocaleDateString(getLocaleCode(i18n.language))}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create/Edit Form */}
        {showCreateForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full max-h-screen overflow-y-auto">
              <div className="p-6">
                <h3 className="text-xl font-bold mb-4">
                  {editingHero ? t('common.edit_hero_content') : t('common.create_hero_content')}
                </h3>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.hero_type')}</label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="custom">{t('admin.hero_custom')}</option>
                      <option value="auto">{t('admin.hero_auto')}</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.hero_main_title')}</label>
                    <input
                      type="text"
                      required
                      value={formData.title}
                      onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder={t('admin.hero_main_title_placeholder')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.hero_subtitle')}</label>
                    <input
                      type="text"
                      required
                      value={formData.subtitle}
                      onChange={(e) => setFormData(prev => ({ ...prev, subtitle: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder={t('admin.hero_subtitle_placeholder')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('animals.description')}</label>
                    <textarea
                      required
                      rows="3"
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder={t('forms.additional_description')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Highlights (Max 3)</label>
                    {formData.bullet_points.map((point, index) => (
                      <input
                        key={index}
                        type="text"
                        value={point}
                        onChange={(e) => {
                          const newPoints = [...formData.bullet_points];
                          newPoints[index] = e.target.value;
                          setFormData(prev => ({ ...prev, bullet_points: newPoints }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 mb-2"
                        placeholder={`Highlight ${index + 1} (e.g., 🏠 Find owned pets looking for friends)`}
                      />
                    ))}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms.upload_photos')}</label>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Upload Image:</label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                      </div>
                      
                      <div className="text-center text-gray-500">Or</div>
                      
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Image URL:</label>
                        <input
                          type="url"
                          value={formData.image_url}
                          onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value, image_base64: '' }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          placeholder="https://example.com/image.jpg"
                        />
                      </div>

                      {(formData.image_base64 || formData.image_url) && (
                        <div className="mt-2">
                          <img
                            src={formData.image_base64 ? `data:image/jpeg;base64,${formData.image_base64}` : formData.image_url}
                            alt="Preview"
                            className="w-32 h-32 object-cover rounded-md"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex space-x-3 pt-4">
                    <button
                      type="submit"
                      className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700"
                    >
                      {editingHero ? {Update} : {Create}} Hero Content
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreateForm(false);
                        setEditingHero(null);
                      }}
                      className="bg-gray-300 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-400"
                    >{t('common.cancel')}</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Dynamic Hero Section Component
const DynamicHeroSection = () => {
  const [heroContent, setHeroContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHeroContent();
  }, []);

  const loadHeroContent = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/hero-content`);
      setHeroContent(response.data);
    } catch (error) {
      console.error('Error loading hero content:', error);
      // Fallback to default content
      setHeroContent({
        title: "Discover Animals Near You",
        subtitle: "Connect with pets, help stray animals, and build a caring community through TailLink",
        description: "เริ่มต้นCreateชุมชนคนรักสัตว์ในย่านของคุณวันนี้",
        bullet_points: [
          "🏠 Find owned pets looking for friends",
          "🐕 Help stray animals in need",
          "❤️ Support community social pets"
        ],
        image_url: "https://images.unsplash.com/photo-1687360440731-d124aea41a3e"
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-r from-fur-primary/10 to-fur-secondary/50 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <div className="h-10 bg-white bg-opacity-20 rounded mb-4 animate-pulse"></div>
              <div className="h-6 bg-white bg-opacity-20 rounded mb-6 animate-pulse"></div>
              <div className="space-y-2">
                <div className="h-4 bg-white bg-opacity-20 rounded animate-pulse"></div>
                <div className="h-4 bg-white bg-opacity-20 rounded animate-pulse"></div>
                <div className="h-4 bg-white bg-opacity-20 rounded animate-pulse"></div>
              </div>
            </div>
            <div className="flex justify-center">
              <div className="w-full max-w-md h-64 bg-white bg-opacity-20 rounded-lg animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (/* content hero
    <div className="bg-gradient-to-r from-fur-primary/10 to-fur-secondary/50 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="text-4xl font-bold mb-4">
              {heroContent?.title || "Discover Animals Near You"}
            </h2>
            <p className="text-xl mb-6">
              {heroContent?.subtitle || "Connect with pets, help stray animals, and build a caring community through TailLink"}
            </p>
            {heroContent?.bullet_points && heroContent.bullet_points.length > 0 && (
              <div className="space-y-2 text-lg">
                {heroContent.bullet_points.map((point, index) => (
                  <p key={index}>{point}</p>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-center">
            <img 
              src={
                heroContent?.image_base64 
                  ? `data:image/jpeg;base64,${heroContent.image_base64}`
                  : heroContent?.image_url || "https://images.unsplash.com/photo-1687360440731-d124aea41a3e"
              }
              alt="Hero image"
              className="rounded-lg shadow-lg max-w-md w-full"
            />
          </div>
        </div>
      </div>
    </div>
  */
  null
  );
};

// Add Animal Form Component
const AddAnimalForm = ({ onSubmit, onCancel, currentUser }) => {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    animal_type: 'owned',
    species: 'dog',
    breed: '',
    age: '',
    description: '',
    photos: [],
    location: null,
    owner_id: null,
    tags: [],
    health_status: '',
    blood_type: '',  // NEW: Blood type field
    needs_help: false,
    help_description: '',
    show_exact_location: true  // Default: show exact location
  });

  const [step, setStep] = useState(1);
  const [isSelectingLocation, setIsSelectingLocation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tags, setTags] = useState('');

  useEffect(() => {
    if (formData.animal_type === 'owned' && currentUser) {
      setFormData(prev => ({ ...prev, owner_id: currentUser.id }));
    } else {
      setFormData(prev => ({ ...prev, owner_id: null }));
    }
  }, [formData.animal_type, currentUser]);

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length === 0) return;
    
    const resizedImages = [];
    
    for (let i = 0; i < Math.min(files.length, 5 - formData.photos.length); i++) {
      const file = files[i];
      
      // ตรวจสอบt('admin.hero_type')ไฟล์
      if (!file.type.startsWith('image/')) {
        alert(t('alerts.please_select_image_only'));
        continue;
      }
      
      // ตรวจสอบขนาดไฟล์ (ก่อนย่อ)
      if (file.size > 10 * 1024 * 1024) { // 10MB
        alert(t('alerts.max_file_size'));
        continue;
      }
      
      try {
        // Resize image
        const resizedBase64 = await new Promise((resolve) => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const img = new Image();
          
          img.onload = () => {
            // คำนวณขนาดใหม่โดยคงอัตราส่วน
            const maxWidth = 800;
            const maxHeight = 600;
            let { width, height } = img;
            
            if (width > height) {
              if (width > maxWidth) {
                height = (height * maxWidth) / width;
                width = maxWidth;
              }
            } else {
              if (height > maxHeight) {
                width = (width * maxHeight) / height;
                height = maxHeight;
              }
            }
            
            // ตั้งค่า canvas
            canvas.width = width;
            canvas.height = height;
            
            // วาดรูปที่ย่อขนาดแล้ว
            ctx.drawImage(img, 0, 0, width, height);
            
            // แปลงเป็น base64 (เอาเฉพาะส่วนข้อมูล ไม่เอา data:image/jpeg;base64,)
            const base64 = canvas.toDataURL('image/jpeg', 0.8).split(',')[1];
            resolve(base64);
          };
          
          img.src = URL.createObjectURL(file);
        });
        
        resizedImages.push(resizedBase64);
      } catch (error) {
        console.error('Error resizing image:', error);
        alert(t('alerts.image_error'));
      }
    }
    
    // อัปเดต state ด้วยรูปที่ย่อแล้ว
    setFormData(prev => ({
      ...prev,
      photos: [...prev.photos, ...resizedImages].slice(0, 5)
    }));
    
    // เคลียร์ input file เพื่อให้สามารถเลือกไฟล์เดิมซ้ำได้
    e.target.value = '';
  };

  const removePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Prepare tags array
      const tagsArray = tags.split(',').map(tag => tag.trim()).filter(tag => tag);
      
      const animalData = {
        ...formData,
        tags: tagsArray,
        photos: formData.photos
      };

      await axios.post(`${API}/animals`, animalData);
      
      alert(t('alerts.animal_added_success'));
      onSubmit();
    } catch (error) {
      console.error('Error creating animal:', error);
      alert(t('alerts.animal_add_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-3">
          <button
            onClick={onCancel}
            className="text-gray-600 hover:text-gray-800 hover:bg-gray-100 p-2 rounded-lg transition-colors"
            title={t('common.back')}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <h2 className="text-2xl font-bold text-gray-900">{`➕ ${t('common.add_new_animal')}`}</h2>
        </div>
        <button
          onClick={onCancel}
          className="text-gray-500 hover:text-gray-700"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms.animal_name_required')}</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
              placeholder={t('forms.animal_name_placeholder')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('animals.animal_type')} *</label>
            <select
              value={formData.animal_type}
              onChange={(e) => setFormData(prev => ({ ...prev, animal_type: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="owned">{t('animals.my_pet')}</option>
              <option value="stray">{t('animals.stray')}</option>
              <option value="social_pet">{t('animals.social_pet')}</option>
              <option value="wild">{t('profile.pet_type_wild')}</option>
            </select>
          </div>
          
          {/* Privacy Setting for Owned Pets */}
          {formData.animal_type === 'owned' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-2">🔒 {t('profile.privacy_title')}</h4>
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.show_exact_location !== false}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    show_exact_location: e.target.checked 
                  }))}
                  className="mt-1 w-5 h-5 text-blue-600 rounded"
                />
                <div>
                  <span className="text-sm font-medium text-gray-800">
                    {t('profile.show_exact_location')}
                  </span>
                  <p className="text-xs text-gray-600 mt-1">
                    {t('profile.show_exact_location_help')}
                  </p>
                </div>
              </label>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('animals.species')} *</label>
            <select
              value={formData.species}
              onChange={(e) => setFormData(prev => ({ ...prev, species: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="dog">{t('animals.dog')}</option>
              <option value="cat">{t('animals.cat')}</option>
              <option value="bird">{t('animals.bird')}</option>
              <option value="rabbit">{t('animals.rabbit')}</option>
              <option value="hamster">{t('animals.hamster')}</option>
              <option value="fish">{t('animals.fish')}</option>
              <option value="turtle">{t('animals.turtle')}</option>
              <option value="other">{t('animals.other')}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms.breed_optional')}</label>
            <input
              type="text"
              value={formData.breed}
              onChange={(e) => setFormData(prev => ({ ...prev, breed: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
              placeholder={t('forms.breed_placeholder')}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('animals.description')} *</label>
          <textarea
            required
            rows="3"
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
            placeholder={t('forms.description_placeholder')}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms.age_optional')}</label>
            <input
              type="text"
              value={formData.age}
              onChange={(e) => setFormData(prev => ({ ...prev, age: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
              placeholder={t('forms.age_placeholder')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms.blood_type_optional')}</label>
            <select
              value={formData.blood_type}
              onChange={(e) => setFormData(prev => ({ ...prev, blood_type: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">{t('forms.not_specified')}</option>
              <option value="DEA 1.1+">DEA 1.1+ ({t('forms.dog_blood')})</option>
              <option value="DEA 1.1-">DEA 1.1- ({t('forms.dog_blood')})</option>
              <option value="DEA 3+">DEA 3+ ({t('forms.dog_blood')})</option>
              <option value="DEA 3-">DEA 3- ({t('forms.dog_blood')})</option>
              <option value="DEA 4+">DEA 4+ ({t('forms.dog_blood')})</option>
              <option value="DEA 4-">DEA 4- ({t('forms.dog_blood')})</option>
              <option value="A">A ({t('forms.cat_blood')})</option>
              <option value="B">B ({t('forms.cat_blood')})</option>
              <option value="AB">AB ({t('forms.cat_blood')})</option>
              <option value="Universal Donor">{t('forms.universal_donor')}</option>
              <option value="Universal Recipient">{t('forms.universal_recipient')}</option>
              <option value="Other">{t('forms.other')}</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms.health_status_optional')}</label>
            <input
              type="text"
              value={formData.health_status}
              onChange={(e) => setFormData(prev => ({ ...prev, health_status: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
              placeholder={t('forms.health_status_placeholder')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms.tags_comma_separated')}</label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
              placeholder={t('forms.tags_placeholder')}
            />
          </div>
        </div>

        {/* Photos Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('forms.upload_photos')}</label>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handlePhotoUpload}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          />
          
          {formData.photos.length > 0 && (
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
              {formData.photos.map((photo, index) => (
                <div key={index} className="relative">
                  <img
                    src={resolvePhotoSrc(photo, 'medium')}
                    alt={`Photo ${index + 1}`}
                    className="w-full h-32 object-cover rounded-md"
                  />
                  <button
                    type="button"
                    onClick={() => removePhoto(index)}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Location */}
        <LocationSelector
          onLocationSelect={(location) => setFormData(prev => ({ ...prev, location }))}
          selectedLocation={formData.location}
          showExactLocation={formData.show_exact_location}
        />

        {/* Help Request Section */}
        <div className="border-t pt-4">
          <div className="flex items-center space-x-2 mb-3">
            <input
              type="checkbox"
              id="needs_help"
              checked={formData.needs_help}
              onChange={(e) => setFormData(prev => ({ ...prev, needs_help: e.target.checked }))}
              className="rounded border-gray-300 focus:ring-blue-500"
            />
            <label htmlFor="needs_help" className="text-sm font-medium text-gray-700">
              {t('forms.needs_help')}
            </label>
          </div>
          
          {formData.needs_help && (
            <textarea
              rows="3"
              value={formData.help_description}
              onChange={(e) => setFormData(prev => ({ ...prev, help_description: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
              placeholder={t('forms.help_description_placeholder')}
            />
          )}
        </div>

        {/* Submit Buttons */}
        <div className="flex space-x-4 pt-4">
          <button
            type="submit"
            disabled={loading || !formData.location}
            className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-all"
          >
            {loading ? t('alerts.saving') : `💾 ${t('common.save_animal_data')}`}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 font-medium transition-all"
          >{t('common.cancel')}</button>
        </div>
      </form>
    </div>
  );
};

// Spotted Here Modal Component
const SpottedHereModal = ({ isOpen, onClose, animal, onSuccess }) => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState('');
  const [location, setLocation] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [mapCenter, setMapCenter] = useState([13.7563, 100.5018]); // Default to Bangkok

  useEffect(() => {
    if (isOpen) {
      // Reset form when modal opens
      setReason('');
      setLocation(null);
      setGettingLocation(false);
      setShowMap(false);
    }
  }, [isOpen]);

  const getCurrentLocation = async () => {
    setGettingLocation(true);
    
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`
            );
            const data = await response.json();
            const address = data.display_name || `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
            
            setLocation({ lat, lng, address });
            setMapCenter([lat, lng]);
          } catch (error) {
            console.error('Error getting address:', error);
            setLocation({ 
              lat, 
              lng, 
              address: `${lat.toFixed(6)}, ${lng.toFixed(6)}` 
            });
          }
          setGettingLocation(false);
        },
        (error) => {
          console.error('Error getting location:', error);
          alert(t('alerts.cannot_get_location'));
          setGettingLocation(false);
        }
      );
    } else {
      alert(t('alerts.geolocation_not_supported'));
      setGettingLocation(false);
    }
  };

  const handleMapLocationSelect = async (selectedLoc) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${selectedLoc.lat}&lon=${selectedLoc.lng}&addressdetails=1`
      );
      const data = await response.json();
      const address = data.display_name || `${selectedLoc.lat.toFixed(6)}, ${selectedLoc.lng.toFixed(6)}`;
      
      setLocation({ ...selectedLoc, address });
      setShowMap(false);
    } catch (error) {
      console.error('Error getting address:', error);
      setLocation(selectedLoc);
      setShowMap(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!reason.trim()) {
      alert(t('spotted_here.reason_required'));
      return;
    }

    if (!location) {
      alert(t('alerts.please_select_location'));
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/animals/${animal.id}/spotted-here`, {
        lat: location.lat,
        lng: location.lng,
        address: location.address,
        reason: reason.trim()
      });

      alert(t('alerts.location_updated'));
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error updating location:', error);
      if (error.response?.data?.detail) {
        alert(`Error: ${error.response.data.detail}`);
      } else {
        alert(t('alerts.location_update_error'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full max-h-screen overflow-y-auto">
        <div className="p-6 border-b">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-bold text-gray-800">{t('spotted_here.title')}</h3>
              <p className="text-sm text-gray-600 mt-1">{t('spotted_here.subtitle')}</p>
            </div>
            <button
              onClick={onClose}
              disabled={loading}
              className="text-gray-500 hover:text-gray-700 text-2xl"
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Animal Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <div className="text-3xl">{getAnimalIcon(animal.species)}</div>
              <div>
                <h4 className="font-semibold text-gray-800">{animal.name}</h4>
                <p className="text-sm text-gray-600">{animal.species} • {animal.breed}</p>
              </div>
            </div>
          </div>

          {/* Location Selection */}
          {!location ? (
            <div>
              <h4 className="font-medium text-gray-800 mb-3">{t('common.select_location_method')}</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={getCurrentLocation}
                  disabled={gettingLocation}
                  className="w-full bg-blue-600 text-white px-4 py-3 rounded-xl hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center font-medium transition-all"
                >
                  {gettingLocation ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      {t('common.locating')}
                    </>
                  ) : (
                    <span className="flex items-center space-x-2">
                      <img src="/location-pin.png" alt="GPS" className="w-5 h-5 brightness-0 invert" />
                      <span>{t('animals.use_gps')}</span>
                    </span>
                  )}
                </button>
                
                <button
                  onClick={() => setShowMap(true)}
                  className="w-full bg-green-600 text-white px-4 py-3 rounded-xl hover:bg-green-700 flex items-center justify-center space-x-2 font-medium transition-all"
                >
                  <img src="/maps-icon.png" alt="Map" className="w-6 h-6" />
                  <span>{t('animals.select_on_map')}</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-medium text-green-800 mb-2">✅ {t('spotted_here.new_location')}</h4>
                  <p className="text-sm text-green-700 mb-1">{location.address}</p>
                  <p className="text-xs text-green-600">
                    📍 {location.lat.toFixed(6)}, {location.lng.toFixed(6)}
                  </p>
                </div>
                <button
                  onClick={() => setLocation(null)}
                  className="text-red-600 hover:text-red-800 text-sm ml-4"
                >
                  {t('common.delete')}
                </button>
              </div>
            </div>
          )}

          {/* Reason Input - Only show when location is selected */}
          {location && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('spotted_here.reason_label')}
                </label>
                <textarea
                  required
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  rows="4"
                  placeholder={t('spotted_here.reason_placeholder')}
                  disabled={loading}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('spotted_here.reason_required')}
                </p>
              </div>

              {/* Warning */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  ⚠️ This will update the animal's location and notify the owner and followers.
                </p>
              </div>

              {/* Buttons */}
              <div className="flex space-x-3">
                <button
                  onClick={handleSubmit}
                  disabled={loading || !location || !reason.trim()}
                  className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-all"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      {t('common.loading')}
                    </div>
                  ) : (
                    t('spotted_here.confirm_button')
                  )}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-all"
                >
                  {t('spotted_here.cancel_button')}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Map Modal */}
        {showMap && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-60 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-lg max-w-3xl w-full max-h-screen overflow-hidden">
              <div className="p-4 border-b">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold">🗺️ {t('common.select_location')}</h3>
                  <button
                    onClick={() => setShowMap(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ×
                  </button>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  {t('common.click_to_select')}
                </p>
              </div>
              
              <div className="h-96 relative">
                <LocationPicker
                  center={mapCenter}
                  onLocationSelect={handleMapLocationSelect}
                  selectedLocation={location ? [location.lat, location.lng] : null}
                />
              </div>
              
              <div className="p-4 border-t">
                <button
                  onClick={() => setShowMap(false)}
                  className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Merge Animal Modal Component
const MergeAnimalModal = ({ isOpen, onClose, currentAnimal, onSuccess }) => {
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState('search'); // 'search', 'compare', 'confirm'
  const [searchResults, setSearchResults] = useState([]);
  const [selectedAnimal, setSelectedAnimal] = useState(null);
  const [primaryAnimalId, setPrimaryAnimalId] = useState(null);
  const [mergeReason, setMergeReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingAnimals, setLoadingAnimals] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Reset form when modal opens
      setStep('search');
      setSearchResults([]);
      setSelectedAnimal(null);
      setPrimaryAnimalId(currentAnimal.id);
      setMergeReason('');
      
      // Load nearby animals automatically
      loadNearbyAnimals();
    }
  }, [isOpen, currentAnimal.id]);

  const loadNearbyAnimals = async () => {
    setLoadingAnimals(true);
    try {
      const params = {};
      
      // Add location filter (1 km radius from current animal)
      if (currentAnimal.location?.lat && currentAnimal.location?.lng) {
        params.lat = currentAnimal.location.lat;
        params.lng = currentAnimal.location.lng;
        params.radius = 1000; // 1000 meters = 1 km
      }
      
      const response = await axios.get(`${API}/animals`, { params });
      // Filter out current animal from results and sort by distance
      const filtered = response.data
        .filter(a => a.id !== currentAnimal.id)
        .sort((a, b) => (a.distance || 999) - (b.distance || 999)); // Sort by distance, closest first
      
      setSearchResults(filtered);
    } catch (error) {
      console.error('Error loading nearby animals:', error);
      alert(t('merge.merge_error'));
    } finally {
      setLoadingAnimals(false);
    }
  };

  const handleSelectAnimal = (animal) => {
    if (animal.id === currentAnimal.id) {
      alert(t('merge.cannot_merge_self'));
      return;
    }
    setSelectedAnimal(animal);
    setStep('compare');
  };

  const handleMerge = async () => {
    if (!mergeReason.trim()) {
      alert(t('merge.reason_required'));
      return;
    }

    if (!selectedAnimal) return;

    const isPrimaryThis = primaryAnimalId === currentAnimal.id;
    const primary = isPrimaryThis ? currentAnimal : selectedAnimal;
    const secondary = isPrimaryThis ? selectedAnimal : currentAnimal;

    // Additional confirmation
    const confirmMessage = `⚠️ ${t('merge.confirm_merge_title')}\n\n` +
      `${t('merge.primary_label')}: ${primary.name}\n` +
      `${t('merge.secondary_label')}: ${secondary.name}\n\n` +
      `${t('merge.merge_reason')}: ${mergeReason}\n\n` +
      `${t('merge.confirm_merge_question')}`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/animals/${primary.id}/merge/${secondary.id}`, {
        reason: mergeReason.trim()
      });
      
      alert(t('merge.merge_success'));
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error merging animals:', error);
      if (error.response?.data?.detail) {
        alert(`Error: ${error.response.data.detail}`);
      } else {
        alert(t('merge.merge_error'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const AnimalPreviewCard = ({ animal, isPrimary }) => (
    <div className={`border-2 rounded-lg p-4 ${isPrimary ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50'}`}>
      <div className="flex items-center space-x-3 mb-3">
        {animal.photos && animal.photos.length > 0 ? (
          <img 
            src={resolvePhotoSrc(animal.photos[0], 'medium')}
            alt={animal.name}
            className="w-16 h-16 object-cover rounded-lg"
          />
        ) : (
          <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center text-2xl">
            {getAnimalIcon(animal.species)}
          </div>
        )}
        <div>
          <h4 className="font-semibold text-gray-800">{animal.name}</h4>
          <p className="text-sm text-gray-600">{animal.species} • {animal.breed || 'Unknown'}</p>
          {isPrimary && (
            <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-1 rounded-full mt-1 inline-block">
              {t('merge.primary_label')}
            </span>
          )}
        </div>
      </div>
      <div className="text-xs text-gray-600 space-y-1">
        <div><strong>{t('animals.age')}:</strong> {animal.age || t('forms.not_specified')}</div>
        <div><strong>{t('animals.health_status')}:</strong> {animal.health_status || t('forms.not_specified')}</div>
        {animal.description && (
          <div><strong>{t('animals.description')}:</strong> {animal.description.substring(0, 100)}...</div>
        )}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[2100] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg max-w-4xl w-full max-h-screen overflow-y-auto">
        <div className="p-6 border-b">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-bold text-gray-800">{t('merge.title')}</h3>
              <p className="text-sm text-gray-600 mt-1">{t('merge.subtitle')}</p>
            </div>
            <button
              onClick={onClose}
              disabled={loading}
              className="text-gray-500 hover:text-gray-700 text-2xl"
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Search Step */}
          {step === 'search' && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2">📝 {currentAnimal.name}</h4>
                <p className="text-sm text-blue-700">{currentAnimal.species} • {currentAnimal.breed}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('merge.select_animal')}
                </label>
                <p className="text-xs text-blue-600 mb-3">
                  📍 {t('merge.nearby_animals_1km')}
                </p>
              </div>

              {loadingAnimals && (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-2"></div>
                  <p className="text-gray-600">{t('merge.loading_animals')}</p>
                </div>
              )}

              {!loadingAnimals && searchResults.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-gray-600 mb-2">{t('merge.no_nearby_animals')}</p>
                  <p className="text-sm text-gray-500">{t('merge.no_nearby_hint')}</p>
                </div>
              )}

              {!loadingAnimals && searchResults.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm text-gray-600 mb-2">
                    {t('merge.found_animals')}: {searchResults.length}
                  </p>
                  {searchResults.map(animal => (
                    <button
                      key={animal.id}
                      onClick={() => handleSelectAnimal(animal)}
                      className="w-full border rounded-lg p-4 hover:bg-gray-50 text-left transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        {animal.photos && animal.photos.length > 0 ? (
                          <img 
                            src={resolvePhotoSrc(animal.photos[0], 'medium')}
                            alt={animal.name}
                            className="w-12 h-12 object-cover rounded-lg"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center text-xl">
                            {getAnimalIcon(animal.species)}
                          </div>
                        )}
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-800">{animal.name}</h4>
                          <p className="text-sm text-gray-600">
                            {animal.species} • {animal.breed || 'Unknown'} • {animal.age || 'Unknown age'}
                          </p>
                          {animal.distance !== undefined && (
                            <p className="text-xs text-blue-600 mt-1">
                              📍 {animal.distance.toFixed(2)} km away
                            </p>
                          )}
                        </div>
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Compare Step */}
          {step === 'compare' && selectedAnimal && (
            <div className="space-y-4">
              <h4 className="font-semibold text-gray-800 mb-4">{t('merge.comparison_title')}</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="primary"
                      checked={primaryAnimalId === currentAnimal.id}
                      onChange={() => setPrimaryAnimalId(currentAnimal.id)}
                      className="w-4 h-4"
                    />
                    <span className="font-medium">{t('merge.select_primary')}</span>
                  </label>
                  <AnimalPreviewCard animal={currentAnimal} isPrimary={primaryAnimalId === currentAnimal.id} />
                </div>

                <div>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="primary"
                      checked={primaryAnimalId === selectedAnimal.id}
                      onChange={() => setPrimaryAnimalId(selectedAnimal.id)}
                      className="w-4 h-4"
                    />
                    <span className="font-medium">{t('merge.select_primary')}</span>
                  </label>
                  <AnimalPreviewCard animal={selectedAnimal} isPrimary={primaryAnimalId === selectedAnimal.id} />
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">{t('merge.primary_description')}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('merge.reason_label')}
                </label>
                <textarea
                  value={mergeReason}
                  onChange={(e) => setMergeReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  rows="4"
                  placeholder={t('merge.reason_placeholder')}
                  required
                />
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="font-medium text-yellow-800 mb-2">{t('merge.merge_warning')}</p>
                <ul className="text-sm text-yellow-700 space-y-1">
                  <li>{t('merge.merge_warning_1')}</li>
                  <li>{t('merge.merge_warning_2')}</li>
                  <li>{t('merge.merge_warning_3')}</li>
                  <li>{t('merge.merge_warning_4')}</li>
                </ul>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setStep('search')}
                  disabled={loading}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  {t('common.back')}
                </button>
                <button
                  onClick={handleMerge}
                  disabled={loading || !mergeReason.trim()}
                  className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      {t('common.loading')}
                    </div>
                  ) : (
                    t('merge.confirm_button')
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


// Location Update Post Component
const LocationUpdatePost = ({ post, animal, onRollback }) => {
  const { t, i18n } = useTranslation();
  const [showRollbackModal, setShowRollbackModal] = useState(false);
  const [rolling, setRolling] = useState(false);

  const oldLoc = post.old_location;
  const newLoc = post.new_location;

  // Calculate distance moved
  const distanceMoved = oldLoc && newLoc 
    ? calculate_distance(oldLoc.lat, oldLoc.lng, newLoc.lat, newLoc.lng)
    : null;

  // Simple distance calculation function
  function calculate_distance(lat1, lng1, lat2, lng2) {
    const R = 6371; // Earth radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c;
    return distance < 1 ? (distance * 1000).toFixed(0) + ' m' : distance.toFixed(2) + ' km';
  }

  const handleRollback = async () => {
    setRolling(true);
    try {
      await axios.post(`${API}/posts/${post.id}/rollback-location`);
      alert('Location has been rolled back successfully');
      setShowRollbackModal(false);
      if (onRollback) onRollback();
    } catch (error) {
      console.error('Error rolling back location:', error);
      if (error.response?.data?.detail) {
        alert(`Error: ${error.response.data.detail}`);
      } else {
        alert('Failed to rollback location');
      }
    } finally {
      setRolling(false);
    }
  };

  const isRolledBack = post.status === 'rolled_back';
  const isRollbackPost = post.post_type === 'location_rollback';

  return (
    <div className="space-y-4">
      {/* Status Badge */}
      {isRolledBack && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-800">
            {t('spotted_here.status_rolled_back')}
          </p>
          {post.rollback_by_name && post.rollback_at && (
            <p className="text-xs text-red-600 mt-1">
              {t('spotted_here.rolled_back_by')} {post.rollback_by_name} • {new Date(post.rollback_at).toLocaleDateString(getLocaleCode(i18n.language))}
            </p>
          )}
        </div>
      )}

      {isRollbackPost && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <p className="text-sm text-yellow-800">
            ⚠️ {t('spotted_here.location_restored')}
          </p>
        </div>
      )}

      {/* Location Information */}
      {oldLoc && newLoc && (
        <div className="bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
            {/* Old Location */}
            <div className="bg-white rounded-lg p-3 border border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-1">
                📍 {t('spotted_here.moved_from')}
              </p>
              <p className="text-sm text-gray-700 font-medium">{oldLoc.address}</p>
              <p className="text-xs text-gray-500 mt-1">
                {oldLoc.lat.toFixed(6)}, {oldLoc.lng.toFixed(6)}
              </p>
            </div>

            {/* New Location */}
            <div className="bg-white rounded-lg p-3 border border-green-300">
              <p className="text-xs font-semibold text-green-700 uppercase mb-1">
                📍 {t('spotted_here.moved_to')}
              </p>
              <p className="text-sm text-gray-700 font-medium">{newLoc.address}</p>
              <p className="text-xs text-gray-500 mt-1">
                {newLoc.lat.toFixed(6)}, {newLoc.lng.toFixed(6)}
              </p>
            </div>
          </div>

          {/* Distance Info */}
          {distanceMoved && (
            <div className="bg-white rounded-lg p-2 border border-gray-200">
              <p className="text-xs text-gray-600">
                <strong>{t('spotted_here.distance_moved')}</strong> {distanceMoved}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Map visualization - Simple link to Google Maps showing both locations */}
      {oldLoc && newLoc && (
        <div className="bg-white border border-gray-200 rounded-lg p-3">
          <p className="text-xs font-semibold text-gray-600 uppercase mb-2">🗺️ View on Map</p>
          <div className="flex space-x-2">
            <a
              href={`https://www.google.com/maps/dir/${oldLoc.lat},${oldLoc.lng}/${newLoc.lat},${newLoc.lng}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 text-sm text-center font-medium transition-all"
            >
              View Route on Google Maps
            </a>
          </div>
        </div>
      )}

      {/* Reason */}
      {post.reason && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-xs font-semibold text-blue-800 uppercase mb-1">
            {t('spotted_here.reason')}
          </p>
          <p className="text-sm text-gray-700">{post.reason}</p>
        </div>
      )}

      {/* Rollback Button */}
      {!isRolledBack && !isRollbackPost && oldLoc && (
        <button
          onClick={() => setShowRollbackModal(true)}
          className="w-full bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 text-sm font-medium transition-all flex items-center justify-center space-x-2"
        >
          <span>⚠️</span>
          <span>{t('spotted_here.rollback_button')}</span>
        </button>
      )}

      {/* Rollback Confirmation Modal */}
      {showRollbackModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-3">
              {t('spotted_here.rollback_title')}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {t('spotted_here.rollback_message')}
            </p>
            <div className="flex space-x-3">
              <button
                onClick={handleRollback}
                disabled={rolling}
                className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {rolling ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {t('common.loading')}
                  </div>
                ) : (
                  t('spotted_here.rollback_confirm')
                )}
              </button>
              <button
                onClick={() => setShowRollbackModal(false)}
                disabled={rolling}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                {t('spotted_here.rollback_cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Recently Moved Animal Card Component
const RecentlyMovedAnimalCard = ({ data, onViewAnimal }) => {
  const { t, i18n } = useTranslation();
  const { animal, movements, total_movements, current_distance_from_search } = data;
  
  // Helper function to format distance
  const formatDistance = (distance) => {
    if (!distance) return 'N/A';
    return distance < 1000 
      ? `${Math.round(distance)} m` 
      : `${(distance / 1000).toFixed(1)} km`;
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const localeCode = getLocaleCode(i18n.language);
    return date.toLocaleDateString(localeCode, { month: 'short', day: 'numeric' });
  };

  return (
    <div className="bg-white border-2 border-orange-300 rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
      {/* Badge */}
      <div className="bg-gradient-to-r from-orange-400 to-orange-500 text-white px-4 py-2 text-sm font-semibold flex items-center justify-center">
        <span className="mr-2">🔄</span>
        <span>{t('animals.recently_moved')}</span>
      </div>
      
      <div 
        className="cursor-pointer"
        onClick={() => onViewAnimal(animal)}
      >
        {/* Animal Photo */}
        {animal.photos && animal.photos.length > 0 && (
          <img
            src={resolvePhotoSrc(animal.photos[0], 'medium')}
            alt={animal.name}
            className="w-full h-48 object-cover"
          />
        )}
        
        <div className="p-4">
          {/* Animal Name */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <span className="text-2xl">{getAnimalIcon(animal.species)}</span>
              <h3 className="font-semibold text-gray-800">{animal.name}</h3>
            </div>
          </div>

          <p className="text-gray-600 text-sm mb-3">
            {animal.species} {animal.breed && `• ${animal.breed}`}
          </p>

          {/* Movement History */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
            <p className="text-xs font-semibold text-blue-800 uppercase mb-2">
              {t('animals.movement_history')} ({total_movements} {t('animals.movements')})
            </p>
            
            <div className="space-y-2">
              {movements.slice(0, 3).map((movement, index) => (
                <div key={index} className="text-xs">
                  <div className="flex items-start">
                    <span className="text-orange-500 mr-1">→</span>
                    <div className="flex-1">
                      <p className="text-gray-700 font-medium">
                        {t('animals.moved_to')} {movement.to_location?.address || 'Unknown'}
                      </p>
                      <p className="text-gray-500 mt-0.5">
                        {t('animals.moved_at')} {formatDate(movement.moved_at)}
                        {movement.distance_moved && ` • ${formatDistance(movement.distance_moved)}`}
                      </p>
                      {movement.reason && (
                        <p className="text-gray-600 italic mt-0.5">"{movement.reason}"</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {total_movements > 3 && (
              <p className="text-xs text-blue-600 mt-2 font-medium">
                +{total_movements - 3} {t('animals.view_more_movements')}
              </p>
            )}
          </div>

          {/* Current Distance */}
          {current_distance_from_search && (
            <div className="text-xs text-gray-500 mt-2">
              <span>{t('animals.distance_from_here')}</span>
              <span className="font-medium text-orange-600 ml-1">
                {formatDistance(current_distance_from_search)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Animal Detail View Component (updated with edit functionality)
const AnimalDetailView = ({ animal, onBack, onPostSubmit, onAnimalUpdate, currentUser, onViewUser }) => {
  const { t, i18n } = useTranslation();
  const { getContentOpacity } = useBackground();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [newPost, setNewPost] = useState('');
  const [newComment, setNewComment] = useState('');
  const [selectedPostPhotos, setSelectedPostPhotos] = useState([]);
  const [commentPhotos, setCommentPhotos] = useState([]);
  const [showComments, setShowComments] = useState({});
  const [fullScreenImage, setFullScreenImage] = useState(null);
  const [fullScreenImages, setFullScreenImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  
  // NEW: Spotted Here Modal State
  const [showSpottedHereModal, setShowSpottedHereModal] = useState(false);
  
  // NEW: Merge Animal Modal State
  const [showMergeModal, setShowMergeModal] = useState(false);
  
  // NEW: Merged Animals State
  const [mergedAnimals, setMergedAnimals] = useState([]);
  const [loadingMerged, setLoadingMerged] = useState(false);
  
  // Handle view user - always use navigate for consistency
  const handleViewUserClick = (userId) => {
    if (!userId) return;
    navigate(`/user/${userId}`);
  };
  
  // NEW: Edit mode states
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    animal_type: '',
    species: '',
    breed: '',
    age: '',
    description: '',
    blood_type: '',
    health_status: '',
    tags: '',
    needs_help: false,
    help_description: '',
    location: null
  });
  const [editPhotos, setEditPhotos] = useState([]);

  const canEdit = currentUser && (currentUser.id === animal.created_by);
  
  const canUnmerge = currentUser && (
    currentUser.is_admin || // Admin สามารถ unmerge ได้ทุกอัน
    currentUser.id === animal.created_by || // เจ้าของโปรไฟล์หลัก
    mergedAnimals.some(m => m.created_by === currentUser.id) // เจ้าของโปรไฟล์ที่ถูก merge
  );
  
  // Debug log
  console.log('Merge Debug:', {
    canEdit,
    canUnmerge,
    isAdmin: currentUser?.is_admin,
    currentUser: currentUser?.id,
    animalCreatedBy: animal.created_by,
    mergedAnimalsCount: mergedAnimals.length
  });

  useEffect(() => {
    loadPosts();
    checkFollowStatus();
    initializeEditForm();
    loadMergedAnimals();
  }, [animal.id]);

  const loadMergedAnimals = async () => {
    setLoadingMerged(true);
    try {
      const response = await axios.get(`${API}/animals/${animal.id}/merged-animals`);
      setMergedAnimals(response.data);
    } catch (error) {
      console.error('Error loading merged animals:', error);
    } finally {
      setLoadingMerged(false);
    }
  };

  const initializeEditForm = () => {
    // Explicitly handle show_exact_location for old animals
    const showExactLocation = animal.show_exact_location !== undefined 
      ? animal.show_exact_location 
      : true; // Default to true for old animals
    
    setEditForm({
      name: animal.name || '',
      animal_type: animal.animal_type || 'owned',
      species: animal.species || 'dog',
      breed: animal.breed || '',
      age: animal.age || '',
      description: animal.description || '',
      blood_type: animal.blood_type || '',
      health_status: animal.health_status || '',
      tags: animal.tags ? animal.tags.join(', ') : '',
      needs_help: animal.needs_help || false,
      help_description: animal.help_description || '',
      location: animal.location || null,
      show_exact_location: showExactLocation
    });
    setEditPhotos(animal.photos || []);
    
    // Debug log
    console.log('Initialize Edit Form:', {
      animalName: animal.name,
      originalValue: animal.show_exact_location,
      setValue: showExactLocation,
      animalType: animal.animal_type
    });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Prepare tags array
      const tagsArray = editForm.tags.split(',').map(tag => tag.trim()).filter(tag => tag);
      
      const updateData = {
        ...editForm,
        tags: tagsArray,
        photos: editPhotos
      };
      
      // Debug log - Enhanced
      console.log('🔄 Updating animal with data:', {
        name: updateData.name,
        animal_type: updateData.animal_type,
        show_exact_location: updateData.show_exact_location,
        location: updateData.location,  // ← Added
        hasLocationField: 'location' in updateData,  // ← Added
        hasField: 'show_exact_location' in updateData
      });

      const response = await axios.put(`${API}/animals/${animal.id}`, updateData);
      
      console.log('✅ Update response:', response.data);  // ← Added
      
      setIsEditing(false);
      alert(t('alerts.animal_updated'));
      
      // Reload page to show updated data
      window.location.reload();
    } catch (error) {
      console.error('Error updating animal:', error);
      if (error.response?.data?.detail) {
        alert(`เกิดข้อผิดพลาด: ${error.response.data.detail}`);
      } else {
        alert(t('alerts.animal_update_error'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditPhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length === 0) return;
    
    const resizedImages = [];
    
    for (let i = 0; i < Math.min(files.length, 5 - editPhotos.length); i++) {
      const file = files[i];
      
      // ตรวจสอบt('admin.hero_type')ไฟล์
      if (!file.type.startsWith('image/')) {
        alert(t('alerts.please_select_image_only'));
        continue;
      }
      
      // ตรวจสอบขนาดไฟล์ (ก่อนย่อ)
      if (file.size > 10 * 1024 * 1024) { // 10MB
        alert(t('alerts.max_file_size'));
        continue;
      }
      
      try {
        // Resize image
        const resizedBase64 = await new Promise((resolve) => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const img = new Image();
          
          img.onload = () => {
            // คำนวณขนาดใหม่โดยคงอัตราส่วน
            const maxWidth = 800;
            const maxHeight = 600;
            let { width, height } = img;
            
            if (width > height) {
              if (width > maxWidth) {
                height = (height * maxWidth) / width;
                width = maxWidth;
              }
            } else {
              if (height > maxHeight) {
                width = (width * maxHeight) / height;
                height = maxHeight;
              }
            }
            
            // ตั้งค่า canvas
            canvas.width = width;
            canvas.height = height;
            
            // วาดรูปที่ย่อขนาดแล้ว
            ctx.drawImage(img, 0, 0, width, height);
            
            // แปลงเป็น base64 (เอาเฉพาะส่วนข้อมูล ไม่เอา data:image/jpeg;base64,)
            const base64 = canvas.toDataURL('image/jpeg', 0.8).split(',')[1];
            resolve(base64);
          };
          
          img.src = URL.createObjectURL(file);
        });
        
        resizedImages.push(resizedBase64);
      } catch (error) {
        console.error('Error resizing image:', error);
        alert(t('alerts.image_error'));
      }
    }
    
    // อัปเดต state ด้วยรูปที่ย่อแล้ว
    setEditPhotos(prev => [...prev, ...resizedImages].slice(0, 5));
    
    // เคลียร์ input file เพื่อให้สามารถเลือกไฟล์เดิมซ้ำได้
    e.target.value = '';
  };

  const removeEditPhoto = (index) => {
    setEditPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleLocationUpdate = async () => {
    if (!currentUser) {
      alert(t('alerts.login_to_report_sighting'));
      return;
    }

    // Open spotted here modal instead of directly updating
    setShowSpottedHereModal(true);
  };

  const handleUnmerge = async () => {
    if (!canUnmerge) {
      alert(t('merge.no_permission_unmerge'));
      return;
    }

    const confirmed = window.confirm(t('merge.unmerge_message'));
    if (!confirmed) return;

    try {
      await axios.post(`${API}/animals/${animal.id}/unmerge`);
      alert(t('merge.unmerge_success'));
      // Reload animal data or redirect
      window.location.reload();
    } catch (error) {
      console.error('Error unmerging animal:', error);
      if (error.response?.data?.detail) {
        alert(`Error: ${error.response.data.detail}`);
      } else {
        alert(t('merge.unmerge_error'));
      }
    }
  };

  const handleUnmergeSpecific = async (secondaryId, secondaryName) => {
    if (!canUnmerge) {
      alert(t('merge.no_permission_unmerge'));
      return;
    }

    const confirmed = window.confirm(`${t('merge.unmerge_message')}\n\nAnimal: ${secondaryName}`);
    if (!confirmed) return;

    try {
      await axios.post(`${API}/animals/${animal.id}/unmerge/${secondaryId}`);
      alert(t('merge.unmerge_success'));
      // Reload merged animals list
      loadMergedAnimals();
      if (onAnimalUpdate) {
        onAnimalUpdate();
      }
    } catch (error) {
      console.error('Error unmerging animal:', error);
      if (error.response?.data?.detail) {
        alert(`Error: ${error.response.data.detail}`);
      } else {
        alert(t('merge.unmerge_error'));
      }
    }
  };

  const loadPosts = async () => {
    try {
      const response = await axios.get(`${API}/posts?animal_id=${animal.id}`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error loading posts:', error);
    }
  };

  const checkFollowStatus = async () => {
    if (currentUser && animal.followers) {
      setIsFollowing(animal.followers.includes(currentUser.id));
    }
  };

  const handleFollow = async () => {
    if (!currentUser) {
      alert(t('alerts.login_to_follow'));
      return;
    }

    try {
      if (isFollowing) {
        await axios.delete(`${API}/animals/${animal.id}/follow`);
        setIsFollowing(false);
      } else {
        await axios.post(`${API}/animals/${animal.id}/follow`);
        setIsFollowing(true);
      }
    } catch (error) {
      console.error('Error toggling follow:', error);
    }
  };

  const handleSpottedHere = async () => {
    if (!currentUser) {
      alert(t('alerts.login_to_report_sighting'));
      return;
    }

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`
            );
            const data = await response.json();
            const address = data.display_name || `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
            
            await axios.put(`${API}/animals/${animal.id}/location`, {
              lat, lng, address
            });
            
            alert(t('alerts.location_updated'));
            loadPosts(); // Reload to show new location update post
          } catch (error) {
            console.error('Error updating location:', error);
            alert(t('alerts.location_update_error'));
          }
        },
        (error) => {
          console.error('Error getting location:', error);
          alert(t('alerts.cannot_get_location'));
        }
      );
    }
  };

  const handlePostPhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length === 0) return;
    
    // Limit total photos to 5
    if (selectedPostPhotos.length + files.length > 5) {
      alert(t('alerts.max_photos'));
      return;
    }
    
    const resizedImages = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // ตรวจสอบt('admin.hero_type')ไฟล์
      if (!file.type.startsWith('image/')) {
        alert(t('alerts.please_select_image_only'));
        continue;
      }
      
      // ตรวจสอบขนาดไฟล์ (ก่อนย่อ)
      if (file.size > 10 * 1024 * 1024) { // 10MB
        alert(t('alerts.photo_too_large', { name: file.name }));
        continue;
      }
      
      try {
        // Resize image
        const resizedBase64 = await new Promise((resolve) => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const img = new Image();
          
          img.onload = () => {
            // คำนวณขนาดใหม่โดยคงอัตราส่วน
            const maxWidth = 800;
            const maxHeight = 600;
            let { width, height } = img;
            
            if (width > height) {
              if (width > maxWidth) {
                height = (height * maxWidth) / width;
                width = maxWidth;
              }
            } else {
              if (height > maxHeight) {
                width = (width * maxHeight) / height;
                height = maxHeight;
              }
            }
            
            // ตั้งค่า canvas
            canvas.width = width;
            canvas.height = height;
            
            // วาดรูปที่ย่อขนาดแล้ว
            ctx.drawImage(img, 0, 0, width, height);
            
            // แปลงเป็น base64 (เอาเฉพาะส่วนข้อมูล ไม่เอา data:image/jpeg;base64,)
            const base64 = canvas.toDataURL('image/jpeg', 0.8).split(',')[1];
            resolve(base64);
          };
          
          img.src = URL.createObjectURL(file);
        });
        
        resizedImages.push(resizedBase64);
      } catch (error) {
        console.error('Error resizing image:', error);
        alert(`${t('alerts.image_error_with_name')} ${file.name}`);
      }
    }
    
    // อัปเดต state ด้วยรูปที่ย่อแล้ว
    setSelectedPostPhotos(prev => [...prev, ...resizedImages].slice(0, 5));
    
    // เคลียร์ input file เพื่อให้สามารถเลือกไฟล์เดิมซ้ำได้
    e.target.value = '';
  };

  const removePostPhoto = (index) => {
    setSelectedPostPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleCommentPhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length === 0) return;
    
    const resizedImages = [];
    
    for (let i = 0; i < Math.min(files.length, 5 - commentPhotos.length); i++) {
      const file = files[i];
      
      // ตรวจสอบt('admin.hero_type')ไฟล์
      if (!file.type.startsWith('image/')) {
        alert(t('alerts.please_select_image_only'));
        continue;
      }
      
      // ตรวจสอบขนาดไฟล์ (ก่อนย่อ)
      if (file.size > 10 * 1024 * 1024) { // 10MB
        alert(t('alerts.max_file_size'));
        continue;
      }
      
      try {
        // Resize image
        const resizedBase64 = await new Promise((resolve) => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const img = new Image();
          
          img.onload = () => {
            // คำนวณขนาดใหม่โดยคงอัตราส่วน
            const maxWidth = 800;
            const maxHeight = 600;
            let { width, height } = img;
            
            if (width > height) {
              if (width > maxWidth) {
                height = (height * maxWidth) / width;
                width = maxWidth;
              }
            } else {
              if (height > maxHeight) {
                width = (width * maxHeight) / height;
                height = maxHeight;
              }
            }
            
            // ตั้งค่า canvas
            canvas.width = width;
            canvas.height = height;
            
            // วาดรูปที่ย่อขนาดแล้ว
            ctx.drawImage(img, 0, 0, width, height);
            
            // แปลงเป็น base64 (เอาเฉพาะส่วนข้อมูล ไม่เอา data:image/jpeg;base64,)
            const base64 = canvas.toDataURL('image/jpeg', 0.8).split(',')[1];
            resolve(base64);
          };
          
          img.src = URL.createObjectURL(file);
        });
        
        resizedImages.push(resizedBase64);
      } catch (error) {
        console.error('Error resizing image:', error);
        alert(t('alerts.image_error'));
      }
    }
    
    // อัปเดต state ด้วยรูปที่ย่อแล้ว
    setCommentPhotos(prev => [...prev, ...resizedImages].slice(0, 5));
    
    // เคลียร์ input file เพื่อให้สามารถเลือกไฟล์เดิมซ้ำได้
    e.target.value = '';
  };

  const removeCommentPhoto = (index) => {
    setCommentPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmitPost = async (e) => {
    e.preventDefault();
    if (!currentUser) {
      alert(t('alerts.login_to_post'));
      return;
    }

    if (!newPost.trim() && selectedPostPhotos.length === 0) {
      alert(t('alerts.write_message_or_select_image'));
      return;
    }

    // Check payload size before sending
    const payload = {
      animal_id: animal.id,
      content: newPost,
      photos: selectedPostPhotos
    };
    
    const payloadSize = JSON.stringify(payload).length;
    if (payloadSize > 50 * 1024 * 1024) { // 50MB limit
      alert(t('alerts.data_too_large'));
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/posts`, payload, {
        timeout: 60000, // 60 second timeout for large uploads
        maxContentLength: Infinity,
        maxBodyLength: Infinity
      });

      setNewPost('');
      setSelectedPostPhotos([]);
      loadPosts();
      if (onPostSubmit) onPostSubmit();
    } catch (error) {
      console.error('Error creating post:', error);
      
      if (error.code === 'ECONNABORTED') {
        alert(t('alerts.upload_timeout'));
      } else if (error.response?.status === 413) {
        alert(t('alerts.data_too_large'));
      } else if (error.response?.status === 400) {
        alert(t('alerts.invalid_data'));
      } else {
        alert(t('alerts.post_error'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitComment = async (e, postId) => {
    e.preventDefault();
    if (!currentUser) {
      alert(t('alerts.login_to_comment'));
      return;
    }

    if (!newComment.trim() && commentPhotos.length === 0) {
      alert(t('alerts.write_comment_or_select_image'));
      return;
    }

    try {
      await axios.post(`${API}/posts/${postId}/comment-with-photos`, {
        content: newComment,
        photos: commentPhotos
      });

      setNewComment('');
      setCommentPhotos([]);
      loadPosts();
    } catch (error) {
      console.error('Error creating comment:', error);
      alert(t('alerts.comment_error'));
    }
  };

  const handleLike = async (postId) => {
    if (!currentUser) {
      alert(t('alerts.login_to_like'));
      return;
    }

    try {
      await axios.post(`${API}/posts/${postId}/like`);
      loadPosts();
    } catch (error) {
      console.error('Error liking post:', error);
    }
  };

  const toggleComments = (postId) => {
    setShowComments(prev => ({
      ...prev,
      [postId]: !prev[postId]
    }));
  };

  const openFullScreen = (images, index = 0) => {
    setFullScreenImages(images);
    setCurrentImageIndex(index);
    setFullScreenImage(images[index]);
  };

  const closeFullScreen = () => {
    setFullScreenImage(null);
    setFullScreenImages([]);
    setCurrentImageIndex(0);
  };

  const navigateImage = (direction) => {
    const newIndex = direction === 'next' 
      ? (currentImageIndex + 1) % fullScreenImages.length
      : currentImageIndex === 0 ? fullScreenImages.length - 1 : currentImageIndex - 1;
    
    setCurrentImageIndex(newIndex);
    setFullScreenImage(fullScreenImages[newIndex]);
  };

  const PhotoGrid = ({ photos, onPhotoClick }) => {
    if (photos.length === 0) return null;

    const gridClasses = {
      1: "grid-cols-1",
      2: "grid-cols-2", 
      3: "grid-cols-3",
      4: "grid-cols-2"
    };

    const getGridClass = () => {
      if (photos.length <= 4) return gridClasses[photos.length];
      return "grid-cols-3";
    };

    return (
      <div className={`grid ${getGridClass()} gap-2 mt-3`}>
        {photos.slice(0, 5).map((photo, index) => (
          <div 
            key={index} 
            className={`relative ${photos.length === 3 && index === 0 ? 'col-span-3' : ''} ${photos.length > 4 && index === 4 ? 'relative' : ''}`}
            onClick={() => onPhotoClick(photos, index)}
          >
            <img
              src={resolvePhotoSrc(photo, 'medium')}
              alt={`Photo ${index + 1}`}
              className="w-full h-48 object-cover rounded-md cursor-pointer hover:opacity-90 transition-opacity"
            />
            {photos.length > 5 && index === 4 && (
              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-md cursor-pointer">
                <span className="text-white text-2xl font-bold">+{photos.length - 5}</span>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  // ฟังก์ชันคำนวณระยะทาง (Haversine formula)
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // รัศมีโลกในหน่วยกิโลเมตร
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c; // ระยะทางในกิโลเมตร
    return distance;
  };

  // ฟังก์ชันแปลงระยะทางเป็นเวลาเดินทาง
  const getTravelTime = (distanceKm) => {
    if (distanceKm < 0.001) return null; // ถ้าระยะทางน้อยมาก

    // ความเร็วเฉลี่ย (กม./ชม.)
    const walkingSpeed = 5;    // เดิน 5 กม./ชม.
    const cyclingSpeed = 15;   // จักรยาน 15 กม./ชม.
    const drivingSpeed = 30;   // ขับรถในเมือง 30 กม./ชม.

    const walkingTime = (distanceKm / walkingSpeed) * 60; // นาที
    const cyclingTime = (distanceKm / cyclingSpeed) * 60; // นาที
    const drivingTime = (distanceKm / drivingSpeed) * 60; // นาที

    return {
      distance: distanceKm,
      walking: Math.round(walkingTime),
      cycling: Math.round(cyclingTime),
      driving: Math.round(drivingTime)
    };
  };

  // Format travel time
  const formatTravelTime = (minutes) => {
    if (minutes < 1) return `< 1 ${t('common.minute')}`;
    if (minutes < 60) return `${minutes} ${t('common.minutes_plural')}`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins === 0) return `${hours} ${t('common.hours_plural')}`;
    return `${hours} ${t('common.hours_plural')} ${mins} ${t('common.minutes_plural')}`;
  };

  // คำนวณระยะทางจากตำแหน่งผู้ใช้
  const getUserTravelInfo = () => {
    if (!currentUser?.location || !animal.location) return null;
    
    const userLat = currentUser.location.lat;
    const userLng = currentUser.location.lng;
    const animalLat = animal.location.lat;
    const animalLng = animal.location.lng;

    const distance = calculateDistance(userLat, userLng, animalLat, animalLng);
    return getTravelTime(distance);
  };

  const travelInfo = getUserTravelInfo();
  
  // Get dynamic opacity
  const opacity = getContentOpacity();
  const cardClass = `backdrop-blur-md rounded-lg shadow-md`;
  const cardStyle = { backgroundColor: `rgba(255, 255, 255, ${opacity})` };

  return (
    <div className="space-y-6 rounded-t-lg pb-20">
      {/* Animal Profile */}
      <div className={`${cardClass} overflow-hidden`} style={cardStyle}>
        {isEditing ? (
          /* Edit Form */
          <div className="p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              ✏️ {t('common.edit_animal_info')}: <span className="text-blue-600">{animal.name}</span>
            </h2>
            
            <form onSubmit={handleEditSubmit} className="space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms.animal_name_required')}</label>
                  <input
                    type="text"
                    required
                    value={editForm.name}
                    onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('animals.animal_type')} *</label>
                  <select
                    value={editForm.animal_type}
                    onChange={(e) => setEditForm(prev => ({ ...prev, animal_type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="owned">{t('animals.my_pet')}</option>
                    <option value="stray">{t('animals.stray')}</option>
                    <option value="social_pet">{t('animals.social_pet')}</option>
                    <option value="wild">{t('profile.pet_type_wild')}</option>
                  </select>
                </div>
                
                {/* Privacy Setting for Owned Pets */}
                {editForm.animal_type === 'owned' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-800 mb-2">🔒 {t('profile.privacy_title')}</h4>
                    <label className="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editForm.show_exact_location === true}
                        onChange={(e) => {
                          const newValue = e.target.checked;
                          console.log('Privacy checkbox changed:', {
                            oldValue: editForm.show_exact_location,
                            newValue: newValue
                          });
                          setEditForm(prev => ({ 
                            ...prev, 
                            show_exact_location: newValue
                          }));
                        }}
                        className="mt-1 w-5 h-5 text-blue-600 rounded"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-800">
                          {t('profile.show_exact_location')}
                        </span>
                        <p className="text-xs text-gray-600 mt-1">
                          {t('profile.show_exact_location_help')}
                        </p>
                        {/* Debug display */}
                        <p className="text-xs text-orange-600 mt-1">
                          Current: {String(editForm.show_exact_location)}
                        </p>
                      </div>
                    </label>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('animals.species')} *</label>
                  <select
                    value={editForm.species}
                    onChange={(e) => setEditForm(prev => ({ ...prev, species: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="dog">{t('animals.dog')}</option>
                    <option value="cat">{t('animals.cat')}</option>
                    <option value="bird">{t('animals.bird')}</option>
                    <option value="rabbit">{t('animals.rabbit')}</option>
                    <option value="hamster">{t('animals.hamster')}</option>
                    <option value="fish">{t('animals.fish')}</option>
                    <option value="turtle">{t('animals.turtle')}</option>
                    <option value="other">{t('animals.other')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms.breed_optional')}</label>
                  <input
                    type="text"
                    value={editForm.breed}
                    onChange={(e) => setEditForm(prev => ({ ...prev, breed: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder={t('forms.breed_placeholder')}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('animals.description')} *</label>
                <textarea
                  required
                  rows="3"
                  value={editForm.description}
                  onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('forms.description_placeholder')}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms.age_optional')}</label>
                  <input
                    type="text"
                    value={editForm.age}
                    onChange={(e) => setEditForm(prev => ({ ...prev, age: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder={t('forms.age_placeholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms.blood_type_optional')}</label>
                  <select
                    value={editForm.blood_type}
                    onChange={(e) => setEditForm(prev => ({ ...prev, blood_type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">{t('forms.not_specified')}</option>
                    <option value="DEA 1.1+">DEA 1.1+ ({t('forms.dog_blood')})</option>
                    <option value="DEA 1.1-">DEA 1.1- ({t('forms.dog_blood')})</option>
                    <option value="DEA 3+">DEA 3+ ({t('forms.dog_blood')})</option>
                    <option value="DEA 3-">DEA 3- ({t('forms.dog_blood')})</option>
                    <option value="DEA 4+">DEA 4+ ({t('forms.dog_blood')})</option>
                    <option value="DEA 4-">DEA 4- ({t('forms.dog_blood')})</option>
                    <option value="A">A ({t('forms.cat_blood')})</option>
                    <option value="B">B ({t('forms.cat_blood')})</option>
                    <option value="AB">AB ({t('forms.cat_blood')})</option>
                    <option value="Universal Donor">{t('forms.universal_donor')}</option>
                    <option value="Universal Recipient">{t('forms.universal_recipient')}</option>
                    <option value="Other">{t('forms.other')}</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms.health_status_optional')}</label>
                  <input
                    type="text"
                    value={editForm.health_status}
                    onChange={(e) => setEditForm(prev => ({ ...prev, health_status: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder={t('forms.health_status_placeholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms.tags')}</label>
                  <input
                    type="text"
                    value={editForm.tags}
                    onChange={(e) => setEditForm(prev => ({ ...prev, tags: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder={t('forms.tags_placeholder')}
                  />
                </div>
              </div>

              {/* Photos */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('forms.upload_photos')}</label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleEditPhotoUpload}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
                
                {editPhotos.length > 0 && (
                  <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                    {editPhotos.map((photo, index) => (
                      <div key={index} className="relative">
                        <img
                          src={resolvePhotoSrc(photo, 'medium')}
                          alt={`Photo ${index + 1}`}
                          className="w-full h-32 object-cover rounded-md"
                        />
                        <button
                          type="button"
                          onClick={() => removeEditPhoto(index)}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Location */}
              <LocationSelector
                onLocationSelect={(location) => setEditForm(prev => ({ ...prev, location }))}
                selectedLocation={editForm.location}
                showExactLocation={editForm.show_exact_location}
                className="mt-4"
              />

              {/* Help Request */}
              <div className="border-t pt-4">
                <div className="flex items-center space-x-2 mb-3">
                  <input
                    type="checkbox"
                    id="edit_needs_help"
                    checked={editForm.needs_help}
                    onChange={(e) => setEditForm(prev => ({ ...prev, needs_help: e.target.checked }))}
                    className="rounded border-gray-300 focus:ring-blue-500"
                  />
                  <label htmlFor="edit_needs_help" className="text-sm font-medium text-gray-700">
                    {t('forms.needs_help')}
                  </label>
                </div>
                
                {editForm.needs_help && (
                  <textarea
                    rows="3"
                    value={editForm.help_description}
                    onChange={(e) => setEditForm(prev => ({ ...prev, help_description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500"
                    placeholder={t('forms.help_description_placeholder')}
                  />
                )}
              </div>

              {/* Submit Buttons */}
              <div className="flex space-x-4 pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? t('alerts.saving') : `💾 ${t('common.save_changes')}`}
                </button>
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="bg-gray-300 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-400"
                >{t('common.cancel')}</button>
              </div>
            </form>
          </div>
        ) : (
          /* Display Mode */
          <>
            {/* Animal Photos */}
            {animal.photos && animal.photos.length > 0 && (
              <PhotoGrid 
                photos={animal.photos} 
                onPhotoClick={(photos, index) => openFullScreen(photos, index)}
              />
            )}

            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <span className="text-4xl">{getAnimalIcon(animal.species)}</span>
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">{animal.name}</h1>
                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                      animal.animal_type === 'owned' ? 'bg-green-100 text-green-800' :
                            animal.animal_type === 'stray' ? 'bg-orange-100 text-orange-800' :
                            animal.animal_type === 'wild' ? 'bg-amber-100 text-amber-800' :
                            'bg-teal-100 text-teal-800'
                    }`}>
                      {animal.animal_type === 'owned' ? t('animals.pet') :
                             animal.animal_type === 'stray' ? t('animals.stray') :
                             animal.animal_type === 'wild' ? t('profile.pet_type_wild') :
                             t('animals.social_pet')}
                    </span>
                  </div>
                </div>
                
                <div className="flex space-x-3">
                  {canEdit && (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="bg-blue-600 text-white px-4 py-2 rounded-xl hover:bg-blue-700 text-sm"
                    >
                     <span >✏️</span>
                    <span className="hidden xs:inline ">{t('common.edit')}</span>
                    </button>
                  )}
                  {currentUser && (
                    <>
                      <button
                        onClick={() => setShowMergeModal(true)}
                        className="bg-orange-600 text-white px-4 py-2 rounded-xl hover:bg-orange-700 text-sm flex items-center space-x-1"
                      >
                        <span>🔗</span>
                        <span className="hidden xs:inline ">{t('merge.merge_button')}</span>
                      </button>
                      <button
                        onClick={() => {
                          // Scroll to the create post section
                          const createPostSection = document.getElementById('create-post-section');
                          if (createPostSection) {
                            createPostSection.scrollIntoView({ 
                              behavior: 'smooth', 
                              block: 'start' 
                            });
                            // Focus on the textarea after scrolling
                            setTimeout(() => {
                              const textarea = createPostSection.querySelector('textarea');
                              if (textarea) {
                                textarea.focus();
                              }
                            }, 500);
                          }
                        }}
                        className="bg-gradient-to-r from-pink-500 to-orange-600 text-white px-4 py-2 rounded-xl hover:from-pink-600 hover:to-orange-700 text-sm flex items-center space-x-1 shadow-lg"
                      >
                        <span>✨</span>
                        <span className="hidden xs:inline font-semibold">Post Update</span>
                      </button>
                    </>
                  )}
                  <button
                    onClick={handleLocationUpdate}
                    className="bg-white text-blue-600 border-2 border-blue-600 px-4 py-2 rounded-xl hover:bg-blue-50 flex items-center space-x-2 shadow-md font-medium transition-all"
                  >
                    <img src="/location-pin.png" alt="Location" className="w-5 h-5" />
                    <span className="hidden xs:inline font-semibold">Spotted Here</span>
                  </button>
                  <button
                    onClick={handleFollow}
                    className={`rounded-xl text-sm font-medium flex items-center ${
                      isFollowing 
                        ? 'bg-gray-200 text-gray-800 hover:bg-gray-300' 
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    <span className="inline">{isFollowing ? '✓' : '+'}</span>
                    <span className="hidden xs:inline ">{isFollowing ? t('animals.following') : t('animals.follow')}</span>
                  </button>
                </div>
              </div>

              {/* Merged Status Banner */}
              {animal.merged_into && (
                <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4 mb-4">
                  <p className="font-semibold text-yellow-800 mb-2">{t('merge.merged_status')}</p>
                  <p className="text-sm text-yellow-700 mb-2">
                    <strong>{t('merge.merged_into')}</strong> 
                    <button 
                      onClick={() => navigate(`/animal/${animal.merged_into}`)}
                      className="text-blue-600 hover:underline ml-1"
                    >
                      {t('merge.view_primary')}
                    </button>
                  </p>
                  {animal.merge_reason && (
                    <p className="text-sm text-yellow-700 mb-2">
                      <strong>{t('merge.merge_reason')}</strong> {animal.merge_reason}
                    </p>
                  )}
                  {animal.merged_by_name && (
                    <p className="text-sm text-yellow-700 mb-2">
                      <strong>👤 {t('merge.merged_by')}</strong> {animal.merged_by_name}
                      {animal.merged_at && (
                        <span className="text-xs ml-2">
                          ({new Date(animal.merged_at).toLocaleDateString(getLocaleCode(i18n.language))})
                        </span>
                      )}
                    </p>
                  )}
                  {canUnmerge && (
                    <button
                      onClick={handleUnmerge}
                      className="bg-yellow-600 text-white px-4 py-2 rounded-md hover:bg-yellow-700 text-sm"
                    >
                      {t('merge.unmerge_button')}
                    </button>
                  )}
                </div>
              )}

              <p className="text-gray-700 mb-4">{animal.description}</p>

              <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 mb-4">
                <div><strong>{t('animals.species')}:</strong> {animal.species}</div>
                {animal.breed && <div><strong>{t('animals.breed')}:</strong> {animal.breed}</div>}
                {animal.age && (
                  <div><strong>{t('animals.age')}:</strong> {animal.age}</div>
                )}
                {animal.blood_type && (
                  <div><strong>🩸 {t('animals.blood_type')}</strong> {animal.blood_type}</div>
                )}
                {animal.health_status && (
                  <div><strong>{t('animals.health_status')}:</strong> {animal.health_status}</div>
                )}
                <div><strong>👥 {t('common.followers')}:</strong> {animal.followers?.length || 0}</div>
                {animal.created_by_name && (
                  <div><strong>🐾 {t('common.posted_by')}</strong> 
                    <button 
                      onClick={() => handleViewUserClick(animal.created_by)}
                      className="text-blue-600 hover:underline ml-1"
                    >
                      {animal.created_by_name}
                    </button>
                  </div>
                )}
                {animal.last_spotted_at && (
                  <div><strong>📍 {t('animals.last_spotted')}</strong> {new Date(animal.last_spotted_at).toLocaleDateString(getLocaleCode(i18n.language))}</div>
                )}
                {animal.last_spotted_by_name && (
                  <div><strong>🔍 {t('animals.spotted_by')}</strong> 
                    <button 
                      onClick={() => handleViewUserClick(animal.last_spotted_by)}
                      className="text-blue-600 hover:underline ml-1"
                    >
                      {animal.last_spotted_by_name}
                    </button>
                  </div>
                )}
              </div>

              {animal.tags && animal.tags.length > 0 && (
                <div className="mb-4">
                  <div className="flex flex-wrap gap-2">
                    {animal.tags.map((tag, index) => (
                      <span key={index} className="bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-sm">
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {animal.needs_help && animal.help_description && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
                  <h3 className="font-semibold text-red-800 mb-2">🆘 {t('animals.needs_help')}</h3>
                  <p className="text-red-700">{animal.help_description}</p>
                </div>
              )}

              {animal.location && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <img src="/location-pin.png" alt="Location" className="w-5 h-5" />
                    <h3 className="font-semibold text-blue-800">{t('animals.location_title')}</h3>
                  </div>
                  <p className="text-blue-700 mb-3">
                    {animal.show_exact_location === false 
                      ? t('forms.not_specified')
                      : animal.location.address
                    }
                  </p>
                  
                  {/* แผนที่ OpenStreetMap */}
                  <div className="mb-4 rounded-md overflow-hidden border border-blue-300">
                    {/* Privacy Notice */}
                    {animal.show_exact_location === false && (
                      <div className="bg-blue-50 border-b border-blue-200 px-3 py-2">
                        <div className="flex items-center space-x-2 text-blue-700 text-sm">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                          <span className="font-medium">📍 {t('profile.approximate_location_notice')}</span>
                        </div>
                        <p className="text-xs text-blue-600 mt-1 ml-6">
                          {t('profile.approximate_location_description')}
                        </p>
                      </div>
                    )}
                    
                    <div 
                      id={`map-${animal.id}`}
                      className="w-full h-64 relative"
                    >
                      {/* Check if location should be approximate */}
                      {animal.show_exact_location === false ? (
                        // Show approximate area with circle overlay on real map
                        <PrivacyMapView 
                          actualLat={animal.location.lat}
                          actualLng={animal.location.lng}
                          animalName={animal.name}
                        />
                      ) : (
                        // Show exact location with map
                        <iframe
                          src={`https://www.openstreetmap.org/export/embed.html?bbox=${animal.location.lng-0.01},${animal.location.lat-0.01},${animal.location.lng+0.01},${animal.location.lat+0.01}&layer=mapnik&marker=${animal.location.lat},${animal.location.lng}`}
                          className="w-full h-full border-0"
                          allowFullScreen
                          loading="lazy"
                          title={`Location ${animal.name}`}
                        />
                      )}
                    </div>
                  </div>
                  
                  {/* แสดงระยะทางและเวลาเดินทาง */}
                  {travelInfo && animal.show_exact_location !== false && (
                    <div className="bg-white rounded-md p-3 mb-3 border border-blue-100">
                      <h4 className="font-medium text-blue-800 mb-2">🚶‍♂️ {t('common.travel_from_location')}</h4>
                      <div className="text-sm space-y-1">
                        <div className="flex justify-between">
                          <span className="text-gray-600">{t('common.distance')}</span>
                          <span className="font-medium text-blue-700">
                            {travelInfo.distance < 1 
                              ? `${Math.round(travelInfo.distance * 1000)} ${t('common.meters')}` 
                              : `${travelInfo.distance.toFixed(1)} ${t('common.km')}`}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">🚶‍♂️ {t('common.walk')}</span>
                          <span className="font-medium text-green-600">{formatTravelTime(travelInfo.walking)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">🚴‍♂️ {t('common.bicycle')}</span>
                          <span className="font-medium text-orange-600">{formatTravelTime(travelInfo.cycling)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">🚗 {t('common.drive')}</span>
                          <span className="font-medium text-orange-600">{formatTravelTime(travelInfo.driving)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {!currentUser?.location && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-3">
                      <p className="text-yellow-700 text-sm">
                        💡 <strong>{t('common.tip')}:</strong> {t('common.location_tip_travel')}
                      </p>
                    </div>
                  )}
                  
                  <div className="flex space-x-2">
                    {animal.show_exact_location !== false && (
                      <>
                        <a
                          href={`https://www.google.com/maps?q=${animal.location.lat},${animal.location.lng}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-blue-600 text-white px-4 py-2 rounded-xl hover:bg-blue-700 text-sm flex-1 text-center font-medium transition-all flex items-center justify-center space-x-2"
                        >
                          <img src="/maps-icon.png" alt="Google Maps" className="w-5 h-5" />
                          <span>Google Maps</span>
                        </a>
                        <a
                          href={`https://www.openstreetmap.org/?mlat=${animal.location.lat}&mlon=${animal.location.lng}&zoom=16`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-green-600 text-white px-4 py-2 rounded-xl hover:bg-green-700 text-sm flex-1 text-center font-medium transition-all flex items-center justify-center space-x-2"
                        >
                          <img src="/maps-icon.png" alt="OSM" className="w-5 h-5" />
                          <span>OpenStreetMap</span>
                        </a>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Merged Animals Section */}
      {mergedAnimals.length > 0 && (
        <div className={cardClass} style={cardStyle}>
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <span className="mr-2">🔗</span>
              {animal.merged_into ? t('merge.related_profiles_title') : t('merge.merged_animals_title')} ({mergedAnimals.length})
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {animal.merged_into ? t('merge.related_profiles_description') : t('merge.merged_animals_description')}
            </p>
            
            <div className="space-y-4">
              {mergedAnimals.map((mergedAnimal) => (
                <div 
                  key={mergedAnimal.id}
                  className={`border-2 rounded-lg p-4 ${
                    mergedAnimal.is_primary ? 'border-green-300 bg-green-50' : 'border-blue-200 bg-blue-50'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <span className="text-3xl">{getAnimalIcon(mergedAnimal.species)}</span>
                      <div>
                        <div className="flex items-center space-x-2">
                          <h4 className="font-bold text-gray-800 text-lg">{mergedAnimal.name}</h4>
                          {mergedAnimal.is_primary && (
                            <span className="bg-green-600 text-white text-xs px-2 py-1 rounded-full font-medium">
                              {t('merge.primary_profile')}
                            </span>
                          )}
                        </div>
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                          mergedAnimal.animal_type === 'owned' ? 'bg-green-100 text-green-800' :
                          mergedAnimal.animal_type === 'stray' ? 'bg-orange-100 text-orange-800' :
                          'bg-teal-100 text-teal-800'
                        }`}>
                          {mergedAnimal.animal_type === 'owned' ? t('animals.pet') :
                           mergedAnimal.animal_type === 'stray' ? t('animals.stray') : t('animals.social_pet')}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex space-x-2">
                      <button
                        onClick={() => navigate(`/animal/${mergedAnimal.id}`)}
                        className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
                      >
                        👁️ {t('merge.view_button')}
                      </button>
                      
                      {canUnmerge && !mergedAnimal.is_primary && (
                        <button
                          onClick={() => handleUnmergeSpecific(mergedAnimal.id, mergedAnimal.name)}
                          className="px-3 py-1 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 text-sm font-medium"
                        >
                          ↩️ {t('merge.unmerge_specific')}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Photos */}
                  {mergedAnimal.photos && mergedAnimal.photos.length > 0 && (
                    <div className="mb-3">
                      <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                        {mergedAnimal.photos.slice(0, 5).map((photo, idx) => (
                          <img 
                            key={idx}
                            src={resolvePhotoSrc(photo, 'medium')}
                            alt={`${mergedAnimal.name} ${idx + 1}`}
                            className="w-full h-20 object-cover rounded-md cursor-pointer hover:opacity-80"
                            onClick={() => openFullScreen(mergedAnimal.photos, idx)}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Description */}
                  {mergedAnimal.description && (
                    <p className="text-gray-700 mb-3 text-sm">{mergedAnimal.description}</p>
                  )}

                  {/* Details Grid */}
                  <div className="grid grid-cols-2 gap-3 text-sm text-gray-600 mb-3">
                    <div><strong>{t('animals.species')}:</strong> {mergedAnimal.species}</div>
                    {mergedAnimal.breed && <div><strong>{t('animals.breed')}:</strong> {mergedAnimal.breed}</div>}
                    {mergedAnimal.age && <div><strong>{t('animals.age')}:</strong> {mergedAnimal.age}</div>}
                    {mergedAnimal.blood_type && <div><strong>🩸 {t('animals.blood_type')}</strong> {mergedAnimal.blood_type}</div>}
                    {mergedAnimal.health_status && <div><strong>{t('animals.health_status')}:</strong> {mergedAnimal.health_status}</div>}
                    {mergedAnimal.created_by_name && (
                      <div><strong>🐾 {t('common.posted_by')}</strong> {mergedAnimal.created_by_name}</div>
                    )}
                  </div>

                  {/* Tags */}
                  {mergedAnimal.tags && mergedAnimal.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {mergedAnimal.tags.map((tag, idx) => (
                        <span key={idx} className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Needs Help */}
                  {mergedAnimal.needs_help && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="text-red-600 font-semibold">⚠️ {t('animals.needs_help')}</span>
                      </div>
                      {mergedAnimal.help_description && (
                        <p className="text-sm text-red-700">{mergedAnimal.help_description}</p>
                      )}
                    </div>
                  )}

                  {/* Merge Reason */}
                  {mergedAnimal.merge_reason && !mergedAnimal.is_primary && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
                      <p className="text-sm text-yellow-800">
                        <strong>📝 {t('merge.merge_reason')}</strong> {mergedAnimal.merge_reason}
                      </p>
                      {mergedAnimal.merged_by_name && (
                        <p className="text-xs text-yellow-700 mt-1">
                          <strong>👤 {t('merge.merged_by')}</strong> {mergedAnimal.merged_by_name}
                          {mergedAnimal.merged_at && (
                            <span className="ml-2">
                              • {new Date(mergedAnimal.merged_at).toLocaleDateString(getLocaleCode(i18n.language))}
                            </span>
                          )}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Create Post Section */}
      {currentUser && (
        <div 
          id="create-post-section"
          className={cardClass} 
          style={{
            ...cardStyle,
            border: '3px solid #f97316', // Orange border
            boxShadow: '0 0 0 1px rgba(249, 115, 22, 0.1), 0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            scrollMarginTop: '100px' // Offset for fixed header
          }}
        >
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                <span className="text-orange-500">✨</span> 📝 {t('common.write_post_about')} {animal.name}
              </h3>
              <span className="px-3 py-1 bg-orange-100 text-orange-600 text-xs font-semibold rounded-full">
                {t('common.share_story')}
              </span>
            </div>
          <form onSubmit={handleSubmitPost} className="space-y-4">
            <textarea
              value={newPost}
              onChange={(e) => setNewPost(e.target.value)}
              placeholder={`${t('common.tell_story_about')} ${animal.name}...`}
              className="w-full px-3 py-2 border-2 border-orange-300 rounded-md focus:ring-orange-500 focus:border-orange-500 focus:outline-none"
              rows="3"
            />

            <div>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handlePostPhotoUpload}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
              
              {selectedPostPhotos.length > 0 && (
                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
                  {selectedPostPhotos.map((photo, index) => (
                    <div key={index} className="relative">
                      <img
                        src={resolvePhotoSrc(photo, 'medium')}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-24 object-cover rounded-md"
                      />
                      <button
                        type="button"
                        onClick={() => removePostPhoto(index)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="bg-blue-600 text-white px-4 sm:px-6 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
            >
              {loading ? (
                <>
                  <span className="inline">⏳</span>
                  <span className="hidden xs:inline ml-1">กำลังโพสต์...</span>
                </>
              ) : (
                <>
                  <img src="/share-icon.png" alt="Post" className="w-5 h-5 inline" />
                  <span className="hidden xs:inline ml-1">{t('common.post')}</span>
                </>
              )}
            </button>
          </form>
          </div>
        </div>
      )}

      {/* Posts Timeline */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">📱 {t('common.post_about')} {animal.name}</h3>
        
        {posts.length === 0 ? (
          <div className={`${cardClass} p-8 text-center`} style={cardStyle}>
            <div className="text-6xl mb-4">📝</div>
            <p className="text-gray-600">{t('posts.no_posts_about', { name: animal.name })}</p>
            <p className="text-sm text-gray-500 mt-2">{t('posts.be_first_to_share', { name: animal.name })}</p>
          </div>
        ) : (
          posts.map((post) => (
            <div key={post.id} className={`${cardClass} p-6`} style={cardStyle}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white">
                    🐾
                  </div>
                  <div>
                     <button
                    onClick={() => handleViewUserClick(post.user_id)}
                    className="text-sm text-blue-600 hover:underline"
                  >
                    👤 {post.created_by_name}
                  </button>
                
                    <p className="text-sm text-gray-500">
                      {new Date(post.created_at).toLocaleDateString(getLocaleCode(i18n.language))} {t('common.at')} {new Date(post.created_at).toLocaleTimeString(getLocaleCode(i18n.language), { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
                
                {(post.post_type === 'location_update' || post.post_type === 'location_rollback') && (
                  <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                    📍 {t('spotted_here.location_update_post')}
                  </span>
                )}
              </div>

              <p className="text-gray-800 mb-4">{post.content}</p>

              {/* Show LocationUpdatePost for location_update or location_rollback posts */}
              {(post.post_type === 'location_update' || post.post_type === 'location_rollback') && (
                <LocationUpdatePost 
                  post={post} 
                  animal={animal}
                  onRollback={() => loadPosts()} 
                />
              )}

              {post.photos && post.photos.length > 0 && (
                <PhotoGrid 
                  photos={post.photos} 
                  onPhotoClick={(photos, index) => openFullScreen(photos, index)}
                />
              )}

              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <div className="flex space-x-4">
                  <button
                    onClick={() => handleLike(post.id)}
                    className={`flex items-center space-x-1 text-sm ${
                      currentUser && post.likes && post.likes.includes(currentUser.id)
                        ? 'text-red-600'
                        : 'text-gray-600 hover:text-red-600'
                    }`}
                  >
                    <span>❤️</span>
                    <span>{post.likes ? post.likes.length : 0}</span>
                  </button>
                  
                  <button
                    onClick={() => toggleComments(post.id)}
                    className="flex items-center space-x-1 text-sm text-gray-600 hover:text-blue-600"
                  >
                    <span>💬</span>
                    <span>{post.comments ? post.comments.length : 0}</span>
                  </button>
                </div>
                
          
              </div>

              {/* Comments Section */}
              {showComments[post.id] && (
                <div className="mt-4 pt-4 border-t space-y-4 ml-8">
                  {/* Existing Comments */}
                  {post.comments && post.comments.map((comment) => (
                    <div key={comment.id} className="bg-gray-50 rounded-md p-3">
                      <div className="flex items-center space-x-2 mb-2">
                        <div className="w-6 h-6 bg-blue-400 rounded-full flex items-center justify-center text-white text-xs">
                          🐾
                        </div>
                        <button
                          onClick={() => handleViewUserClick(comment.user_id)}
                          className="font-medium text-sm text-blue-600 hover:underline"
                        >
                          {comment.username}
                        </button>
                        <span className="text-xs text-gray-500">
                          {new Date(comment.created_at).toLocaleDateString(getLocaleCode(i18n.language))}
                        </span>
                      </div>
                      <p className="text-gray-700 text-sm mb-2">{comment.content}</p>
                      
                      {comment.photos && comment.photos.length > 0 && (
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          {comment.photos.map((photo, index) => (
                            <img
                              key={index}
                              src={resolvePhotoSrc(photo, 'medium')}
                              alt={`Comment photo ${index + 1}`}
                              className="w-full h-20 object-cover rounded-md cursor-pointer hover:opacity-90"
                              onClick={() => openFullScreen(comment.photos, index)}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Add Comment Form */}
                  {currentUser && (
                    <form onSubmit={(e) => handleSubmitComment(e, post.id)} className="space-y-3">
                      <textarea
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="เขียนความคิดเห็น..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                        rows="2"
                      />
                      
                      <div>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleCommentPhotoUpload}
                          className="w-full px-3 py-1 border border-gray-300 rounded-md text-sm"
                        />
                        
                        {commentPhotos.length > 0 && (
                          <div className="mt-2 grid grid-cols-3 gap-2">
                            {commentPhotos.map((photo, index) => (
                              <div key={index} className="relative">
                                <img
                                  src={resolvePhotoSrc(photo, 'medium')}
                                  alt={`Comment preview ${index + 1}`}
                                  className="w-full h-16 object-cover rounded-md"
                                />
                                <button
                                  type="button"
                                  onClick={() => removeCommentPhoto(index)}
                                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs hover:bg-red-600"
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <button
                        type="submit"
                        className="bg-blue-600 text-white px-2 sm:px-4 py-2 rounded-md hover:bg-blue-700 text-sm flex items-center"
                      >
                        <span className="inline">💬</span>
                        <span className="hidden xs:inline ml-1">{t('common.comment')}</span>
                      </button>
                    </form>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Spotted Here Modal */}
      <SpottedHereModal
        isOpen={showSpottedHereModal}
        onClose={() => setShowSpottedHereModal(false)}
        animal={animal}
        onSuccess={() => {
          loadPosts(); // Reload posts to show new location update
          if (onAnimalUpdate) {
            onAnimalUpdate(); // Reload animal data to update map
          }
        }}
      />

      {/* Merge Animal Modal */}
      <MergeAnimalModal
        isOpen={showMergeModal}
        onClose={() => setShowMergeModal(false)}
        currentAnimal={animal}
        onSuccess={() => {
          if (onAnimalUpdate) {
            onAnimalUpdate(); // Reload animal data
          }
          // Could also redirect or reload page
          window.location.reload();
        }}
      />

      {/* Full Screen Image Viewer */}
      {fullScreenImage && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center">
          <div className="relative max-w-4xl max-h-full">
            <img
              src={resolvePhotoSrc(fullScreenImage, 'full')}
              alt="Full screen view"
              className="max-w-full max-h-screen object-contain"
            />
            
            {/* Close Button */}
            <button
              onClick={closeFullScreen}
              className="absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-70"
            >
              ×
            </button>
            
            {/* Navigation Arrows */}
            {fullScreenImages.length > 1 && (
              <>
                <button
                  onClick={() => navigateImage('prev')}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white bg-black bg-opacity-50 rounded-full w-12 h-12 flex items-center justify-center hover:bg-opacity-70"
                >
                  ←
                </button>
                <button
                  onClick={() => navigateImage('next')}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white bg-black bg-opacity-50 rounded-full w-12 h-12 flex items-center justify-center hover:bg-opacity-70"
                >
                  →
                </button>
                
                {/* Image Counter */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white bg-black bg-opacity-50 px-3 py-1 rounded-full">
                  {currentImageIndex + 1} / {fullScreenImages.length}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Highlights Section Component (keeping the existing one)
const HighlightsSection = ({ userLocation, onViewAnimal }) => {
  const [highlights, setHighlights] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHighlights();
  }, [userLocation]);

  const loadHighlights = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (userLocation) {
        params.append('lat', userLocation.lat);
        params.append('lng', userLocation.lng);
        params.append('radius', '10000'); // 10km radius
      }

      const response = await axios.get(`${API}/highlights?${params}`);
      setHighlights(response.data);
    } catch (error) {
      console.error('Error loading highlights:', error);
    } finally {
      setLoading(false);
    }
  };

  
};

// Main App Component
// Main App Component (เดิม)
// Main App Component (เดิม)
function AppContent() {
  const { user, loading, isAuthenticated, logout } = useAuth();
  const { getBackgroundStyle } = useBackground();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Use GPS Context - Unified GPS state management
  const { gpsMode, lastLocation, startGps, stopGps, updateLastLocation, GPS_MODES } = useGps();
  
  // Get initial view from URL query parameter or default to 'map'
  const getInitialView = () => {
    const params = new URLSearchParams(location.search);
    const viewParam = params.get('view');
    return viewParam || 'map'; // Default to map view
  };
  
  const [currentView, setCurrentView] = useState(getInitialView());
  const [selectedAnimal, setSelectedAnimal] = useState(null);
  
  // Update view when URL changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const viewParam = params.get('view');
    if (viewParam && (viewParam === 'map' || viewParam === 'feed')) {
      setCurrentView(viewParam);
    }
  }, [location.search]);
  const [animals, setAnimals] = useState([]);
  const [userLocation, setUserLocation] = useState(() => {
    // Try to get location from localStorage on initial load
    const savedLocation = localStorage.getItem('taillink_location');
    return savedLocation ? JSON.parse(savedLocation) : null;
  });
  const [filters, setFilters] = useState({
    animal_type: '',
    species: '',
    radius: 1000  // Default 1km
  });
  const [loadingAnimals, setLoadingAnimals] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showLocationManager, setShowLocationManager] = useState(false);
  const [gpsUpdateInterval, setGpsUpdateInterval] = useState(null); // For GPS update interval
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [lastFeedLoadLocation, setLastFeedLoadLocation] = useState(null); // Location when feed was last loaded
  const [lastFeedLoadTime, setLastFeedLoadTime] = useState(Date.now()); // Time when feed was last loaded
  const [feedReloadSettings, setFeedReloadSettings] = useState({
    min_distance_meters: 50,
    min_time_minutes: 5
  });
  
  // NEW: Reverse Geocoding Address State
  const [lastLocationAddress, setLastLocationAddress] = useState('');
  const [loadingAddress, setLoadingAddress] = useState(false);
  
  // NEW: Species Filter State
  const [selectedSpecies, setSelectedSpecies] = useState('');
  
  // NEW: Animal Type Filter State
  const [selectedAnimalType, setSelectedAnimalType] = useState('');
  
  // NEW: Recently Moved Animals State
  const [recentlyMovedAnimals, setRecentlyMovedAnimals] = useState([]);
  const [recentlyMovedTimeframe, setRecentlyMovedTimeframe] = useState('7d'); // 7d, 30d, 6m, 1y, all
  const [loadingRecentlyMoved, setLoadingRecentlyMoved] = useState(false);

  // NEW: Use refs to store latest values for interval callback
  const userLocationRef = useRef(userLocation);
  const lastFeedLoadLocationRef = useRef(lastFeedLoadLocation);
  const lastFeedLoadTimeRef = useRef(lastFeedLoadTime);
  const feedReloadSettingsRef = useRef(feedReloadSettings);

  // Update refs when values change
  useEffect(() => {
    userLocationRef.current = userLocation;
  }, [userLocation]);

  useEffect(() => {
    lastFeedLoadLocationRef.current = lastFeedLoadLocation;
  }, [lastFeedLoadLocation]);

  useEffect(() => {
    lastFeedLoadTimeRef.current = lastFeedLoadTime;
  }, [lastFeedLoadTime]);

  useEffect(() => {
    feedReloadSettingsRef.current = feedReloadSettings;
  }, [feedReloadSettings]);

  // ฟังก์ชันคำนวณระยะทาง (Haversine formula)
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // รัศมีโลกในหน่วยกิโลเมตร
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c; // ระยะทางในกิโลเมตร
    return distance;
  };
  // Recalculate distances for all animals based on new location
  const recalculateDistances = (newLocation) => {
    setAnimals(prevAnimals => {
      return prevAnimals.map(animal => {
        if (animal.location && animal.location.lat && animal.location.lng) {
          const distance = calculateDistance(
            newLocation.lat,
            newLocation.lng,
            animal.location.lat,
            animal.location.lng
          );
          return {
            ...animal,
            distance: distance * 1000 // Convert to meters
          };
        }
        return animal;
      });
    });
  };

  useEffect(() => {
    getUserLocation();
    // ไม่เรียก loadAnimals() ตรงนี้ เพราะจะโหลดโดยไม่มี filters
    // ให้รอ useEffect ถัดไปที่มี dependency [userLocation, filters] 
  }, []);

  useEffect(() => {
    getUserLocation();
  }, [user]);

  // Reverse Geocoding: Convert lastLocation to address
  useEffect(() => {
    if (lastLocation && (lastLocation.lat && lastLocation.lng)) {
      setLoadingAddress(true);
      
      // Call OpenStreetMap Nominatim API for reverse geocoding
      fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lastLocation.lat}&lon=${lastLocation.lng}&addressdetails=1`)
        .then(response => response.json())
        .then(data => {
          const address = data.display_name || `${lastLocation.lat.toFixed(6)}, ${lastLocation.lng.toFixed(6)}`;
          setLastLocationAddress(address);
          console.log('📍 Reverse geocoded address:', address);
        })
        .catch(error => {
          console.error('Error reverse geocoding:', error);
          setLastLocationAddress(`${lastLocation.lat.toFixed(6)}, ${lastLocation.lng.toFixed(6)}`);
        })
        .finally(() => {
          setLoadingAddress(false);
        });
    }
  }, [lastLocation]);

  // Load animals when lastLocation changes (Map pan/zoom)
  useEffect(() => {
    if (lastLocation) {
      console.log('🔵 lastLocation changed, reloading feed with top 50 closest');
      loadAnimals();
    }
  }, [lastLocation]);

  // Reload animals when species filter changes
  useEffect(() => {
    if (lastLocation) {
      console.log('🔍 Species filter changed, reloading animals');
      loadAnimals();
    }
  }, [selectedSpecies]);

  // Reload animals when animal type filter changes
  useEffect(() => {
    if (lastLocation) {
      console.log('🔍 Animal type filter changed, reloading animals');
      loadAnimals();
    }
  }, [selectedAnimalType]);

  // Cleanup GPS update interval on component unmount
  useEffect(() => {
    return () => {
      if (gpsUpdateInterval !== null) {
        clearInterval(gpsUpdateInterval);
        console.log('Cleared GPS update interval');
      }
    };
  }, [gpsUpdateInterval]);

  // Handle GPS mode changes - sync with GPS Context
  useEffect(() => {
    const isGpsActive = gpsMode !== GPS_MODES.OFF;
    
    // Save preference to localStorage
    localStorage.setItem('taillink_auto_update_gps', JSON.stringify(isGpsActive));
    
    // Clear any existing interval
    if (gpsUpdateInterval !== null) {
      clearInterval(gpsUpdateInterval);
      setGpsUpdateInterval(null);
      console.log('Cleared existing GPS interval');
    }
    
    // If GPS is active (TRACK or RADAR), start interval to fetch GPS every 4 seconds
    if (isGpsActive && navigator.geolocation) {
      const initAutoUpdateGPS = async () => {
        try {
          // 1. Fetch GPS location first (without feed update) and wait for it
          console.log(`📍 GPS ${gpsMode} mode - Fetching current location...`);
          const newLocation = await fetchGPSLocation(false);
          
          // 2. Wait a bit for state to update
          await new Promise(resolve => setTimeout(resolve, 300));
          
          // 3. Then reload feed with new location
          console.log('🔄 Reloading feed with new GPS location:', newLocation);
          await loadAnimals(newLocation);
          
          // 4. Set up interval for continuous updates every 4 seconds
          const intervalId = setInterval(async () => {
            try {
              await fetchGPSLocation(true); // Pass true to enable feed updates in interval
            } catch (error) {
              console.error('❌ GPS interval error (will retry):', error.message);
              // Don't stop interval - just log and retry next time
            }
          }, 4000);
          
          setGpsUpdateInterval(intervalId);
          console.log(`✅ Started GPS auto-update for ${gpsMode} mode (every 4 seconds)`);
        } catch (error) {
          console.error('❌ Failed to initialize GPS auto-update:', error);
        }
      };
      
      initAutoUpdateGPS();
    }
    
    // Cleanup on unmount or mode change
    return () => {
      if (gpsUpdateInterval !== null) {
        clearInterval(gpsUpdateInterval);
        console.log('🧹 Cleanup: Cleared GPS interval');
      }
    };
  }, [gpsMode]);

  // Load feed reload settings
  useEffect(() => {
    const loadFeedReloadSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings/feed-reload`);
        setFeedReloadSettings({
          min_distance_meters: response.data.min_distance_meters,
          min_time_minutes: response.data.min_time_minutes
        });
        console.log('Feed reload settings loaded:', response.data);
      } catch (error) {
        console.error('Error loading feed reload settings:', error);
        // Use default values if error
      }
    };
    loadFeedReloadSettings();
  }, []);

  // Fetch unread notifications count (mock for now, should be from backend API)
  useEffect(() => {
    if (isAuthenticated && user) {
      // TODO: Replace with actual API call to fetch unread notifications
      // For demo purposes, set a mock value
      // const fetchUnreadCount = async () => {
      //   try {
      //     const response = await axios.get(`${API}/notifications/unread-count`);
      //     setUnreadNotifications(response.data.count);
      //   } catch (error) {
      //     console.log('Error fetching unread notifications:', error);
      //   }
      // };
      // fetchUnreadCount();
      
      // Mock: Set to 0 initially, will be updated by notification events
      setUnreadNotifications(0);
    } else {
      setUnreadNotifications(0);
    }
  }, [isAuthenticated, user]);

  // ฟังก์ชันคำนวณระยะทางสำหรับ animal card ในหน้าแรก
  const getAnimalDistance = (animal) => {
    if (!userLocation || !animal.location) return null;
    
    const userLat = userLocation.lat;
    const userLng = userLocation.lng;
    const animalLat = animal.location.lat;
    const animalLng = animal.location.lng;

    return calculateDistance(userLat, userLng, animalLat, animalLng);
  };

  // ฟังก์ชันResize imageก่อนอัปโหลด
  const resizeImage = (file, maxWidth = 800, maxHeight = 600, quality = 0.8) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // คำนวณขนาดใหม่โดยคงอัตราส่วน
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }
        
        // ตั้งค่า canvas
        canvas.width = width;
        canvas.height = height;
        
        // วาดรูปที่ย่อขนาดแล้ว
        ctx.drawImage(img, 0, 0, width, height);
        
        // แปลงเป็น base64
        const base64 = canvas.toDataURL('image/jpeg', quality);
        resolve(base64);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  // ฟังก์ชันจัดการการเลือกรูปและย่อขนาด
  const handleImageSelection = async (files, callback) => {
    if (!files || files.length === 0) return;
    
    const resizedImages = [];
    
    for (let i = 0; i < Math.min(files.length, 5); i++) {
      const file = files[i];
      
      // ตรวจสอบt('admin.hero_type')ไฟล์
      if (!file.type.startsWith('image/')) {
        alert(t('alerts.please_select_image_only'));
        continue;
      }
      
      // ตรวจสอบขนาดไฟล์ (ก่อนย่อ)
      if (file.size > 10 * 1024 * 1024) { // 10MB
        alert(t('alerts.max_file_size'));
        continue;
      }
      
      try {
        const resizedBase64 = await resizeImage(file);
        resizedImages.push(resizedBase64);
      } catch (error) {
        console.error('Error resizing image:', error);
        alert(t('alerts.image_error'));
      }
    }
    
    callback(resizedImages);
  };

  const getUserLocation = async () => {
    // Priority 1: Load location from localStorage (user selection)
    const savedLocation = localStorage.getItem('taillink_location');
    if (savedLocation) {
      try {
        const location = JSON.parse(savedLocation);
        setUserLocation(location);
        console.log('📍 Loaded location from localStorage:', location);
        return;
      } catch (error) {
        console.log('Error parsing saved location:', error);
        localStorage.removeItem('taillink_location');
      }
    }

    // Priority 2: Use last map center location (always available when map was used)
    if (lastLocation) {
      const location = {
        lat: lastLocation.lat,
        lng: lastLocation.lng,
        address: 'Last Map Location'
      };
      setUserLocation(location);
      localStorage.setItem('taillink_location', JSON.stringify(location));
      console.log('📍 Using last map location:', location);
      return;
    }

    // TODO: Uncomment this later to sync with backend for logged-in users
    // // If logged in, try to get user's saved location from backend
    // if (isAuthenticated && user) {
    //   try {
    //     const response = await axios.get(`${API}/auth/location`);
    //     if (response.data.location) {
    //       const location = response.data.location;
    //       setUserLocation(location);
    //       localStorage.setItem('taillink_location', JSON.stringify(location));
    //       return;
    //     }
    //   } catch (error) {
    //     console.log('No saved location found on backend');
    //   }
    // }

    // Fallback to GPS location using getCurrentPosition
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setUserLocation(location);
          localStorage.setItem('taillink_location', JSON.stringify(location));
          
          // Reload feed ด้วย location ใหม่ทันที
          setTimeout(() => {
            loadAnimals(location);
          }, 500);
        },
        (error) => {
          console.log('Error getting GPS location:', error);
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0
        }
      );
    }
  };

  // Function to fetch GPS location (called repeatedly when auto-update is enabled)
  const fetchGPSLocation = async (shouldUpdateFeed = false) => {
    return new Promise((resolve, reject) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const location = {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            };
            
            // Update state and localStorage สำหรับทุกคน
            setUserLocation(location);
            localStorage.setItem('taillink_location', JSON.stringify(location));
            console.log('GPS updated:', location);
            
            // If shouldUpdateFeed is true (from interval updates), check if we should reload
            const isGpsActive = gpsMode !== GPS_MODES.OFF;
            if (shouldUpdateFeed && isGpsActive) {
              console.log(`GPS ${gpsMode} mode active - checking if feed reload needed`);
              // Check if feed should be reloaded based on distance/time settings
              if (shouldReloadFeed(location)) {
                loadAnimals(location);
              } else {
                // Just recalculate distances without reloading from backend
                recalculateDistances(location);
              }
            }
            
            // TODO: Uncomment this later to save to backend for logged-in users
            // if (user) {
            //   try {
            //     await axios.put(`${API}/auth/location`, location);
            //     console.log('✅ GPS location saved to backend');
            //   } catch (error) {
            //     console.error('Error saving GPS location to backend:', error);
            //   }
            // }
            
            resolve(location);
          },
          (error) => {
            // Only stop GPS on permission denied — transient errors should not kill GPS
            if (error.code === error.PERMISSION_DENIED) {
              console.error('GPS Permission denied');
              stopGps();
              alert('GPS Error: คุณไม่ได้อนุญาตให้เข้าถึงตำแหน่ง กรุณาอนุญาตใน browser settings');
            } else {
              // Timeout or position unavailable — just log, interval will retry
              console.warn(`GPS transient error [${error.code}]: ${error.message}`);
            }
            
            reject(new Error(error.message || 'GPS error'));
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 5000
          }
        );
      } else {
        reject(new Error('Geolocation not available'));
      }
    });
  };

  // Function to check if feed should be reloaded based on location/time
  const shouldReloadFeed = (newLocation = null) => {
    const currentLocation = newLocation || userLocationRef.current;
    const lastLocation = lastFeedLoadLocationRef.current;
    
    if (!currentLocation || !lastLocation) return false;
    
    // Check distance moved (use value from settings)
    const distanceMoved = calculateDistance(
      lastLocation.lat,
      lastLocation.lng,
      currentLocation.lat,
      currentLocation.lng
    );
    const distanceInMeters = distanceMoved * 1000; // Convert km to meters
    
    if (distanceInMeters > feedReloadSettingsRef.current.min_distance_meters) {
      console.log(`Distance moved: ${distanceInMeters.toFixed(2)}m (threshold: ${feedReloadSettingsRef.current.min_distance_meters}m) - Reloading feed`);
      return true;
    }
    
    // Check time passed (use value from settings)
    const timePassed = Date.now() - lastFeedLoadTimeRef.current;
    const minutesPassed = timePassed / (1000 * 60);
    
    if (minutesPassed > feedReloadSettingsRef.current.min_time_minutes) {
      console.log(`Time passed: ${minutesPassed.toFixed(2)} minutes (threshold: ${feedReloadSettingsRef.current.min_time_minutes} minutes) - Reloading feed`);
      return true;
    }
    
    return false;
  };

  const loadAnimals = async (customLocation = null) => {
    setLoadingAnimals(true);
    try {
      // Use lastLocation as current location
      const locationToUse = lastLocation;
      
      console.log('🔍 Loading 50 closest animals from location:', locationToUse);
      
      if (!locationToUse) {
        console.warn('⚠️ No lastLocation available, cannot load animals');
        setAnimals([]);
        setLoadingAnimals(false);
        return;
      }
      
      const params = new URLSearchParams({
        lat: locationToUse.lat.toString(),
        lng: locationToUse.lng.toString(),
        radius: '1000', // 1 km radius
        limit: '50', // Get top 50 closest animals
        sort: 'distance' // Sort by distance from location
      });
      
      // Add species filter if selected
      if (selectedSpecies) {
        params.append('species', selectedSpecies);
      }
      
      // Add animal type filter if selected
      if (selectedAnimalType) {
        params.append('animal_type', selectedAnimalType);
      }
      
      // Save location and time when feed is loaded
      setLastFeedLoadLocation({ lat: locationToUse.lat, lng: locationToUse.lng });
      setLastFeedLoadTime(Date.now());

      const response = await axios.get(`${API}/animals?${params}`);
      console.log(`✅ Loaded ${response.data.length} closest animals`);
      setAnimals(response.data);
    } catch (error) {
      console.error('Error loading animals:', error);
    } finally {
      setLoadingAnimals(false);
    }
  };

  // NEW: Refs for recently moved animals
  const recentlyMovedTimeframeRef = useRef(recentlyMovedTimeframe);
  const animalsRef = useRef(animals);

  useEffect(() => {
    recentlyMovedTimeframeRef.current = recentlyMovedTimeframe;
  }, [recentlyMovedTimeframe]);

  useEffect(() => {
    animalsRef.current = animals;
  }, [animals]);

  // NEW: Load Recently Moved Animals (using refs)
  const loadRecentlyMovedAnimals = useCallback(async () => {
    const locationToUse = lastLocation; // Use lastLocation instead of userLocationRef
    const timeframeToUse = recentlyMovedTimeframeRef.current;
    const currentRadius = 1000; // Use fixed 1km radius
    
    if (!locationToUse) {
      setRecentlyMovedAnimals([]);
      return;
    }
    
    setLoadingRecentlyMoved(true);
    try {
      const params = new URLSearchParams({
        lat: locationToUse.lat.toString(),
        lng: locationToUse.lng.toString(),
        radius: currentRadius.toString(),
        timeframe: timeframeToUse
      });

      const response = await axios.get(`${API}/animals/recently-moved?${params}`);
      
      // Filter out animals that are already in main feed
      const currentAnimals = animalsRef.current;
      const mainFeedAnimalIds = new Set(currentAnimals.map(a => a.id));
      
      console.log(`📊 Main feed has ${currentAnimals.length} animals:`, Array.from(mainFeedAnimalIds).slice(0, 3));
      console.log(`📊 Recently moved raw data: ${response.data.length} animals`);
      
      const filteredRecentlyMoved = response.data.filter(
        item => !mainFeedAnimalIds.has(item.animal.id)
      );
      
      console.log(`✅ Loaded ${response.data.length} recently moved animals (${filteredRecentlyMoved.length} after filtering duplicates with main feed)`);
      setRecentlyMovedAnimals(filteredRecentlyMoved);
    } catch (error) {
      console.error('Error loading recently moved animals:', error);
      setRecentlyMovedAnimals([]);
    } finally {
      setLoadingRecentlyMoved(false);
    }
  }, [lastLocation]); // Depend on lastLocation

  // NEW: Load recently moved animals - initial load and when timeframe changes
  useEffect(() => {
    if (!lastLocation) return;

    // Add delay to ensure animals are loaded first
    const timer = setTimeout(() => {
      console.log('🔄 Loading recently moved animals (timeframe change)');
      loadRecentlyMovedAnimals();
    }, 1000); // Wait 1 second after main feed loads

    return () => clearTimeout(timer);
  }, [recentlyMovedTimeframe, loadRecentlyMovedAnimals]);

  // NEW: Re-filter recently moved when main animals change (to remove duplicates)
  useEffect(() => {
    if (!lastLocation) return;
    if (recentlyMovedAnimals.length === 0) return; // No need to filter if empty

    console.log(`🔍 Re-filtering recently moved animals (main feed has ${animals.length} animals)`);
    
    // Re-filter existing data
    const mainFeedAnimalIds = new Set(animals.map(a => a.id));
    const filtered = recentlyMovedAnimals.filter(item => !mainFeedAnimalIds.has(item.animal.id));
    
    if (filtered.length !== recentlyMovedAnimals.length) {
      console.log(`🔍 Filtered out ${recentlyMovedAnimals.length - filtered.length} duplicates`);
      setRecentlyMovedAnimals(filtered);
    }
  }, [animals]);

  const handleViewAnimal = (animal) => {
    navigate(`/animal/${animal.id}`);
  };

  // NEW: Reload selected animal data
  const reloadSelectedAnimal = async () => {
    if (!selectedAnimal) return;
    
    try {
      const response = await axios.get(`${API}/animals/${selectedAnimal.id}`);
      setSelectedAnimal(response.data);
      console.log('✅ Animal data reloaded');
    } catch (error) {
      console.error('Error reloading animal:', error);
    }
  };

  const handleAddAnimal = () => {
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }
    setCurrentView('add');
  };

  const handleAnimalCreated = () => {
    setCurrentView('feed');
    loadAnimals();
  };

  const handleViewUser = (userId) => {
    navigate(`/user/${userId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🐾</div>
          <p className="text-xl text-gray-600">{t('common.loading')} TailLink...</p>
        </div>
      </div>
    );
  }

  return (
    <BackgroundImage page="home" className="fur-background">
      {/* Announcement Popup */}
      <AnnouncementPopup />

      {/* Navigation Header - Hidden in Map View */}
      {currentView !== 'map' && (
        <nav className="fur-nav bg-white/95 shadow-sm border-b border-fur-primary/10 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div 
              className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => setCurrentView('feed')}
            >
              <img src="/taillink-logo.png" alt="TailLink" className="h-12 w-auto object-contain" />
              <h1 className="fur-heading font-bold text-2xl text-fur-primary">TailLink</h1>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-4">
              {/* Add Animal - CTA button */}
              <button
                onClick={handleAddAnimal}
                className="bg-orange-600 text-white px-2 sm:px-4 py-2 rounded-md hover:bg-orange-700 transition-colors font-semibold"
              >
                <span className="inline text-xl font-bold">+</span>
                <span className="hidden sm:inline ml-1">{t('nav.add_animal')}</span>
              </button>
              
              {isAuthenticated && (
                <>
                  {user?.is_admin && (
                    <button
                      onClick={() => window.open('/admin', '_blank')}
                      className="px-2 sm:px-4 py-2 rounded-md transition-colors text-orange-600 hover:bg-orange-50 border border-orange-200"
                    >
                      <span className="inline">🛠️</span>
                      <span className="hidden sm:inline ml-1">{t('nav.admin')}</span>
                    </button>
                  )}
                </>
              )}
              
              {/* Settings button - visible to everyone */}
              <button
                onClick={() => navigate('/notifications')}
                className="px-2 sm:px-4 py-2 rounded-md bg-gray-600 text-white hover:bg-gray-700 transition-colors relative"
              >
                <span className="inline">⚙️</span>
                <span className="hidden sm:inline ml-1">{t('nav.settings')}</span>
                {/* Notification Badge */}
                {unreadNotifications > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center shadow-lg animate-pulse">
                    {unreadNotifications > 9 ? '9+' : unreadNotifications}
                  </span>
                )}
              </button>
            </div>

            {/* User Menu / Login Buttons */}
            <div className="hidden lg:flex items-center space-x-4">
              {isAuthenticated ? (
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => navigate(`/user/${user?.id}`)}
                    className="flex items-center space-x-2 hover:bg-gray-50 rounded px-3 py-2"
                  >
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white">
                      🐾
                    </div>
                    <span className="text-gray-700">{user?.username}</span>
                  </button>
                  <button
                    onClick={logout}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    🚪 {t('nav.logout')}
                  </button>
                </div>
              ) : (
                <div className="flex space-x-2">
                  <button
                    onClick={() => setShowLoginModal(true)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    {t('nav.login')}
                  </button>
                  <button
                    onClick={() => setShowRegisterModal(true)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                  >
                    {t('nav.register')}
                  </button>
                </div>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="lg:hidden">
              <button
                onClick={() => setShowMobileMenu(true)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </nav>
      )}

      {/* Mobile Menu */}
      <MobileMenu
        isOpen={showMobileMenu}
        onClose={() => setShowMobileMenu(false)}
        currentView={currentView}
        onNavigate={(view) => {
          if (view === 'add') {
            handleAddAnimal();
          } else if (view === 'set_location') {
            setShowLocationManager(true);
          } else {
            setCurrentView(view);
          }
          setShowMobileMenu(false);
        }}
        isAuthenticated={isAuthenticated}
        onLogin={() => setShowLoginModal(true)}
        onRegister={() => setShowRegisterModal(true)}
        onLogout={logout}
        user={user}
        unreadNotifications={unreadNotifications}
      />

      {/* Hero Section */}
      {currentView === 'feed' && <DynamicHeroSection />}

      {/* Main Content */}
      <main className={currentView === 'map' ? '' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'}>
        {/* Welcome Banner - แสดงใน feed view */}
        {currentView === 'feed' && (
          <WelcomeBanner show={getSetting('ui.showWelcomeMessage', true)} />
        )}
        
        {currentView === 'admin' && isAuthenticated && (
          <AdminPanel user={user} />
        )}

        {currentView === 'add' && (
          <AddAnimalForm
            onSubmit={handleAnimalCreated}
            onCancel={() => setCurrentView('feed')}
            currentUser={user}
          />
        )}

        {currentView === 'detail' && selectedAnimal && (
          <AnimalDetailView
            animal={selectedAnimal}
            onBack={() => {
              setCurrentView('feed');
              setSelectedAnimal(null);
            }}
            onPostSubmit={loadAnimals}
            onAnimalUpdate={reloadSelectedAnimal}
            currentUser={user}
            onViewUser={handleViewUser}
          />
        )}

        {currentView === 'events' && (
          <Suspense fallback={<V1Fallback/>}><EventsPage userLocation={userLocation} /></Suspense>
        )}

        {currentView === 'lost-pets' && (
          <Suspense fallback={<V1Fallback/>}><LostPetsPage userLocation={userLocation} /></Suspense>
        )}

        {currentView === 'blood-bank' && (
          <Suspense fallback={<V1Fallback/>}><BloodBankPage currentUser={user} userLocation={userLocation} /></Suspense>
        )}

        {currentView === 'blood-buddy' && (
          <Suspense fallback={<V1Fallback/>}><BloodBuddyPage currentUser={user} userLocation={userLocation} /></Suspense>
        )}

        {currentView === 'feed' && (
          <div className="pb-20">
            {/* Highlights Section */}
            <HighlightsSection 
              userLocation={userLocation} 
              onViewAnimal={handleViewAnimal}
            />

            {/* Animals Near You Section */}
            <div className="bg-fur-secondary/70 rounded-lg shadow-md p-6">
              <div className="flex justify-between items-center mb-6">
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-gray-800">
                    🐾 {t('animals.near_you')}
                  </h2>
                  {lastLocation && (
                    <button
                      onClick={() => setCurrentView('map')}
                      className="mt-2 text-sm text-gray-600 hover:text-gray-800 transition-colors cursor-pointer group w-full"
                    >
                      <div className="flex flex-wrap items-center gap-2 bg-white border border-orange-500 hover:border-orange-600 rounded-lg px-3 py-2 shadow-sm hover:shadow transition-all text-left">
                        <img src="/location-pin.png" alt="Location" className="w-4 h-4 flex-shrink-0" />
                        <div className="flex-1 min-w-[200px]">
                          <span className="text-xs text-gray-500 block">📍 {t('animals.current_location')}</span>
                          <span className="text-xs text-gray-800 block break-words">
                            {loadingAddress ? '🔄 กำลังโหลด...' : (lastLocationAddress || 'กำลังโหลดที่อยู่...')}
                          </span>
                          <span className="text-xs text-gray-500 font-mono block mt-0.5">
                            {lastLocation.lat.toFixed(6)}, {lastLocation.lng.toFixed(6)}
                          </span>
                        </div>
                      </div>
                    </button>
                  )}
                  
                  {/* Filters Section - Compact Layout */}
                  <div className="mt-3 flex flex-wrap gap-2 items-center">
                    {/* Animal Type Filter - Compact */}
                    <select
                      value={selectedAnimalType}
                      onChange={(e) => setSelectedAnimalType(e.target.value)}
                      className="text-sm px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white"
                    >
                      <option value="">🏷️ {t('animals.all_types') || 'ทุกประเภท'}</option>
                      <option value="owned">🏠 {t('animals.pet') || 'สัตว์เลี้ยง'}</option>
                      <option value="stray">🐾 {t('animals.stray') || 'สัตว์จรจัด'}</option>
                      <option value="social_pet">❤️ {t('animals.social_pet') || 'สัตว์เลี้ยงชุมชน'}</option>
                      <option value="wild">🌲 {t('profile.pet_type_wild') || 'สัตว์ป่า'}</option>
                    </select>

                    {/* Species Filter - Compact */}
                    <select
                      value={selectedSpecies}
                      onChange={(e) => setSelectedSpecies(e.target.value)}
                      className="text-sm px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white"
                    >
                      <option value="">🔍 {t('animals.all_species') || 'ทุกชนิด'}</option>
                      <option value="dog">🐕 {t('animals.dog') || 'สุนัข'}</option>
                      <option value="cat">🐱 {t('animals.cat') || 'แมว'}</option>
                      <option value="bird">🐦 {t('animals.bird') || 'นก'}</option>
                      <option value="rabbit">🐰 {t('animals.rabbit') || 'กระต่าย'}</option>
                      <option value="hamster">🐹 {t('animals.hamster') || 'หนูแฮมสเตอร์'}</option>
                      <option value="fish">🐠 {t('animals.fish') || 'ปลา'}</option>
                      <option value="turtle">🐢 {t('animals.turtle') || 'เต่า'}</option>
                      <option value="other">🐾 {t('animals.other') || 'อื่นๆ'}</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Animals Grid */}
              {!userLocation ? (
                <div className="text-center py-12">
                  <img src="/location-pin.png" alt="Location" className="w-20 h-20 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">{t('animals.use_gps')}</h3>
                  <p className="text-gray-600 mb-4">{t('animals.near_you')}</p>
                  <div className="space-y-3">
                    <button
                      onClick={getUserLocation}
                      className="bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 flex items-center justify-center space-x-2 mx-auto font-medium transition-all shadow-md"
                    >
                      <img src="/location-pin.png" alt="GPS" className="w-5 h-5 brightness-0 invert" />
                      <span>{t('animals.use_gps')}</span>
                    </button>
                    <button
                      onClick={() => setShowLocationManager(true)}
                      className="bg-gray-600 text-white px-6 py-3 rounded-xl hover:bg-gray-700 font-medium transition-all shadow-md flex items-center justify-center space-x-2 mx-auto"
                    >
                      <img src="/maps-icon.png" alt="Map" className="w-6 h-6" />
                      <span>{t('common.select_location')}</span>
                    </button>
                  </div>
                </div>
              ) : loadingAnimals ? (
                <div className="space-y-6">
                  {/* Loading Message */}
                  <div className="text-center py-8">
                    <div className="inline-block">
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
                        <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
                        <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
                      </div>
                      <p className="text-gray-600 mt-4 font-medium">{t('common.loading')}...</p>
                    </div>
                  </div>
                  
                  {/* Skeleton Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2, 3, 4, 5, 6].map(i => (
                      <div key={i} className="bg-white border rounded-lg overflow-hidden shadow-sm">
                        <div className="animate-pulse">
                          <div className="bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 h-48 bg-[length:200%_100%] animate-shimmer"></div>
                          <div className="p-4 space-y-3">
                            <div className="h-5 bg-gray-200 rounded w-3/4"></div>
                            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                            <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                            <div className="flex space-x-2 mt-3">
                              <div className="h-6 bg-gray-200 rounded-full w-16"></div>
                              <div className="h-6 bg-gray-200 rounded-full w-20"></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : animals.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">🐾</div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">{t('animals.no_animals')}</h3>
                  <p className="text-gray-600 mb-4">{t('animals.add_first')}</p>
                  <button
                    onClick={handleAddAnimal}
                    className="bg-green-600 text-white px-6 py-3 rounded-md hover:bg-green-700"
                  >
                    ➕ {t('nav.add_animal')}
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {animals.map((animal) => (
                    <div
                      key={animal.id}
                      className="bg-white border rounded-lg overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                      onClick={() => handleViewAnimal(animal)}
                    >
                      {/* Animal Photo */}
                      {animal.photos && animal.photos.length > 0 && (
                        <img
                          src={resolvePhotoSrc(animal.photos[0], 'medium')}
                          alt={animal.name}
                          className="w-full h-48 object-cover"
                        />
                      )}
           
                      <div className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <span className="text-2xl">{getAnimalIcon(animal.species)}</span>
                            <h3 className="font-semibold text-gray-800">{animal.name}</h3>
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            animal.animal_type === 'owned' ? 'bg-green-100 text-green-800' :
                            animal.animal_type === 'stray' ? 'bg-orange-100 text-orange-800' :
                            animal.animal_type === 'wild' ? 'bg-amber-100 text-amber-800' :
                            'bg-teal-100 text-teal-800'
                          }`}>
                            {animal.animal_type === 'owned' ? t('animals.pet') :
                             animal.animal_type === 'stray' ? t('animals.stray') :
                             animal.animal_type === 'wild' ? t('profile.pet_type_wild') :
                             t('animals.social_pet')}
                          </span>
                        </div>

                        <p className="text-gray-600 text-sm mb-2">
                          {animal.species} {animal.breed && `• ${animal.breed}`}
                        </p>

                        <p className="text-gray-700 text-sm mb-3 line-clamp-2">
                          {animal.description}
                        </p>
                        
                        <div className="flex justify-between items-center text-xs text-gray-500">
                          <span className="text-gray-600">{t('common.distance')}</span>
                          <span className="font-medium text-blue-700">
                            {(() => {
                              // ใช้ distance จาก backend ถ้ามี
                              if (animal.distance !== undefined && animal.distance !== null) {
                                return animal.distance < 1000 
                                  ? `${Math.round(animal.distance)} ${t('common.meters')}` 
                                  : `${(animal.distance / 1000).toFixed(1)} ${t('common.km')}`;
                              }
                              
                              // Fallback: คำนวณ distance เอง
                              const distance = getAnimalDistance(animal);
                              if (!distance) return "ไม่ทราบ";
                              return distance < 1 
                                ? `${Math.round(distance * 1000)} ${t('common.meters')}` 
                                : `${distance.toFixed(1)} ${t('common.km')}`;
                            })()}
                          </span>
                        </div>

                        <div className="flex justify-between items-center text-xs text-gray-500">
                          <span>👥 {animal.followers?.length || 0} {t('common.followers')}</span>
                          {animal.created_by_name && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent card click
                                handleViewUser(animal.created_by);
                              }}
                              className="text-blue-600 hover:underline"
                            >
                              {t('common.by')} {animal.created_by_name}
                            </button>
                          )}
                        </div>

                        {animal.needs_help && (
                          <div className="mt-2 bg-red-50 border border-red-200 rounded p-2">
                            <span className="text-red-800 text-xs font-medium">🆘 ต้องการความช่วยเหลือ</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Recently Moved Animals Section */}
            {userLocation && (
              <div className="bg-yellow-50 rounded-lg shadow-md p-6 mt-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                  <div className="flex-1">
                    <h2 className="text-xl font-bold text-gray-800 flex items-center">
                      <span className="mr-2">🔄</span>
                      {t('animals.recently_moved')}
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      {t('animals.recently_moved_desc')}
                    </p>
                  </div>
                  
                  {/* Timeframe Selector */}
                  <div className="w-full sm:w-auto">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('animals.show_moved_from')}
                    </label>
                    <select
                      value={recentlyMovedTimeframe}
                      onChange={(e) => setRecentlyMovedTimeframe(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500 bg-white"
                    >
                      <option value="7d">{t('animals.timeframe_7d')}</option>
                      <option value="30d">{t('animals.timeframe_30d')}</option>
                      <option value="6m">{t('animals.timeframe_6m')}</option>
                      <option value="1y">{t('animals.timeframe_1y')}</option>
                      <option value="all">{t('animals.timeframe_all')}</option>
                    </select>
                  </div>
                </div>

                {/* Recently Moved Animals Grid */}
                {loadingRecentlyMoved ? (
                  <div className="text-center py-8">
                    <div className="inline-block">
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-orange-600 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
                        <div className="w-3 h-3 bg-orange-600 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
                        <div className="w-3 h-3 bg-orange-600 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
                      </div>
                      <p className="text-gray-600 mt-4 font-medium">{t('common.loading')}...</p>
                    </div>
                  </div>
                ) : recentlyMovedAnimals.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="text-4xl mb-2">🗺️</div>
                    <p className="text-gray-600">{t('animals.no_recently_moved')}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {recentlyMovedAnimals.map((data) => (
                      <RecentlyMovedAnimalCard
                        key={data.animal.id}
                        data={data}
                        onViewAnimal={handleViewAnimal}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {currentView === 'map' && (
          <Suspense fallback={<V1Fallback/>}>
            <MapView
              userLocation={userLocation}
              onAnimalClick={handleViewAnimal}
              onBack={() => setCurrentView('feed')}
              setCurrentView={setCurrentView}
              currentUser={user}
            />
          </Suspense>
        )}
      </main>

      {/* Bottom Navigation Bar - All Screen Sizes (Always Visible) */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-gray-200 shadow-lg z-[2000]">
        <div className="flex justify-around items-center h-16">
          <button
            onClick={() => setCurrentView('map')}
            className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
              currentView === 'map' ? 'text-blue-600 bg-blue-50' : 'text-gray-600'
            }`}
          >
            <span className="text-xl mb-0.5">🗺️</span>
            <span className="text-[10px] font-medium">Map</span>
          </button>
          
          <button
            onClick={() => setCurrentView('feed')}
            className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
              currentView === 'feed' ? 'text-blue-600 bg-blue-50' : 'text-gray-600'
            }`}
          >
            <span className="text-xl mb-0.5">📋</span>
            <span className="text-[10px] font-medium">Feed</span>
          </button>

          <button
            onClick={() => setCurrentView('events')}
            className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
              currentView === 'events' ? 'text-orange-600 bg-orange-50' : 'text-gray-600'
            }`}
            data-testid="nav-events"
          >
            <span className="text-xl mb-0.5">🎉</span>
            <span className="text-[10px] font-medium">Events</span>
          </button>

          <button
            onClick={() => setCurrentView('lost-pets')}
            className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
              currentView === 'lost-pets' ? 'text-red-600 bg-red-50' : 'text-gray-600'
            }`}
            data-testid="nav-lost-pets"
          >
            <span className="text-xl mb-0.5">🔍</span>
            <span className="text-[10px] font-medium">ตามหา</span>
          </button>

          <button
            onClick={() => setCurrentView('blood-bank')}
            className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
              currentView === 'blood-bank' ? 'text-red-600 bg-red-50' : 'text-gray-600'
            }`}
            data-testid="nav-blood-bank"
          >
            <span className="text-xl mb-0.5">🩸</span>
            <span className="text-[10px] font-medium">Blood</span>
          </button>

          <button
            onClick={() => setCurrentView('blood-buddy')}
            className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
              currentView === 'blood-buddy' ? 'text-orange-600 bg-orange-50' : 'text-gray-600'
            }`}
            data-testid="nav-blood-buddy"
          >
            <span className="text-xl mb-0.5">🤝</span>
            <span className="text-[10px] font-medium">Buddy</span>
          </button>
          
          <button
            onClick={() => {
              if (user) {
                navigate(`/user/${user.id}`);
              } else {
                setShowLoginModal(true);
              }
            }}
            className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
              currentView === 'profile' ? 'text-blue-600 bg-blue-50' : 'text-gray-600'
            }`}
          >
            <span className="text-xl mb-0.5">👤</span>
            <span className="text-[10px] font-medium">Profile</span>
          </button>
        </div>
      </div>

      {/* Login Modal */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSwitchToRegister={() => {
          setShowLoginModal(false);
          setShowRegisterModal(true);
        }}
      />

      {/* Register Modal */}
      <RegisterModal
        isOpen={showRegisterModal}
        onClose={() => setShowRegisterModal(false)}
        onSwitchToLogin={() => {
          setShowRegisterModal(false);
          setShowLoginModal(true);
        }}
      />

      {/* User Location Manager */}
      {showLocationManager && (
        <UserLocationManager
          user={user}
          onLocationUpdate={(location) => {
            if (location) {
              setUserLocation(location);
              localStorage.setItem('taillink_location', JSON.stringify(location));
              
              // Update last location (sync with MapView)
              updateLastLocation({
                lat: location.lat,
                lng: location.lng
              });
              
              // Reload feed with new location immediately
              console.log('🔄 Location updated, reloading feed with:', location);
              loadAnimals(location);
            } else {
              setUserLocation(null);
              localStorage.removeItem('taillink_location');
            }
            getUserLocation(); // Refresh user location
          }}
          onClose={() => setShowLocationManager(false)}
        />
      )}
    </BackgroundImage>
  );
}

// User Profile Page Component for URL routing
const UserProfilePage = () => {
  const { t, i18n } = useTranslation();
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { getBackgroundStyle } = useBackground();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadUser = async () => {
      if (!userId) return;
      
      setLoading(true);
      try {
        const response = await axios.get(`${API}/users/${userId}`);
        setUser(response.data);
        
        // Set page title and meta for sharing
        const displayName = response.data.full_name || response.data.username || 'User';
        document.title = `${displayName} | TailLink`;
        
        // Add meta tags for social sharing
        updateMetaTags({
          title: `${displayName} - โปรไฟล์ใน TailLink`,
          description: response.data.bio || `ดูโปรไฟล์ของ ${displayName} บน TailLink`,
          image: response.data.profile_image || '',
          url: window.location.href
        });
        
      } catch (error) {
        console.error('Error loading user:', error);
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, [userId, navigate]);

  const updateMetaTags = ({ title, description, image, url }) => {
    const updateMetaTag = (name, content) => {
      let meta = document.querySelector(`meta[name="${name}"]`) || 
                 document.querySelector(`meta[property="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(name.startsWith('og:') ? 'property' : 'name', name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    updateMetaTag('description', description);
    updateMetaTag('og:title', title);
    updateMetaTag('og:description', description);
    updateMetaTag('og:image', image);
    updateMetaTag('og:url', url);
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', image);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={getBackgroundStyle()}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={getBackgroundStyle()}>
        <div className="text-center">
          <div className="text-6xl mb-4">👤</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('alerts.user_not_found')}</h2>
          <p className="text-gray-600 mb-4">{t('alerts.user_not_found_desc')}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700"
          >
            {t('nav.back_to_home')}
          </button>
        </div>
      </div>
    );
  }

  const displayName = user.full_name || user.username || 'User';

  return (
    <div className="min-h-screen" style={getBackgroundStyle()}>
      {/* Navigation */}
      <nav className="bg-white/95 shadow-sm border-b backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="text-blue-600 hover:text-blue-800 flex items-center space-x-2"
              >
                <img src="/taillink-logo.png" alt="TailLink" className="h-12 w-auto object-contain" />
                <span className="fur-heading font-bold text-2xl">TailLink</span>
              </button>
              <span className="text-gray-400">|</span>
              <span className="text-gray-600">{t('settings.user_profile')}</span>
            </div>
            
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigator.share ? 
                  navigator.share({
                    title: `${displayName} - โปรไฟล์ใน TailLink`,
                    text: user.bio || `ดูโปรไฟล์ของ ${displayName} บน TailLink`,
                    url: window.location.href
                  }) : 
                  navigator.clipboard.writeText(window.location.href).then(() => alert(t('alerts.link_copied')))
                }
                className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center space-x-2"
              >
                <img src="/share-icon.png" alt="Share" className="w-5 h-5" />
                <span className="hidden xs:inline">{t('common.share')}</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* User Profile Content */}
      <div className="max-w-4xl mx-auto py-8 px-4 pb-20">
        <UserProfile
          userId={userId}
          currentUser={currentUser}
          onBack={() => navigate('/')}
        />
      </div>

      {/* Bottom Navigation Bar - All Screen Sizes */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-gray-200 shadow-lg z-[2000]">
        <div className="flex justify-around items-center h-16">
          <button
            onClick={() => navigate('/?view=map')}
            className="flex flex-col items-center justify-center flex-1 h-full transition-colors text-gray-600 hover:text-blue-600"
          >
            <span className="text-2xl mb-1">🗺️</span>
            <span className="text-xs font-medium">Map</span>
          </button>
          
          <button
            onClick={() => navigate('/?view=feed')}
            className="flex flex-col items-center justify-center flex-1 h-full transition-colors text-gray-600 hover:text-blue-600"
          >
            <span className="text-2xl mb-1">📋</span>
            <span className="text-xs font-medium">Feed</span>
          </button>
          
          <button
            onClick={() => {
              if (currentUser) {
                navigate(`/user/${currentUser.id}`);
              } else {
                navigate('/');
              }
            }}
            className="flex flex-col items-center justify-center flex-1 h-full transition-colors text-gray-600 hover:text-blue-600"
          >
            <span className="text-2xl mb-1">👤</span>
            <span className="text-xs font-medium">Profile</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// Notification Settings Component
const NotificationSettings = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { permission, isSubscribed, settings, loading, requestPermission, unsubscribe, updateSettings, sendTestNotification } = useNotifications();
  const { user } = useAuth();
  const [localSettings, setLocalSettings] = useState(settings);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  const handleEnableNotifications = async () => {
    const granted = await requestPermission();
    if (granted) {
      alert(t('alerts.notification_enabled'));
    } else {
      alert(t('alerts.notification_enable_error'));
    }
  };

  const handleDisableNotifications = async () => {
    if (window.confirm(t('alerts.notification_disable_confirm'))) {
      await unsubscribe();
      alert(t('alerts.notification_disabled'));
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await updateSettings(localSettings);
      alert(t('alerts.settings_saved'));
    } catch (error) {
      alert(t('alerts.settings_save_error'));
    } finally {
      setSaving(false);
    }
  };

  const handleTestNotification = () => {
    if (permission === 'granted') {
      sendTestNotification();
      alert(t('alerts.test_notification_sent'));
    } else {
      alert(t('alerts.enable_notification_first'));
    }
  };

  // ถ้าไม่ได้ login ให้แสดงแค่ Language settings
  if (!user) {
    return (
      <div className="space-y-6">
        {/* Header with Close Button */}
        <div className="bg-white/90 backdrop-blur-md rounded-lg shadow-md p-6 relative">
          <button
            onClick={() => navigate('/')}
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full p-2 transition-colors"
            title={t('common.close')}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">⚙️ {t('nav.settings')}</h2>
          <p className="text-gray-600">{t('settings.subtitle')}</p>
        </div>

        {/* Language Settings Section */}
        <div className="bg-white/90 backdrop-blur-md rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4">🌍 {t('settings.language')}</h3>
          <LanguageSelector />
        </div>

        {/* Login prompt for notifications */}
        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg shadow-md p-8 text-center">
          <div className="text-6xl mb-4">🔔</div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">{t('notifications.title')}</h3>
          <p className="text-gray-600 mb-4">{t('notifications.login_required')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Close Button */}
      <div className="bg-white/90 backdrop-blur-md rounded-lg shadow-md p-6 relative">
        <button
          onClick={() => navigate('/')}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full p-2 transition-colors"
          title={t('common.close')}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">⚙️ {t('nav.settings')}</h2>
        <p className="text-gray-600">{t('settings.subtitle')}</p>
      </div>

      {/* Language Settings Section */}
      <div className="bg-white/90 backdrop-blur-md rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4">🌍 {t('settings.language')}</h3>
        <LanguageSelector />
      </div>

      {/* Permission Status */}
      <div className="bg-white/90 backdrop-blur-md rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4">{t('notifications.status')}</h3>
        
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className={`w-3 h-3 rounded-full ${permission === 'granted' && isSubscribed ? 'bg-green-500' : 'bg-gray-400'}`}></div>
            <div>
              <p className="font-medium">
                {permission === 'granted' && isSubscribed ? `✅ ${t('notifications.enabled')}` : 
                 permission === 'granted' && !isSubscribed ? `⏸️ ปิดการแจ้งเตือนแล้ว` :
                 permission === 'denied' ? `❌ ${t('notifications.disabled')}` : 
                 `⚠️ ${t('notifications.not_enabled')}`}
              </p>
              <p className="text-sm text-gray-600">
                {permission === 'granted' && isSubscribed ? 'กำลังรับการแจ้งเตือนอยู่' : 
                 permission === 'granted' && !isSubscribed ? 'คลิกปุ่มด้านล่างเพื่อเปิดใช้งานอีกครั้ง' :
                 permission === 'denied' ? 'กรุณาเปิดในการตั้งค่าเบราว์เซอร์' :
                 'คลิกปุ่มด้านล่างเพื่อเปิดใช้งาน'}
              </p>
            </div>
          </div>
        </div>

        {permission !== 'granted' && (
          <button
            onClick={handleEnableNotifications}
            disabled={loading || permission === 'denied'}
            className={`w-full py-3 px-6 rounded-lg font-semibold text-white transition-all ${
              loading || permission === 'denied'
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {loading ? '🔄 กำลังเปิดการแจ้งเตือน...' : '🔔 เปิดการแจ้งเตือน'}
          </button>
        )}

        {permission === 'granted' && !isSubscribed && (
          <button
            onClick={handleEnableNotifications}
            disabled={loading}
            className={`w-full py-3 px-6 rounded-lg font-semibold text-white transition-all ${
              loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {loading ? '🔄 กำลังเปิดการแจ้งเตือน...' : '🔔 เปิดการแจ้งเตือนอีกครั้ง'}
          </button>
        )}

        {permission === 'granted' && isSubscribed && (
          <div className="space-y-2">
            <button
              onClick={handleTestNotification}
              className="w-full py-2 px-4 rounded-lg font-medium text-blue-600 border-2 border-blue-600 hover:bg-blue-50 transition-all flex items-center justify-center space-x-2"
            >
              <img src="/share-icon.png" alt="Send" className="w-5 h-5" />
              <span>ส่งการแจ้งเตือนทดสอบ</span>
            </button>
            <button
              onClick={handleDisableNotifications}
              className="w-full py-2 px-4 rounded-lg font-medium text-red-600 border-2 border-red-600 hover:bg-red-50 transition-all"
            >
              🔕 ปิดการแจ้งเตือน
            </button>
          </div>
        )}
      </div>

      {/* Notification Settings */}
      {permission === 'granted' && isSubscribed && (
        <div className="bg-white/90 backdrop-blur-md rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4">⚙️ ตั้งค่าการแจ้งเตือน</h3>
          
          <div className="space-y-4">
            {/* Urgent Animals */}
            <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg border-2 border-red-200">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-1">
                  <span className="text-xl">🚨</span>
                  <h4 className="font-semibold text-red-900">สัตว์ต้องการความช่วยเหลือด่วน</h4>
                </div>
                <p className="text-sm text-red-700">แจ้งเตือนเมื่อมีสัตว์ใกล้คุณต้องการความช่วยเหลือ</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer ml-4">
                <input
                  type="checkbox"
                  checked={localSettings.urgent_animals}
                  onChange={(e) => setLocalSettings({ ...localSettings, urgent_animals: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            {/* Followed Animals */}
            <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-1">
                  <span className="text-xl">💙</span>
                  <h4 className="font-semibold text-blue-900">สัตว์ที่ติดตาม</h4>
                </div>
                <p className="text-sm text-blue-700">แจ้งเตือนเมื่อมีโพสต์ใหม่เกี่ยวกับสัตว์ที่คุณติดตาม</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer ml-4">
                <input
                  type="checkbox"
                  checked={localSettings.followed_animals}
                  onChange={(e) => setLocalSettings({ ...localSettings, followed_animals: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            {/* Post Comments */}
            <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-1">
                  <span className="text-xl">💬</span>
                  <h4 className="font-semibold text-green-900">คอมเมนต์ในโพสต์</h4>
                </div>
                <p className="text-sm text-green-700">แจ้งเตือนเมื่อมีคนแสดงความคิดเห็นในโพสต์ของคุณ</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer ml-4">
                <input
                  type="checkbox"
                  checked={localSettings.post_comments}
                  onChange={(e) => setLocalSettings({ ...localSettings, post_comments: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>

          {/* Notification Radius */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <img src="/location-pin.png" alt="Location" className="w-5 h-5" />
              <h4 className="font-semibold text-gray-900">รัศมีการแจ้งเตือน</h4>
            </div>
            <p className="text-sm text-gray-600 mb-3">แจ้งเตือนเมื่อมีเหตุการณ์ในรัศมี (สำหรับสัตว์ต้องการความช่วยเหลือ)</p>
            
            <div className="flex items-center space-x-4">
              <input
                type="range"
                min="500"
                max="10000"
                step="500"
                value={localSettings.notification_radius}
                onChange={(e) => setLocalSettings({ ...localSettings, notification_radius: parseInt(e.target.value) })}
                className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
              <span className="text-lg font-bold text-blue-600 min-w-[80px]">
                {(localSettings.notification_radius / 1000).toFixed(1)} กม.
              </span>
            </div>
            
            <div className="flex justify-between text-xs text-gray-500 mt-2">
              <span>0.5 กม.</span>
              <span>10 กม.</span>
            </div>
          </div>

          {/* Save Button */}
          <button
            onClick={handleSaveSettings}
            disabled={saving}
            className={`w-full mt-6 py-3 px-6 rounded-lg font-semibold text-white transition-all ${
              saving
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {saving ? '🔄 กำลังบันทึก...' : '💾 บันทึกการตั้งค่า'}
          </button>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 mb-2">💡 {t('notifications.info_title')}</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• {t('notifications.info_1')}</li>
          <li>• {t('notifications.info_2')}</li>
          <li>• {t('notifications.info_3')}</li>
          <li>• {t('notifications.info_4')}</li>
        </ul>
      </div>
    </div>
  );
};

// Post Detail Page Component for URL routing
const PostDetailPage = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { getBackgroundStyle, getContentOpacity } = useBackground();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const opacity = getContentOpacity();
  const cardClass = `backdrop-blur-md rounded-lg shadow-sm border`;
  const cardStyle = { backgroundColor: `rgba(255, 255, 255, ${opacity})` };

  useEffect(() => {
    const loadPost = async () => {
      if (!postId) return;
      
      setLoading(true);
      try {
        // Use individual post endpoint instead of searching through all posts
        const response = await axios.get(`${API}/posts/${postId}`);
        const foundPost = response.data;
        
        if (foundPost) {
          setPost(foundPost);
          
          // Set page title and meta for sharing
          const postTitle = foundPost.content ? foundPost.content.substring(0, 50) + '...' : 'โพสต์';
          document.title = `${postTitle} | TailLink`;
          
          // Add meta tags for social sharing
          updateMetaTags({
            title: `โพสต์โดย ${foundPost.created_by_name || 'User'} - TailLink`,
            description: foundPost.content || 'ดูโพสต์นี้บน TailLink',
            image: foundPost.images && foundPost.images.length > 0 ? 
                   `${API}/posts/${postId}/social-image` : '',
            url: window.location.href
          });
        } else {
          navigate('/');
        }
        
      } catch (error) {
        console.error('Error loading post:', error);
        // Only navigate away if it's a 404, not other errors
        if (error.response && error.response.status === 404) {
          navigate('/');
        } else {
          // For other errors, show error state but don't redirect
          setPost(null);
        }
      } finally {
        setLoading(false);
      }
    };

    loadPost();
  }, [postId, navigate]);

  const updateMetaTags = ({ title, description, image, url }) => {
    const updateMetaTag = (name, content) => {
      let meta = document.querySelector(`meta[name="${name}"]`) || 
                 document.querySelector(`meta[property="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(name.startsWith('og:') ? 'property' : 'name', name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    updateMetaTag('description', description);
    updateMetaTag('og:title', title);
    updateMetaTag('og:description', description);
    updateMetaTag('og:image', image);
    updateMetaTag('og:url', url);
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', image);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={getBackgroundStyle()}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('common.loading_posts')}</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={getBackgroundStyle()}>
        <div className="text-center">
          <div className="text-6xl mb-4">📝</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">ไม่พบโพสต์</h2>
          <p className="text-gray-600 mb-4">โพสต์ที่คุณต้องการดูอาจไม่มีอยู่</p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700"
          >
            {t('nav.back_to_home')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={getBackgroundStyle()}>
      {/* Navigation */}
      <div className="bg-white/95 shadow-sm border-b backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/')}
              className="text-blue-600 hover:text-blue-700 flex items-center space-x-2"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>{t('nav.back_to_home')}</span>
            </button>
          </div>
          <h1 className="text-xl font-bold text-gray-800">{t('nav.posts')}</h1>
          <div className="w-20"></div>
        </div>
      </div>

      {/* Post Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className={`${cardClass} overflow-hidden`} style={cardStyle}>
          {/* Post Header */}
          <div className="p-6 border-b">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                <User className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 cursor-pointer hover:text-blue-600"
                    onClick={() => navigate(`/user/${post.created_by_id}`)}>
                  {post.created_by_name || 'User'}
                </h3>
                <p className="text-sm text-gray-500">
                  {new Date(post.created_at).toLocaleDateString(getLocaleCode(i18n.language), {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </div>
          </div>

          {/* Post Content */}
          <div className="p-6">
            <p className="text-gray-800 text-lg leading-relaxed mb-6">{post.content}</p>
            
            {/* Post Images */}
            {post.images && post.images.length > 0 && (
              <div className="space-y-4">
                {post.images.map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={`t('forms.upload_photos') ${index + 1}`}
                    className="w-full rounded-lg shadow-sm"
                  />
                ))}
              </div>
            )}
          </div>

          {/* Post Actions */}
          <div className="px-6 py-4 border-t bg-gray-50">
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2 text-gray-600">
                <Heart className="w-5 h-5" />
                <span>{post.likes || 0} ถูกใจ</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-600">
                <MessageCircle className="w-5 h-5" />
                <span>{post.comments ? post.comments.length : 0} ความคิดเห็น</span>
              </div>
              <button
                onClick={() => {
                  if (navigator.share) {
                    navigator.share({
                      title: `โพสต์โดย ${post.created_by_name || 'User'}`,
                      text: post.content,
                      url: window.location.href,
                    });
                  } else {
                    navigator.clipboard.writeText(window.location.href);
                    alert(t('alerts.link_copied'));
                  }
                }}
                className="flex items-center space-x-2 text-gray-600 hover:text-blue-600"
              >
                <Share2 className="w-5 h-5" />
                <span>แชร์</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Map Page Component for URL routing
const MapPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getBackgroundStyle, getContentOpacity } = useBackground();
  
  const opacity = getContentOpacity();
  const cardClass = `backdrop-blur-md rounded-lg shadow-sm border`;
  const cardStyle = { backgroundColor: `rgba(255, 255, 255, ${opacity})` };

  return (
    <div className="min-h-screen" style={getBackgroundStyle()}>
      {/* Navigation */}
      <div className="bg-white/95 shadow-sm border-b backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/')}
              className="text-blue-600 hover:text-blue-700 flex items-center space-x-2"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>{t('nav.back_to_home')}</span>
            </button>
          </div>
          <h1 className="text-xl font-bold text-gray-800">{t('nav.map')}</h1>
          <div className="w-20"></div>
        </div>
      </div>

      {/* Map Integration will go here */}
      <div className="max-w-6xl mx-auto px-4 py-8 pb-20">
        <div className={`${cardClass} p-8 text-center`} style={cardStyle}>
          <div className="text-6xl mb-4">🗺️</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">แผนที่สัตว์</h2>
          <p className="text-gray-600 mb-6">
            ดูตำแหน่งสัตว์ทั้งหมดในพื้นที่ใกล้เคียง
          </p>
          <p className="text-sm text-gray-500">
            กรุณาใช้ปุ่ม Map จากหน้า Home เพื่อดูแผนที่แบบเต็มหน้าจอ
          </p>
        </div>
      </div>

      {/* Bottom Navigation Bar - All Screen Sizes */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-gray-200 shadow-lg z-[2000]">
        <div className="flex justify-around items-center h-16">
          <button
            className="flex flex-col items-center justify-center flex-1 h-full transition-colors text-blue-600 bg-blue-50"
          >
            <span className="text-2xl mb-1">🗺️</span>
            <span className="text-xs font-medium">Map</span>
          </button>
          
          <button
            onClick={() => navigate('/?view=feed')}
            className="flex flex-col items-center justify-center flex-1 h-full transition-colors text-gray-600 hover:text-blue-600"
          >
            <span className="text-2xl mb-1">📋</span>
            <span className="text-xs font-medium">Feed</span>
          </button>
          
          <button
            onClick={() => navigate('/')}
            className="flex flex-col items-center justify-center flex-1 h-full transition-colors text-gray-600 hover:text-blue-600"
          >
            <span className="text-2xl mb-1">👤</span>
            <span className="text-xs font-medium">Profile</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// Feed Reload Settings Admin Component
const FeedReloadSettingsAdmin = () => {
  const [loading, setLoading] = useState(false);
  const [minDistanceMeters, setMinDistanceMeters] = useState(50);
  const [minTimeMinutes, setMinTimeMinutes] = useState(5);
  const [saved, setSaved] = useState(false);

  const API_BASE = API;

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE}/settings/feed-reload`);
      setMinDistanceMeters(response.data.min_distance_meters);
      setMinTimeMinutes(response.data.min_time_minutes);
    } catch (error) {
      console.error('Error loading feed reload settings:', error);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setSaved(false);
    
    try {
      const payload = {
        min_distance_meters: minDistanceMeters,
        min_time_minutes: minTimeMinutes
      };
      
      const response = await axios.put(`${API_BASE}/admin/settings/feed-reload`, payload);
      
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      alert('✅ Feed reload settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert(`❌ Error saving settings: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">⚙️ Feed Reload Settings</h2>
        <p className="text-gray-600">
          กำหนดเงื่อนไขการ reload feed อัตโนมัติเพื่อลดภาระ server และประหยัดทรัพยากร
        </p>
      </div>

      <div className="space-y-6">
        {/* Distance Setting */}
        <div className="border border-gray-200 rounded-lg p-4">
          <label className="block mb-2">
            <span className="text-lg font-semibold text-gray-800">📍 ระยะทางขั้นต่ำ (เมตร)</span>
            <p className="text-sm text-gray-600 mt-1">
              Feed จะ reload เมื่อผู้ใช้เคลื่อนที่เกินระยะทางนี้
            </p>
          </label>
          <div className="flex items-center space-x-4 mt-3">
            <input
              type="number"
              min="10"
              max="1000"
              step="10"
              value={minDistanceMeters}
              onChange={(e) => setMinDistanceMeters(parseInt(e.target.value))}
              className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
            <span className="text-gray-700">เมตร</span>
            <input
              type="range"
              min="10"
              max="1000"
              step="10"
              value={minDistanceMeters}
              onChange={(e) => setMinDistanceMeters(parseInt(e.target.value))}
              className="flex-1"
            />
          </div>
          <p className="text-sm text-gray-500 mt-2">
            ค่าปัจจุบัน: <strong>{minDistanceMeters} เมตร</strong> (แนะนำ: 50-200 เมตร)
          </p>
        </div>

        {/* Time Setting */}
        <div className="border border-gray-200 rounded-lg p-4">
          <label className="block mb-2">
            <span className="text-lg font-semibold text-gray-800">⏰ เวลาขั้นต่ำ (นาที)</span>
            <p className="text-sm text-gray-600 mt-1">
              Feed จะ reload เมื่อเวลาผ่านไปเกินกำหนดนี้ (ถึงแม้ไม่ได้เคลื่อนที่)
            </p>
          </label>
          <div className="flex items-center space-x-4 mt-3">
            <input
              type="number"
              min="1"
              max="60"
              step="1"
              value={minTimeMinutes}
              onChange={(e) => setMinTimeMinutes(parseInt(e.target.value))}
              className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
            <span className="text-gray-700">นาที</span>
            <input
              type="range"
              min="1"
              max="60"
              step="1"
              value={minTimeMinutes}
              onChange={(e) => setMinTimeMinutes(parseInt(e.target.value))}
              className="flex-1"
            />
          </div>
          <p className="text-sm text-gray-500 mt-2">
            ค่าปัจจุบัน: <strong>{minTimeMinutes} นาที</strong> (แนะนำ: 3-10 นาที)
          </p>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-semibold text-blue-800 mb-2">ℹ️ วิธีการทำงาน</h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Feed จะ reload เมื่อเข้าเงื่อนไขใดเงื่อนไขหนึ่ง (OR logic)</li>
            <li>• เคลื่อนที่เกิน {minDistanceMeters} เมตร หรือ</li>
            <li>• เวลาผ่านไปเกิน {minTimeMinutes} นาที</li>
            <li>• ระบบตรวจสอบเงื่อนไขทุก 10 วินาที</li>
          </ul>
        </div>

        {/* Save Button */}
        <div className="flex items-center justify-end space-x-4">
          {saved && (
            <span className="text-green-600 font-medium">✅ บันทึกสำเร็จ!</span>
          )}
          <button
            onClick={handleSave}
            disabled={loading}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
          >
            {loading ? '🔄 กำลังบันทึก...' : '💾 บันทึกการตั้งค่า'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Background Settings Admin Component
// Map Settings Admin Component
const MapSettingsAdmin = () => {
  const { t } = useTranslation();
  
  // Admin Testing Settings (Zoom 1-18)
  const [adminZoom, setAdminZoom] = useState(16);
  const [adminCluster, setAdminCluster] = useState(1.0);
  const [adminRefresh, setAdminRefresh] = useState(10);
  
  // User Default Settings (Zoom 10-18)
  const [userZoom, setUserZoom] = useState(16);
  const [userCluster, setUserCluster] = useState(1.0);
  const [userRefresh, setUserRefresh] = useState(10);
  
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  // Load settings from backend so values sync across devices/environments
  useEffect(() => {
    const loadMapSettings = async () => {
      try {
        const response = await axios.get(`${API}/v2/settings/map`);
        const admin = response.data?.admin || {};
        const user = response.data?.user || {};
        setAdminZoom(admin.zoom || 16);
        setAdminCluster(admin.cluster || 1.0);
        setAdminRefresh(admin.refresh || 10);
        setUserZoom(user.zoom || 16);
        setUserCluster(user.cluster || 1.0);
        setUserRefresh(user.refresh || 10);
      } catch (error) {
        console.error('Error loading map settings:', error);
      }
    };
    loadMapSettings();
  }, []);

  const authConfig = () => {
    const token = localStorage.getItem('taillink_token');
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  };

  const saveMapSettings = async (scope, payload) => {
    const response = await axios.put(`${API}/v2/admin/settings/map/${scope}`, payload, authConfig());
    return response.data?.settings || payload;
  };

  const handleSaveAdmin = async () => {
    setLoading(true);
    try {
      await saveMapSettings('admin', { zoom: adminZoom, cluster: adminCluster, refresh: adminRefresh });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      alert('✅ บันทึกการตั้งค่า Admin Testing สำเร็จ!');
    } catch (error) {
      console.error('Error saving admin settings:', error);
      alert('❌ เกิดข้อผิดพลาดในการบันทึก');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveUser = async () => {
    setLoading(true);
    try {
      await saveMapSettings('user', { zoom: userZoom, cluster: userCluster, refresh: userRefresh });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      alert('✅ บันทึกการตั้งค่า User Default สำเร็จ!');
    } catch (error) {
      console.error('Error saving user settings:', error);
      alert('❌ เกิดข้อผิดพลาดในการบันทึก');
    } finally {
      setLoading(false);
    }
  };

  const handleResetAdmin = async () => {
    setAdminZoom(16);
    setAdminCluster(1.0);
    setAdminRefresh(10);
    try {
      await saveMapSettings('admin', { zoom: 16, cluster: 1.0, refresh: 10 });
      alert('✅ รีเซ็ตการตั้งค่า Admin Testing แล้ว');
    } catch (error) {
      console.error('Error resetting admin map settings:', error);
      alert('❌ เกิดข้อผิดพลาดในการรีเซ็ต');
    }
  };

  const handleResetUser = async () => {
    setUserZoom(16);
    setUserCluster(1.0);
    setUserRefresh(10);
    try {
      await saveMapSettings('user', { zoom: 16, cluster: 1.0, refresh: 10 });
      alert('✅ รีเซ็ตการตั้งค่า User Default แล้ว');
    } catch (error) {
      console.error('Error resetting user map settings:', error);
      alert('❌ เกิดข้อผิดพลาดในการรีเซ็ต');
    }
  };

  // Calculate actual radius values based on multiplier
  const getRadiusForZoom = (zoom, baseRadius, multiplier) => {
    return Math.round(baseRadius * multiplier);
  };

  return (
    <div className="space-y-6">
      {/* Admin Testing Settings Card */}
      <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg shadow-md p-6 border-2 border-orange-300">
        <div className="flex items-center space-x-3 mb-4">
          <span className="text-3xl">🧪</span>
          <div>
            <h3 className="text-xl font-bold text-orange-800">Admin Testing Settings</h3>
            <p className="text-sm text-orange-600">สำหรับ Admin ใช้เองทดสอบค่าต่างๆ</p>
          </div>
        </div>
        
        {/* Admin Zoom Level */}
        <div className="space-y-4">
          <div>
            <h4 className="font-semibold text-orange-800 mb-2">📏 ระดับ Zoom ขั้นต่ำ (Admin)</h4>
            <p className="text-sm text-orange-700 mb-4">
              Admin สามารถปรับ zoom ได้ตั้งแต่ระดับ 1-18
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-orange-800 mb-2">
                Zoom Level: <span className="text-orange-600 font-bold text-lg">{adminZoom}</span>
              </label>
              <input
                type="range"
                min="1"
                max="18"
                step="1"
                value={adminZoom}
                onChange={(e) => setAdminZoom(parseInt(e.target.value))}
                className="w-full h-2 bg-orange-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-orange-600 mt-1">
                <span>1 (ไกลสุด)</span>
                <span>9 (กลาง)</span>
                <span>18 (ใกล้สุด)</span>
              </div>
            </div>
          </div>

          {/* Admin Cluster Radius */}
          <div>
            <h4 className="font-semibold text-orange-800 mb-2">🎯 ระยะการรวมหมุด (Cluster Radius)</h4>
            <p className="text-sm text-orange-700 mb-4">
              ปรับระยะที่จะรวมหมุดสัตว์ที่อยู่ใกล้กัน
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-orange-800 mb-2">
                Multiplier: <span className="text-orange-600 font-bold text-lg">{adminCluster.toFixed(1)}x</span>
              </label>
              <input
                type="range"
                min="0.3"
                max="3.0"
                step="0.1"
                value={adminCluster}
                onChange={(e) => setAdminCluster(parseFloat(e.target.value))}
                className="w-full h-2 bg-orange-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-orange-600 mt-1">
                <span>0.3x</span>
                <span>1.0x</span>
                <span>3.0x</span>
              </div>
            </div>
          </div>

          {/* Admin Refresh Interval */}
          <div>
            <h4 className="font-semibold text-orange-800 mb-2">⏱️ Refresh Interval</h4>
            <p className="text-sm text-orange-700 mb-4">
              ระยะเวลาระหว่างการโหลดหมุดใหม่ (GPS Radar Mode)
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-orange-800 mb-2">
                ทุกๆ: <span className="text-orange-600 font-bold text-lg">{adminRefresh} วินาที</span>
              </label>
              <input
                type="range"
                min="3"
                max="30"
                step="1"
                value={adminRefresh}
                onChange={(e) => setAdminRefresh(parseInt(e.target.value))}
                className="w-full h-2 bg-orange-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-orange-600 mt-1">
                <span>3s</span>
                <span>15s</span>
                <span>30s</span>
              </div>
            </div>
          </div>

          {/* Action Buttons for Admin */}
          <div className="flex space-x-4 pt-4">
            <button
              onClick={handleSaveAdmin}
              disabled={loading}
              className="flex-1 bg-orange-600 text-white px-6 py-3 rounded-lg hover:bg-orange-700 disabled:opacity-50 font-medium shadow-md"
            >
              {loading ? '⏳ กำลังบันทึก...' : saved ? '✅ บันทึกแล้ว' : '💾 บันทึกการตั้งค่า Admin'}
            </button>
            <button
              onClick={handleResetAdmin}
              disabled={loading}
              className="px-6 py-3 border-2 border-orange-400 text-orange-700 rounded-lg hover:bg-orange-50 disabled:opacity-50 font-medium"
            >
              🔄 รีเซ็ต
            </button>
          </div>
        </div>
      </div>

      {/* User Default Settings Card */}
      <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg shadow-md p-6 border-2 border-blue-300">
        <div className="flex items-center space-x-3 mb-4">
          <span className="text-3xl">👥</span>
          <div>
            <h3 className="text-xl font-bold text-blue-800">User Default Settings</h3>
            <p className="text-sm text-blue-600">ค่า Default ที่ Admin กำหนดให้ User ทั้งหมดใช้</p>
          </div>
        </div>

        {/* User Zoom Level */}
        <div className="space-y-4">
          <div>
            <h4 className="font-semibold text-blue-800 mb-2">📏 ระดับ Zoom ขั้นต่ำ (User)</h4>
            <p className="text-sm text-blue-700 mb-4">
              User ทั่วไปสามารถปรับ zoom ได้ตั้งแต่ระดับ 10-18
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-blue-800 mb-2">
                Zoom Level: <span className="text-blue-600 font-bold text-lg">{userZoom}</span>
              </label>
              <input
                type="range"
                min="10"
                max="18"
                step="1"
                value={userZoom}
                onChange={(e) => setUserZoom(parseInt(e.target.value))}
                className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-blue-600 mt-1">
                <span>10 (ไกล)</span>
                <span>14 (กลาง)</span>
                <span>18 (ใกล้สุด)</span>
              </div>
            </div>
          </div>

          {/* User Cluster Radius */}
          <div>
            <h4 className="font-semibold text-blue-800 mb-2">🎯 ระยะการรวมหมุด (Cluster Radius)</h4>
            <p className="text-sm text-blue-700 mb-4">
              ปรับระยะที่จะรวมหมุดสัตว์ที่อยู่ใกล้กัน
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-blue-800 mb-2">
                Multiplier: <span className="text-blue-600 font-bold text-lg">{userCluster.toFixed(1)}x</span>
              </label>
              <input
                type="range"
                min="0.3"
                max="3.0"
                step="0.1"
                value={userCluster}
                onChange={(e) => setUserCluster(parseFloat(e.target.value))}
                className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-blue-600 mt-1">
                <span>0.3x</span>
                <span>1.0x</span>
                <span>3.0x</span>
              </div>
            </div>
          </div>

          {/* User Refresh Interval */}
          <div>
            <h4 className="font-semibold text-blue-800 mb-2">⏱️ Refresh Interval</h4>
            <p className="text-sm text-blue-700 mb-4">
              ระยะเวลาระหว่างการโหลดหมุดใหม่ (GPS Radar Mode)
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-blue-800 mb-2">
                ทุกๆ: <span className="text-blue-600 font-bold text-lg">{userRefresh} วินาที</span>
              </label>
              <input
                type="range"
                min="3"
                max="30"
                step="1"
                value={userRefresh}
                onChange={(e) => setUserRefresh(parseInt(e.target.value))}
                className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-blue-600 mt-1">
                <span>3s</span>
                <span>15s</span>
                <span>30s</span>
              </div>
            </div>
          </div>

          {/* Action Buttons for User */}
          <div className="flex space-x-4 pt-4">
            <button
              onClick={handleSaveUser}
              disabled={loading}
              className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium shadow-md"
            >
              {loading ? '⏳ กำลังบันทึก...' : saved ? '✅ บันทึกแล้ว' : '💾 บันทึกการตั้งค่า User'}
            </button>
            <button
              onClick={handleResetUser}
              disabled={loading}
              className="px-6 py-3 border-2 border-blue-400 text-blue-700 rounded-lg hover:bg-blue-50 disabled:opacity-50 font-medium"
            >
              🔄 รีเซ็ต
            </button>
          </div>
        </div>
      </div>

      {/* Info Section */}
      <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
        <h4 className="font-semibold text-gray-800 mb-3">📌 หมายเหตุสำคัญ</h4>
        <ul className="space-y-2 text-sm text-gray-700">
          <li className="flex items-start gap-2">
            <span className="text-orange-600">🧪</span>
            <div><strong>Admin Testing Settings:</strong> ใช้สำหรับ Admin ทดสอบค่าต่างๆ เมื่อ login เป็น Admin</div>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600">👥</span>
            <div><strong>User Default Settings:</strong> ค่าที่ User ทั่วไปจะใช้เป็น default</div>
          </li>
          <li className="flex items-start gap-2">
            <span>⚙️</span>
            <div><strong>การทำงาน:</strong> ระบบจะตรวจสอบบทบาทผู้ใช้และนำ settings ที่ถูกต้องมาใช้อัตโนมัติ</div>
          </li>
          <li className="flex items-start gap-2">
            <span>🔄</span>
            <div><strong>รีเฟรช:</strong> หลังบันทึก ต้อง refresh หน้า Map View เพื่อให้การตั้งค่ามีผล</div>
          </li>
        </ul>
      </div>
    </div>
  );
};

const BackgroundSettingsAdmin = () => {
  const { t, i18n } = useTranslation();
  const { backgroundSettings, loadBackgroundSettings, setBackgroundSettings } = useBackground();
  const [selectedType, setSelectedType] = useState('color');
  const [color, setColor] = useState('#f3f4f6');
  const [gradientStart, setGradientStart] = useState('#3b82f6');
  const [gradientEnd, setGradientEnd] = useState('#8b5cf6');
  const [gradientDirection, setGradientDirection] = useState('to bottom right');
  const [imageBase64, setImageBase64] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [contentOpacity, setContentOpacity] = useState(0.9);
  const [loading, setLoading] = useState(false);
  const [defaultBackgrounds, setDefaultBackgrounds] = useState([]);
  const [showPreview, setShowPreview] = useState(false);

  const API_BASE = API;

  useEffect(() => {
    loadDefaults();
    // Load current settings
    if (backgroundSettings) {
      setSelectedType(backgroundSettings.type || 'color');
      setColor(backgroundSettings.color || '#f3f4f6');
      setGradientStart(backgroundSettings.gradient_start || '#3b82f6');
      setGradientEnd(backgroundSettings.gradient_end || '#8b5cf6');
      setGradientDirection(backgroundSettings.gradient_direction || 'to bottom right');
      setImageBase64(backgroundSettings.image_base64 || '');
      setImageUrl(backgroundSettings.image_url || '');
      setContentOpacity(backgroundSettings.content_opacity || 0.9);
    }
  }, [backgroundSettings]);

  const loadDefaults = async () => {
    try {
      const response = await axios.get(`${API_BASE}/settings/background/defaults`);
      setDefaultBackgrounds(response.data);
    } catch (error) {
      console.error('Error loading defaults:', error);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert(t('alerts.file_too_large'));
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setImageBase64(reader.result);
      setImageUrl(''); // Clear URL if uploading file
    };
    reader.readAsDataURL(file);
  };

  const handleDefaultSelect = (defaultBg) => {
    setSelectedType(defaultBg.type);
    if (defaultBg.type === 'color') {
      setColor(defaultBg.color);
    } else if (defaultBg.type === 'gradient') {
      setGradientStart(defaultBg.gradient_start);
      setGradientEnd(defaultBg.gradient_end);
      setGradientDirection(defaultBg.gradient_direction);
    }
  };

  const getPreviewStyle = () => {
    switch (selectedType) {
      case 'color':
        return { backgroundColor: color };
      case 'gradient':
        return {
          background: `linear-gradient(${gradientDirection}, ${gradientStart}, ${gradientEnd})`
        };
      case 'image':
        if (imageBase64) {
          return {
            backgroundImage: `url(${imageBase64})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          };
        } else if (imageUrl) {
          return {
            backgroundImage: `url(${imageUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          };
        }
        return { backgroundColor: '#f3f4f6' };
      default:
        return { backgroundColor: '#f3f4f6' };
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const payload = {
        type: selectedType,
        color: selectedType === 'color' ? color : null,
        gradient_start: selectedType === 'gradient' ? gradientStart : null,
        gradient_end: selectedType === 'gradient' ? gradientEnd : null,
        gradient_direction: selectedType === 'gradient' ? gradientDirection : null,
        image_base64: selectedType === 'image' ? imageBase64 : null,
        image_url: selectedType === 'image' ? imageUrl : null,
        content_opacity: contentOpacity
      };

      const response = await axios.put(`${API_BASE}/admin/settings/background`, payload);
      
      // Update context
      await loadBackgroundSettings();
      
      alert('บันทึก Background Settings สำเร็จ!');
    } catch (error) {
      console.error('Error saving background:', error);
      alert('เกิดข้อผิดพลาดในการบันทึก: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">🎨 Background Settings</h2>
        <p className="text-gray-600">ปรับแต่ง Background ของทุกหน้าในเว็บไซต์</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Settings Panel */}
        <div className="space-y-6">
          {/* Type Selector */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold mb-4">t('admin.hero_type') Background</h3>
            <div className="grid grid-cols-3 gap-3">
              {['color', 'gradient', 'image'].map(type => (
                <button
                  key={type}
                  onClick={() => setSelectedType(type)}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    selectedType === type
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="text-center">
                    <div className="text-3xl mb-2">
                      {type === 'color' ? '🎨' : type === 'gradient' ? '🌈' : '🖼️'}
                    </div>
                    <div className="text-sm font-medium">
                      {type === 'color' ? 'Solid Color' : type === 'gradient' ? 'Gradient' : t('forms.upload_photos')}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Color Picker */}
          {selectedType === 'color' && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold mb-4">เลือกสี</h3>
              <div className="space-y-4">
                <input
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="w-full h-16 rounded-lg cursor-pointer border-2 border-gray-200"
                />
                <input
                  type="text"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  placeholder="#ffffff"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
          )}

          {/* Gradient Picker */}
          {selectedType === 'gradient' && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold mb-4">ตั้งค่า Gradient</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">สีเริ่มต้น</label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={gradientStart}
                      onChange={(e) => setGradientStart(e.target.value)}
                      className="w-16 h-10 rounded cursor-pointer border-2 border-gray-200"
                    />
                    <input
                      type="text"
                      value={gradientStart}
                      onChange={(e) => setGradientStart(e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">สีสิ้นสุด</label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={gradientEnd}
                      onChange={(e) => setGradientEnd(e.target.value)}
                      className="w-16 h-10 rounded cursor-pointer border-2 border-gray-200"
                    />
                    <input
                      type="text"
                      value={gradientEnd}
                      onChange={(e) => setGradientEnd(e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">ทิศทาง</label>
                  <select
                    value={gradientDirection}
                    onChange={(e) => setGradientDirection(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="to bottom">บนลงล่าง</option>
                    <option value="to right">ซ้ายไปขวา</option>
                    <option value="to bottom right">บนซ้ายไปล่างขวา</option>
                    <option value="to bottom left">บนขวาไปล่างซ้าย</option>
                    <option value="to top">ล่างขึ้นบน</option>
                    <option value="to left">ขวาไปซ้าย</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Image Upload */}
          {selectedType === 'image' && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold mb-4">Upload Image</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('forms.select_file_max_5mb')}
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div className="text-center text-gray-500">Or</div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Image URL</label>
                  <input
                    type="url"
                    value={imageUrl}
                    onChange={(e) => {
                      setImageUrl(e.target.value);
                      setImageBase64(''); // Clear base64 if using URL
                    }}
                    placeholder="https://example.com/image.jpg"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                {(imageBase64 || imageUrl) && (
                  <div className="mt-4">
                    <img
                      src={imageBase64 || imageUrl}
                      alt="Preview"
                      className="w-full h-48 object-cover rounded-lg border-2 border-gray-200"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Default Backgrounds */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold mb-4">ตัวเลือกสำเร็จรูป</h3>
            <div className="grid grid-cols-2 gap-3">
              {defaultBackgrounds.map((bg) => (
                <button
                  key={bg.id}
                  onClick={() => handleDefaultSelect(bg)}
                  className="p-3 rounded-lg border-2 border-gray-200 hover:border-blue-500 transition-all"
                >
                  <div
                    className="w-full h-24 rounded-lg mb-2"
                    style={
                      bg.type === 'color'
                        ? { backgroundColor: bg.color }
                        : { background: bg.thumbnail }
                    }
                  />
                  <div className="text-sm font-medium text-center">{bg.name}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Content Opacity Slider */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold mb-4">ความโปร่งใสของ Content Cards</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">ความทึบแสง (Opacity)</label>
                <span className="text-lg font-bold text-blue-600">{Math.round(contentOpacity * 100)}%</span>
              </div>
              
              <input
                type="range"
                min="0"
                max="100"
                value={contentOpacity * 100}
                onChange={(e) => setContentOpacity(e.target.value / 100)}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
              
              <div className="flex justify-between text-xs text-gray-500">
                <span>โปร่งใสมาก (0%)</span>
                <span>ทึบแสงสนิท (100%)</span>
              </div>

              {/* Preview Cards */}
              <div className="mt-4 space-y-2">
                <p className="text-sm text-gray-600 mb-2">ตัวอย่าง Content Card:</p>
                <div 
                  className="p-4 rounded-lg shadow-md backdrop-blur-md"
                  style={{ backgroundColor: `rgba(255, 255, 255, ${contentOpacity})` }}
                >
                  <h4 className="font-semibold text-gray-800 mb-2">ตัวอย่าง Card</h4>
                  <p className="text-gray-600 text-sm">
                    นี่คือตัวอย่างความโปร่งใสของ content card โดยมีค่า opacity {Math.round(contentOpacity * 100)}%
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <button
              onClick={handleSave}
              disabled={loading}
              className={`w-full py-3 px-6 rounded-lg font-semibold text-white transition-all ${
                loading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {loading ? '🔄 กำลังบันทึก...' : '💾 บันทึก Background Settings'}
            </button>
          </div>
        </div>

        {/* Preview Panel */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm p-6 sticky top-6">
            <h3 className="text-lg font-semibold mb-4">ตัวอย่าง Preview</h3>
            
            {/* Preview Box */}
            <div
              className="w-full h-96 rounded-lg border-4 border-gray-300 overflow-hidden relative"
              style={getPreviewStyle()}
            >
              {/* Sample Content */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div 
                  className="backdrop-blur-sm p-8 rounded-lg shadow-xl max-w-md"
                  style={{ backgroundColor: `rgba(255, 255, 255, ${contentOpacity})` }}
                >
                  <div className="flex items-center space-x-3 mb-2">
                    <img src="/taillink-logo.png" alt="TailLink" className="h-12 w-auto object-contain" />
                    <h1 className="text-3xl font-bold text-gray-900">TailLink</h1>
                  </div>
                  <p className="text-gray-600 mb-4">ชุมชนคนรักสัตว์ที่อบอุ่น</p>
                  <div className="space-y-2">
                    <div className="bg-blue-500 text-white px-4 py-2 rounded-lg text-center">
                      ตัวอย่างปุ่ม
                    </div>
                    <div className="bg-gray-100 px-4 py-2 rounded-lg">
                      ตัวอย่างข้อความ
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Info */}
            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                💡 <strong>คำแนะนำ:</strong> Background นี้จะถูกนำไปใช้กับทุกหน้าในเว็บไซต์
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Data Migration Panel Component
const DataMigrationPanel = () => {
  const [migrationStatus, setMigrationStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [results, setResults] = useState(null);

  const loadMigrationStatus = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/data-migration/status`);
      setMigrationStatus(response.data);
    } catch (error) {
      console.error('Error loading migration status:', error);
      alert(`เกิดข้อผิดพลาด: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleFixData = async () => {
    if (!confirm('คุณแน่ใจหรือไม่ที่จะแก้ไขข้อมูลสัตว์ทั้งหมด?\n\nระบบจะเพิ่ม status และ coordinates ให้กับสัตว์ที่ยังไม่มีข้อมูลเหล่านี้')) {
      return;
    }

    setFixing(true);
    setResults(null);
    
    try {
      const response = await axios.post(`${API}/admin/data-migration/fix`);
      setResults(response.data);
      alert('✅ แก้ไขข้อมูลเสร็จสมบูรณ์!');
      
      // Reload status after fix
      await loadMigrationStatus();
    } catch (error) {
      console.error('Error fixing data:', error);
      alert(`❌ เกิดข้อผิดพลาด: ${error.response?.data?.detail || error.message}`);
    } finally {
      setFixing(false);
    }
  };

  useEffect(() => {
    loadMigrationStatus();
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">กำลังตรวจสอบข้อมูล...</span>
        </div>
      </div>
    );
  }

  if (!migrationStatus) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8">
        <p className="text-red-600">ไม่สามารถโหลดข้อมูลได้</p>
      </div>
    );
  }

  const needsMigration = migrationStatus.needs_migration;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">🔧 Data Migration Tool</h2>
            <p className="text-gray-600 mt-1">แก้ไขข้อมูลสัตว์ให้เป็นมาตรฐานเดียวกัน เพื่อแสดงผลใน Map View</p>
          </div>
          <button
            onClick={loadMigrationStatus}
            disabled={loading}
            className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 disabled:opacity-50"
          >
            🔄 รีเฟรช
          </button>
        </div>
      </div>

      {/* Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">สัตว์ทั้งหมด</p>
              <p className="text-3xl font-bold text-gray-900">{migrationStatus.total_animals}</p>
            </div>
            <div className="text-4xl">🐾</div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">พร้อมแสดงบน Map</p>
              <p className="text-3xl font-bold text-green-600">{migrationStatus.map_ready}</p>
              <p className="text-xs text-gray-500 mt-1">
                {Math.round((migrationStatus.map_ready / migrationStatus.total_animals) * 100)}%
              </p>
            </div>
            <div className="text-4xl">✅</div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">ต้องแก้ไข</p>
              <p className="text-3xl font-bold text-red-600">
                {migrationStatus.missing_status + migrationStatus.has_location_no_coords}
              </p>
              <p className="text-xs text-gray-500 mt-1">สัตว์</p>
            </div>
            <div className="text-4xl">{needsMigration ? '⚠️' : '✅'}</div>
          </div>
        </div>
      </div>

      {/* Issues Detail */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">📋 รายละเอียดปัญหา</h3>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">
                {migrationStatus.missing_status > 0 ? '❌' : '✅'}
              </span>
              <div>
                <p className="font-medium text-gray-900">สัตว์ที่ไม่มี Status field</p>
                <p className="text-sm text-gray-500">จำเป็นต้องมี status = "active" เพื่อแสดงผลบน map</p>
              </div>
            </div>
            <span className={`text-2xl font-bold ${migrationStatus.missing_status > 0 ? 'text-red-600' : 'text-green-600'}`}>
              {migrationStatus.missing_status}
            </span>
          </div>

          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">
                {migrationStatus.has_location_no_coords > 0 ? '❌' : '✅'}
              </span>
              <div>
                <p className="font-medium text-gray-900">สัตว์ที่ไม่มี location.coordinates</p>
                <p className="text-sm text-gray-500">จำเป็นสำหรับ geospatial query ในการค้นหาบน map</p>
              </div>
            </div>
            <span className={`text-2xl font-bold ${migrationStatus.has_location_no_coords > 0 ? 'text-red-600' : 'text-green-600'}`}>
              {migrationStatus.has_location_no_coords}
            </span>
          </div>
        </div>
      </div>

      {/* Status Distribution */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">📊 การกระจาย Status</h3>
        <div className="space-y-2">
          {Object.entries(migrationStatus.status_distribution).map(([status, count]) => (
            <div key={status} className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <span className="font-medium text-gray-700">
                {status === 'active' ? '✅ Active' : 
                 status === 'merged' ? '🔗 Merged' :
                 status === 'null/missing' ? '❌ Null/Missing' : status}
              </span>
              <span className="font-bold text-gray-900">{count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Problematic Samples */}
      {migrationStatus.problematic_samples.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h3 className="text-lg font-bold text-yellow-900 mb-4">⚠️ ตัวอย่างสัตว์ที่มีปัญหา (5 ตัวแรก)</h3>
          <div className="space-y-3">
            {migrationStatus.problematic_samples.map((sample, idx) => (
              <div key={sample.id} className="bg-white p-4 rounded border border-yellow-300">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{idx + 1}. {sample.name}</p>
                    <p className="text-xs text-gray-500 mt-1">ID: {sample.id.substring(0, 12)}...</p>
                    <div className="flex gap-2 mt-2">
                      {sample.issues.map((issue) => (
                        <span key={issue} className="inline-block px-2 py-1 text-xs bg-red-100 text-red-700 rounded">
                          {issue === 'missing_status' ? '❌ No Status' : '❌ No Coordinates'}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Fix Button */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        {needsMigration ? (
          <div className="text-center">
            <div className="mb-4">
              <p className="text-lg font-medium text-gray-900 mb-2">
                🔧 พบสัตว์ {migrationStatus.missing_status + migrationStatus.has_location_no_coords} ตัวที่ต้องแก้ไข
              </p>
              <p className="text-sm text-gray-600">
                ระบบจะเพิ่ม status="active" และ location.coordinates ให้กับสัตว์ที่ยังไม่มีข้อมูลเหล่านี้
              </p>
            </div>
            
            <button
              onClick={handleFixData}
              disabled={fixing}
              className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-lg font-medium"
            >
              {fixing ? (
                <>
                  <span className="inline-block animate-spin mr-2">⚙️</span>
                  กำลังแก้ไข...
                </>
              ) : (
                <>
                  🔧 แก้ไขข้อมูลทั้งหมด
                </>
              )}
            </button>
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="text-6xl mb-4">🎉</div>
            <p className="text-2xl font-bold text-green-600 mb-2">ข้อมูลเรียบร้อยแล้ว!</p>
            <p className="text-gray-600">สัตว์ทั้งหมดพร้อมแสดงผลบน Map View</p>
          </div>
        )}
      </div>

      {/* Results */}
      {results && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <h3 className="text-lg font-bold text-green-900 mb-4">✅ ผลลัพธ์การแก้ไข</h3>
          <div className="space-y-2">
            <p className="text-green-800">
              <span className="font-medium">✅ เพิ่ม Status:</span> {results.results.status_fixed} สัตว์
            </p>
            <p className="text-green-800">
              <span className="font-medium">✅ เพิ่ม Coordinates:</span> {results.results.coordinates_fixed} สัตว์
            </p>
            {results.results.index_created && (
              <p className="text-green-800">
                <span className="font-medium">✅ สร้าง Geospatial Index:</span> สำเร็จ
              </p>
            )}
            {results.results.errors && results.results.errors.length > 0 && (
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded">
                <p className="font-medium text-yellow-900 mb-2">⚠️ ข้อผิดพลาดบางส่วน:</p>
                <ul className="list-disc list-inside text-sm text-yellow-800">
                  {results.results.errors.map((error, idx) => (
                    <li key={idx}>
                      {error.animal_name || error.type}: {error.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Announcements Admin Component
const AnnouncementsAdmin = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', content: '', image_url: '', image_base64: '', button_text: '', button_url: '', priority: 0, show_once: true, is_active: true });
  const [saving, setSaving] = useState(false);

  const loadAnnouncements = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/announcements`);
      setAnnouncements(res.data.announcements || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAnnouncements(); }, [loadAnnouncements]);

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setForm(prev => ({ ...prev, image_base64: reader.result, image_url: '' }));
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!form.title || !form.content) return;
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/admin/announcements/${editing}`, form);
      } else {
        await axios.post(`${API}/admin/announcements`, form);
      }
      setShowForm(false);
      setEditing(null);
      setForm({ title: '', content: '', image_url: '', image_base64: '', button_text: '', button_url: '', priority: 0, show_once: true, is_active: true });
      loadAnnouncements();
    } catch (e) { alert('เกิดข้อผิดพลาด'); }
    finally { setSaving(false); }
  };

  const handleEdit = (ann) => {
    setForm({
      title: ann.title, content: ann.content,
      image_url: ann.image_url || '', image_base64: ann.image_base64 || '',
      button_text: ann.button_text || '', button_url: ann.button_url || '',
      priority: ann.priority || 0, show_once: ann.show_once !== false, is_active: ann.is_active !== false,
    });
    setEditing(ann.id);
    setShowForm(true);
  };

  const handleToggle = async (id) => {
    try {
      await axios.post(`${API}/admin/announcements/${id}/toggle`);
      loadAnnouncements();
    } catch { /* ignore */ }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('ลบประกาศนี้?')) return;
    try {
      await axios.delete(`${API}/admin/announcements/${id}`);
      loadAnnouncements();
    } catch { /* ignore */ }
  };

  return (
    <div className="space-y-4" data-testid="admin-announcements">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-bold text-gray-800">📢 จัดการประกาศ / Popup</h2>
        <button
          onClick={() => { setShowForm(true); setEditing(null); setForm({ title: '', content: '', image_url: '', image_base64: '', button_text: '', button_url: '', priority: 0, show_once: true, is_active: true }); }}
          className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-semibold hover:bg-orange-600"
          data-testid="create-announcement-btn"
        >
          + สร้างประกาศใหม่
        </button>
      </div>

      {/* Create/Edit Form */}
      {showForm && (
        <div className="bg-white rounded-lg shadow-sm border p-6" data-testid="announcement-form">
          <h3 className="font-bold mb-4">{editing ? 'แก้ไขประกาศ' : 'สร้างประกาศใหม่'}</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">หัวข้อ *</label>
              <input className="w-full px-3 py-2 border rounded-lg text-sm" value={form.title} onChange={(e) => setForm(p => ({...p, title: e.target.value}))} placeholder="หัวข้อประกาศ" data-testid="ann-form-title" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">เนื้อหา *</label>
              <textarea className="w-full px-3 py-2 border rounded-lg text-sm min-h-[100px]" value={form.content} onChange={(e) => setForm(p => ({...p, content: e.target.value}))} placeholder="เนื้อหาประกาศ (รองรับขึ้นบรรทัดใหม่)" data-testid="ann-form-content" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">รูปภาพ</label>
              <div className="flex gap-2 items-center">
                <input className="flex-1 px-3 py-2 border rounded-lg text-sm" value={form.image_url} onChange={(e) => setForm(p => ({...p, image_url: e.target.value, image_base64: ''}))} placeholder="URL รูปภาพ (หรืออัปโหลดด้านล่าง)" />
                <label className="px-3 py-2 bg-gray-100 border rounded-lg text-sm cursor-pointer hover:bg-gray-200">
                  อัปโหลด
                  <input type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
                </label>
              </div>
              {(form.image_base64 || form.image_url) && (
                <img src={form.image_base64 || form.image_url} alt="preview" className="mt-2 w-40 h-24 object-cover rounded-lg" />
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ข้อความปุ่ม CTA</label>
                <input className="w-full px-3 py-2 border rounded-lg text-sm" value={form.button_text} onChange={(e) => setForm(p => ({...p, button_text: e.target.value}))} placeholder="เช่น ดูเพิ่มเติม" data-testid="ann-form-btn-text" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">URL ปุ่ม CTA</label>
                <input className="w-full px-3 py-2 border rounded-lg text-sm" value={form.button_url} onChange={(e) => setForm(p => ({...p, button_url: e.target.value}))} placeholder="https://..." data-testid="ann-form-btn-url" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ลำดับ (สูง = แสดงก่อน)</label>
                <input type="number" className="w-full px-3 py-2 border rounded-lg text-sm" value={form.priority} onChange={(e) => setForm(p => ({...p, priority: parseInt(e.target.value) || 0}))} data-testid="ann-form-priority" />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.show_once} onChange={(e) => setForm(p => ({...p, show_once: e.target.checked}))} />
                  แสดงครั้งเดียว
                </label>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.is_active} onChange={(e) => setForm(p => ({...p, is_active: e.target.checked}))} />
                  เปิดใช้งาน
                </label>
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <button className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-semibold hover:bg-orange-600 disabled:opacity-50" onClick={handleSubmit} disabled={saving || !form.title || !form.content} data-testid="ann-form-submit">
                {saving ? 'กำลังบันทึก...' : (editing ? 'บันทึก' : 'สร้าง')}
              </button>
              <button className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm" onClick={() => { setShowForm(false); setEditing(null); }}>ยกเลิก</button>
            </div>
          </div>
        </div>
      )}

      {/* Announcements List */}
      {loading ? (
        <div className="text-center py-8 text-gray-500">กำลังโหลด...</div>
      ) : announcements.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <div className="text-4xl mb-2">📢</div>
          <p>ยังไม่มีประกาศ</p>
        </div>
      ) : (
        <div className="space-y-3">
          {announcements.map(ann => (
            <div key={ann.id} className={`bg-white rounded-lg shadow-sm border p-4 ${!ann.is_active ? 'opacity-50' : ''}`} data-testid={`ann-item-${ann.id}`}>
              <div className="flex items-start gap-3">
                {(ann.image_base64 || ann.image_url) && (
                  <img src={ann.image_base64 || ann.image_url} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${ann.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {ann.is_active ? 'เปิด' : 'ปิด'}
                    </span>
                    {ann.show_once && <span className="text-xs text-gray-400">แสดงครั้งเดียว</span>}
                    <span className="text-xs text-gray-400">ลำดับ: {ann.priority}</span>
                  </div>
                  <h4 className="font-bold text-sm text-gray-800 truncate">{ann.title}</h4>
                  <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">{ann.content}</p>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => handleToggle(ann.id)} className={`px-2 py-1 rounded text-xs font-medium ${ann.is_active ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`} data-testid={`ann-toggle-${ann.id}`}>
                    {ann.is_active ? 'ปิด' : 'เปิด'}
                  </button>
                  <button onClick={() => handleEdit(ann)} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium" data-testid={`ann-edit-${ann.id}`}>แก้ไข</button>
                  <button onClick={() => handleDelete(ann.id)} className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium" data-testid={`ann-delete-${ann.id}`}>ลบ</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Admin Dashboard Component
const AdminDashboard = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user: currentUser, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [animals, setAnimals] = useState([]);
  const [posts, setPosts] = useState([]);
  const [comments, setComments] = useState([]);
  const [pagination, setPagination] = useState({});
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({});

  // Check admin access
  useEffect(() => {
    // Wait for auth to load first
    if (authLoading) {
      return;
    }
    
    if (!currentUser) {
      alert('กรุณาเข้าสู่ระบบก่อน');
      navigate('/');
      return;
    }
    
    if (!currentUser.is_admin) {
      alert('คุณไม่มีสิทธิ์เข้าถึงหน้า Admin');
      navigate('/');
      return;
    }
    
    loadInitialData();
  }, [currentUser, authLoading, navigate]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      await loadStats();
      await loadData(activeTab);
    } catch (error) {
      console.error('Error loading admin data:', error);
      if (error.response?.status === 403) {
        alert('คุณไม่มีสิทธิ์เข้าถึงหน้า Admin');
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadData = async (tab, page = 1) => {
    try {
      let endpoint = '';
      let params = { page, limit: 20 };
      
      if (search) params.search = search;
      
      switch (tab) {
        case 'users':
          endpoint = '/admin/users';
          break;
        case 'animals':
          endpoint = '/admin/animals';
          if (filters.animal_type) params.animal_type = filters.animal_type;
          break;
        case 'posts':
          endpoint = '/admin/posts';
          break;
        case 'comments':
          endpoint = '/admin/comments';
          break;
        default:
          return;
      }
      
      const response = await axios.get(`${API}${endpoint}`, { params });
      
      switch (tab) {
        case 'users':
          setUsers(response.data.users);
          break;
        case 'animals':
          setAnimals(response.data.animals);
          break;
        case 'posts':
          setPosts(response.data.posts);
          break;
        case 'comments':
          setComments(response.data.comments);
          break;
      }
      
      setPagination({
        total: response.data.total,
        page: response.data.page,
        limit: response.data.limit,
        total_pages: response.data.total_pages
      });
    } catch (error) {
      console.error(`Error loading ${tab}:`, error);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearch('');
    setFilters({});
    if (tab !== 'dashboard') {
      loadData(tab);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (activeTab !== 'dashboard') {
      loadData(activeTab);
    }
  };

  const handleDelete = async (type, id, additionalId = null) => {
    if (!confirm(`คุณแน่ใจOrไม่ที่จะลบ ${type} นี้?`)) return;
    
    try {
      let endpoint = '';
      switch (type) {
        case 'user':
          endpoint = `/admin/users/${id}`;
          break;
        case 'animal':
          endpoint = `/admin/animals/${id}`;
          break;
        case 'post':
          endpoint = `/admin/posts/${id}`;
          break;
        case 'comment':
          endpoint = `/admin/comments/${id}/${additionalId}`;
          break;
      }
      
      await axios.delete(`${API}${endpoint}`);
      alert('ลบสำเร็จ!');
      
      // Reload data
      if (activeTab === 'dashboard') {
        await loadStats();
      } else {
        await loadData(activeTab);
      }
    } catch (error) {
      console.error(`Error deleting ${type}:`, error);
      alert(`เกิดข้อผิดพลาดในการลบ: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleUserToggle = async (userId, field, currentValue) => {
    try {
      const updateData = { [field]: !currentValue };
      await axios.put(`${API}/admin/users/${userId}`, updateData);
      alert('Updateสำเร็จ!');
      await loadData('users');
    } catch (error) {
      console.error('Error updating user:', error);
      alert(`เกิดข้อผิดพลาด: ${error.response?.data?.detail || error.message}`);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">
            {authLoading ? 'กำลังตรวจสอบสิทธิ์...' : 'กำลังโหลดข้อมูล Admin...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="text-blue-600 hover:text-blue-800 flex items-center space-x-2"
              >
                <img src="/taillink-logo.png" alt="TailLink" className="h-12 w-auto object-contain" />
                <span className="fur-heading font-bold text-2xl">TailLink</span>
              </button>
              <span className="text-gray-400">|</span>
              <span className="text-red-600 font-semibold">🛠️ Admin Panel</span>
            </div>
            
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                👨‍💼 {currentUser?.full_name || currentUser?.username}
              </span>
              <button
                onClick={() => navigate('/')}
                className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
              >
                {t('nav.back_to_home')}
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto py-8 px-4">
        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm border-b mb-6">
          <div className="flex space-x-8 px-6 overflow-x-auto">
            {[
              { id: 'dashboard', label: '📊 Dashboard', icon: '📊' },
              { id: 'users', label: '👤 Users', icon: '👤' },
              { id: 'animals', label: '🐾 Animals', icon: '🐾' },
              { id: 'posts', label: '📝 Posts', icon: '📝' },
              { id: 'comments', label: '💬 Comments', icon: '💬' },
              { id: 'data-migration', label: '🔧 Data Migration', icon: '🔧' },
              { id: 'map-settings', label: '🗺️ Map Settings', icon: '🗺️' },
              { id: 'background', label: '🎨 Background', icon: '🎨' },
              { id: 'feed-settings', label: '⚙️ Feed Settings', icon: '⚙️' },
              { id: 'announcements', label: '📢 ประกาศ', icon: '📢' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`py-4 px-2 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Dashboard Stats */}
        {activeTab === 'dashboard' && stats && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center">
                      <span className="text-blue-600">👤</span>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Total Users</dt>
                      <dd className="text-lg font-medium text-gray-900">{stats.total_users}</dd>
                      <dd className="text-sm text-green-600">+{stats.recent_users} this week</dd>
                    </dl>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-green-100 rounded-md flex items-center justify-center">
                      <span className="text-green-600">🐾</span>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Total Animals</dt>
                      <dd className="text-lg font-medium text-gray-900">{stats.total_animals}</dd>
                      <dd className="text-sm text-green-600">+{stats.recent_animals} this week</dd>
                    </dl>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-orange-100 rounded-md flex items-center justify-center">
                      <span className="text-orange-600">📝</span>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Total Posts</dt>
                      <dd className="text-lg font-medium text-gray-900">{stats.total_posts}</dd>
                      <dd className="text-sm text-green-600">+{stats.recent_posts} this week</dd>
                    </dl>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-orange-100 rounded-md flex items-center justify-center">
                      <span className="text-orange-600">💬</span>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Total Comments</dt>
                      <dd className="text-lg font-medium text-gray-900">{stats.total_comments}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        {activeTab !== 'dashboard' && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <form onSubmit={handleSearch} className="flex space-x-4">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="ค้นหา..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              {activeTab === 'animals' && (
                <select
                  value={filters.animal_type || ''}
                  onChange={(e) => setFilters(prev => ({ ...prev, animal_type: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">ทุกt('admin.hero_type')</option>
                  <option value="owned">สัตว์เลี้ยง</option>
                  <option value="stray">สัตว์จรจัด</option>
                  <option value="social_pet">สัตว์ชุมชน</option>
                </select>
              )}
              
              <button
                type="submit"
                className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700"
              >
                🔍 ค้นหา
              </button>
            </form>
          </div>
        )}

        {/* Data Migration Section */}
        {activeTab === 'data-migration' && (
          <DataMigrationPanel />
        )}

        {/* Users Table */}
        {activeTab === 'users' && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">👤 จัดการผู้ใช้</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {user.full_name || user.username}
                          </div>
                          <div className="text-sm text-gray-500">@{user.username}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{user.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleUserToggle(user.id, 'is_active', user.is_active)}
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            user.is_active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {user.is_active ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleUserToggle(user.id, 'is_admin', user.is_admin)}
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            user.is_admin 
                              ? 'bg-orange-100 text-orange-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {user.is_admin ? 'Admin' : 'User'}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(user.created_at).toLocaleDateString(getLocaleCode(i18n.language))}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => navigate(`/user/${user.id}`)}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                        >
                          View
                        </button>
                        {user.id !== currentUser?.id && (
                          <button
                            onClick={() => handleDelete('user', user.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Animals Table */}
        {activeTab === 'animals' && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">🐾 จัดการสัตว์</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Animal</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Owner</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Followers</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {animals.map((animal) => (
                    <tr key={animal.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="text-2xl mr-3">{getAnimalIcon(animal.species)}</span>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{animal.name}</div>
                            <div className="text-sm text-gray-500">{animal.species} {animal.breed && `• ${animal.breed}`}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          animal.animal_type === 'owned' ? 'bg-green-100 text-green-800' :
                            animal.animal_type === 'stray' ? 'bg-orange-100 text-orange-800' :
                            animal.animal_type === 'wild' ? 'bg-amber-100 text-amber-800' :
                            'bg-teal-100 text-teal-800'
                        }`}>
                          {animal.animal_type === 'owned' ? t('animals.pet') :
                             animal.animal_type === 'stray' ? t('animals.stray') :
                             animal.animal_type === 'wild' ? t('profile.pet_type_wild') :
                             t('animals.social_pet')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {animal.created_by_name || 'Unknown'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {animal.followers?.length || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(animal.created_at).toLocaleDateString(getLocaleCode(i18n.language))}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => navigate(`/animal/${animal.id}`)}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                        >
                          View
                        </button>
                        <button
                          onClick={() => handleDelete('animal', animal.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Posts Table */}
        {activeTab === 'posts' && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">📝 จัดการโพสต์</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Content</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Author</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Animal</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Likes</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Comments</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {posts.map((post) => (
                    <tr key={post.id}>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate">
                          {post.content}
                        </div>
                        {post.photos.length > 0 && (
                          <div className="text-xs text-gray-500 mt-1">
                            📷 {post.photos.length} photo(s)
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {post.created_by_name || 'Unknown'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        Animal ID: {post.animal_id.slice(0, 8)}...
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        ❤️ {post.likes?.length || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        💬 {post.comments?.length || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(post.created_at).toLocaleDateString(getLocaleCode(i18n.language))}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleDelete('post', post.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Comments Table */}
        {activeTab === 'comments' && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">💬 จัดการคอมเมนต์</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Content</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Author</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Post</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {comments.map((item, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs">
                          {item.comment.content}
                        </div>
                        {item.comment.photos && item.comment.photos.length > 0 && (
                          <div className="text-xs text-gray-500 mt-1">
                            📷 {item.comment.photos.length} photo(s)
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.comment.username || 'Unknown'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        Post ID: {item.post_id.slice(0, 8)}...
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(item.comment.created_at).toLocaleDateString(getLocaleCode(i18n.language))}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleDelete('comment', item.post_id, item.comment.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Map Settings Tab */}
        {activeTab === 'map-settings' && (
          <MapSettingsAdmin />
        )}

        {/* Background Settings Tab */}
        {activeTab === 'background' && (
          <BackgroundSettingsAdmin />
        )}

        {/* Feed Settings Tab */}
        {activeTab === 'feed-settings' && (
          <FeedReloadSettingsAdmin />
        )}

        {/* Announcements Tab */}
        {activeTab === 'announcements' && (
          <AnnouncementsAdmin />
        )}

        {/* Pagination */}
        {activeTab !== 'dashboard' && activeTab !== 'background' && activeTab !== 'feed-settings' && activeTab !== 'map-settings' && activeTab !== 'announcements' && pagination.total_pages > 1 && (
          <div className="bg-white rounded-lg shadow-sm p-6 mt-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                แสดง {((pagination.page - 1) * pagination.limit) + 1} ถึง {Math.min(pagination.page * pagination.limit, pagination.total)} จาก {pagination.total} รายการ
              </div>
              <div className="flex space-x-2">
                {pagination.page > 1 && (
                  <button
                    onClick={() => loadData(activeTab, pagination.page - 1)}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50"
                  >
                    Previous
                  </button>
                )}
                
                <span className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm">
                  {pagination.page}
                </span>
                
                {pagination.page < pagination.total_pages && (
                  <button
                    onClick={() => loadData(activeTab, pagination.page + 1)}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50"
                  >
                    Next
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Simple Test Admin Component for debugging
const TestAdminDashboard = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <h1 className="text-3xl font-bold text-red-600">🛠️ TEST ADMIN DASHBOARD</h1>
      <p className="text-gray-700 mt-4">This is a test admin dashboard to check routing.</p>
      <button 
        onClick={() => navigate('/')}
        className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-md"
      >
        {t('nav.back_to_home')}
      </button>
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <NotificationProvider>
          <GpsProvider>
            <Router>
              <Routes>
                <Route path="/db-test" element={<DBTestRedirect />} />
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/animal/:animalId" element={<AnimalProfilePage />} />
                <Route path="/user/:userId" element={<UserProfilePage />} />
                <Route path="/post/:postId" element={<PostDetailPage />} />
                <Route path="/map" element={<MapPage />} />
                <Route path="/notifications" element={<NotificationSettings />} />
                <Route path="/language" element={<Suspense fallback={<V1Fallback/>}><LanguageSettingsPage /></Suspense>} />
                <Route path="/pokdeng" element={<Suspense fallback={<V1Fallback/>}><PokdengPage /></Suspense>} />
                <Route path="/v2" element={<V2Shell />}>
                  <Route index element={<HomeFeedRoute />} />
                  <Route path="onboarding" element={<Onboarding />} />
                  <Route path="me" element={<MeProfileRoute />} />
                  <Route path="map" element={<MapV2Route />} />
                  <Route path="community" element={<CommunityHubRoute />} />
                  <Route path="challenges/:slug" element={<ChallengeEventRoute />} />
                  <Route path="stamp-hunts/:slug" element={<StampHuntRoute />} />
                  <Route path="animal/:animalId" element={<V2AnimalProfileRoute />} />
                  <Route path="user/:userId" element={<V2UserProfileRoute />} />
                  <Route path="spot" element={<QuickSpotRoute />} />
                  <Route path="community-care" element={<CommunityCareRoute />} />
                  <Route path="wildlife" element={<WildlifeRoute />} />
                  <Route path="admin" element={<AdminZoneRoute />} />
                  <Route path="debug" element={<DebugRoute />} />
                  <Route path="pokdeng" element={<PokdengLobbyRoute />} />
                </Route>
                <Route path="/" element={<Navigate to="/v2" replace />} />
                <Route path="/v1" element={<AppContent />} />
                <Route path="/legacy" element={<AppContent />} />
              </Routes>
            </Router>
          </GpsProvider>
        </NotificationProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;